// Bump CURRENT_RELEASE + add an entry on every deploy that adds a feature or fixes a bug.
// Shown once (per browser) in the "What's New" popup after a user refreshes to the new build.
// features = highlighted; fixes = secondary.
export const CURRENT_RELEASE = 'v1.249.19';

export const RELEASE_NOTES = {
  'v1.249.19': {
    date: '04 Nov 2026',
    silent: true,   // bug fixes — kept out of the What's New popup so nobody is interrupted
    features: [],
    fixes: [
      'Orders: with a sales channel selected, every shipped and closed tab now lists all of that channel\'s orders. Ready to Print, Ready to Pickup, In Transit and others could show fewer rows than their count.',
      'Orders: Next and Prev now work on Ready to Print, Ready to Pickup, In Transit, Out for Delivery, NDR and Returning. They kept showing the first page.',
      'Orders: filtered results on Delivered, Returned, Cancelled and Lost now have page buttons, so matches past the first page can be reached.',
      'Orders: Blue Dart and Ekart are in the Courier Partner filter.',
      'Orders: a From date now includes orders placed between midnight and 5:30 am that day.',
      'Export: the Export button now waits for the file and downloads it instead of saying it is not ready, and exports honour the Return and Tracking page filters.',
    ],
  },
  'v1.249.17': {
    date: '04 Nov 2026',
    silent: true,   // small fixes — kept out of the What's New popup so nobody is interrupted
    features: [],
    fixes: [
      'Returns: a return scanned into an RTO batch can no longer be restocked twice. Those rows show "In RTO batch" instead of Receive, and confirming a batch leaves alone any return that was already received by hand.',
      'Dashboard and ModIQ: links to Out for Delivery attempts, NDR and Lost now open Orders on the right tab with the filter on.',
      'Dashboard: custom sales channels no longer show as Inactive; Shipping / revenue says what its figure includes; Courier performance has an Other column so each row adds up to Total.',
      'Profit & Loss: the input tax credit breakdown now shows negative lines, so the rows add up to the total.',
      'Purchase Manager: Reserved can no longer go negative, and products fully covered by purchase orders say "On order" instead of "Out — order now".',
      'Shipping Cost: a shipment with no estimate shows "Estimate missing" and "—" instead of ₹0.00.',
      'Vendors: Opening and Charges columns show what makes up Due. A vendor with payments recorded can no longer be deleted.',
      'Vendor Payments: when a date range has no payments, the page says so and offers Show all time. The cards say they cover all time.',
      'Orders: Esc closes the order popup, and its amount has thousands separators.',
      'Browser tabs now show the page name.',
    ],
  },
  'v1.249.1': {
    date: '29 Oct 2026',
    silent: true,   // security hardening — kept out of the What's New popup on purpose
    features: [],
    fixes: [
      'Security: courier tracking webhooks that cannot be verified (Ekart\'s old URL, Shiprocket/Velocity accounts with no key) can no longer mark an order delivered, RTO, lost or cancelled — the courier poll confirms those. New Ekart connections get a secret per-account webhook URL; scripts/ekartWebhookToken.js moves existing ones.',
      'Security: Shopify/Amazon connect "state" codes and scanner tokens are no longer accepted as a login.',
      'Security: the shared demo account is view-only.',
      'Security: sign-up codes — at most 5 per email per hour, 10 wrong guesses then a 1-hour lock, 20 sends per network per hour; codes and PINs now come from a secure random source.',
      'Security: changing or resetting a password signs out every other device (this one stays signed in).',
      'Security: admin login is limited to 10 failed attempts per 15 minutes; mobile scanner locks the account PIN for 30 minutes after 10 wrong location PINs and no longer reveals the account name early.',
      'Security: the web app now sends a Content Security Policy; the purchase-order print page escapes product and vendor names.',
      'Speed: the mobile return scanner\'s 4-second heartbeat no longer reads every returned order; the wallet passbook reads only the latest 200 rows.',
      'Phones and narrow windows: the menu becomes a slide-out drawer and wide tables scroll inside the page.',
      'Sign-up: passwords need 8 characters (same as password change); a mobile number written as +91… is recognised as a duplicate; login ignores stray spaces around the email.',
    ],
  },
  'v1.249.0': {
    date: '26 Oct 2026',
    features: [
      'Branded Tracking: see exactly which orders your tracking page sold. Dashboard › Tracking page now lists each one — the order number, how it came (Buy now, your code, or the customer clicked and ordered within 7 days), which order’s tracking page it came from, the product they clicked and what they bought.',
      'Branded Tracking: those orders are tagged in Shopify — “MC Tracking Page” plus “MC Buy Now”, “MC Code” or “MC Helped”. Change the wording or switch tagging off in Branded Tracking settings.',
      'Orders: a 🛍 “Tracking page” badge marks these orders, the order details show where the sale came from, and the original order shows what its tracking page sold. New filter: Tracking page › Sold by the tracking page.',
    ],
    fixes: [],
  },
  'v1.248.0': {
    date: '15 Oct 2026',
    features: [
      'Profit & Loss: a new full-width layout. The top strip reads like a sum — Sales − Products − Shipping − Ads − Fees = Profit before GST − GST to pay = Profit after GST.',
      'Profit & Loss: a "GST & input tax credit" card shows the GST in your sales, your input tax credit (ITC) and where it came from (stock, shipping, ads, fees), and the GST left to pay.',
      'Profit & Loss: enter ad spend for a whole month per sales channel if you can\'t enter it every day. It is spread evenly over the month.',
      'Profit & Loss: the day table now shows products, shipping, ads, fees, profit before GST, GST and profit after GST as separate columns.',
      'Settings › ModIQ: cost rules are now "if → then". Example: if an order ships from your Kolkata warehouse and has a particular product, add ₹15 per unit — otherwise ₹0. Conditions: ship-from location, products, sales channel and COD/prepaid.',
    ],
    fixes: [],
  },
  'v1.247.0': {
    date: '15 Oct 2026',
    features: [
      'New in ModIQ: Profit & Loss (ModIQ › Profit & Loss). See what you sold, what you spent and what you kept, day by day — "You kept ₹X, that\'s ₹Y from every ₹100 you sold." Sales, costs and GST to pay are worked out for you, and every day opens to show the full working.',
      'Profit & Loss: product cost counts only on delivered and on-the-way orders, at the price you paid. Returned and cancelled goods come back to stock, so they carry no product cost. Shipping comes from Shipping Cost. Type your daily ad spend and the GST is added for you.',
      'Profit & Loss: "Money stuck in returns" shows the value of goods on their way back, back with you but not checked yet, and customer returns being picked up.',
      'Settings › ModIQ: set your per-order costs once — payment gateway %, packing, fulfilment, pick & pack, COD bank charges, marketplace commission — and they apply to every order automatically. Add fixed monthly costs (rent, salaries) and choose how GST and returns are counted.',
      'ModIQ is now a paid add-on: a 7-day free trial, then ₹799 a month from your wallet. If you already use ModIQ, your free trial starts tonight.',
      'Each product\'s cost is now locked when its order ships, so later price changes never rewrite past profit.',
    ],
    fixes: [],
  },
  'v1.246.1': {
    date: '14 Oct 2026',
    features: [],
    fixes: [
      'Blue Dart: courier scans are now read correctly. Branded tracking pages for Blue Dart orders show the full journey again (before, only “Ready for pickup” appeared), and Blue Dart orders now move to “Out for delivery” when the courier scans them out — this had never happened before.',
      'Branded Tracking: the delivery date is now shown as “Arriving by …”. Couriers send a latest-delivery date, and most parcels arrive a day or two earlier, so “Arriving on …” looked wrong to customers.',
      'Tracking: scheduled jobs (courier tracking, Shopify fulfilment retries and the nightly pickup roll-over) could occasionally be skipped without any error when the server was busy at that exact second. They now always run.',
    ],
  },
  'v1.246.0': {
    date: '14 Oct 2026',
    features: [
      'Orders: a small copy button next to the AWB, like the one next to the order number.',
      'Orders › Lost: a new "Lost On" column shows the date and time the courier marked the parcel lost, and which courier.',
      'Orders › Lost: the date filter on this tab now filters by the lost date, so you can pull up everything lost on a given day or week.',
    ],
    fixes: [],
  },
  'v1.245.0': {
    date: '14 Oct 2026',
    features: [
      'Remittance: a new look, in the same style as the Dashboard. The total collected is shown big at the top with a trend line and a Prepaid vs COD split. "COD by courier" is always on screen, and a day-by-day chart shows each day\'s prepaid and COD.',
      'Remittance: 30 days and This month are now one-click periods. The table shows the order count under every amount and a small Prepaid/COD bar for each day.',
    ],
    fixes: [],
  },
  'v1.244.1': {
    date: '14 Oct 2026',
    features: [],
    fixes: [
      'Remittance: the page loads much faster. It was reading every order you have ever had on each visit, which took up to 30 seconds on large accounts and slowed the rest of the panel while it ran. It now reads only the orders in the dates you pick. The numbers are exactly the same.',
    ],
  },
  'v1.244.0': {
    date: '14 Oct 2026',
    features: [
      'Branded Tracking settings: a new, roomier layout. Status and the main on/off switch sit in a header at the top, your stores take the main column, and the settings for all stores sit beside them.',
      'Branded Tracking settings: each store shows whether it is Live, Off or still needs a page address, with its tracking link and a copy button right under the store name.',
      'Settings: tiles have their own colours, and add-ons like Branded Tracking are marked "Add-on · Active".',
    ],
    fixes: [],
  },
  'v1.243.0': {
    date: '14 Oct 2026',
    features: [
      'Dashboard: Branded Tracking analytics now has its own "Tracking page" tab, next to Revenue. It replaces the card at the bottom of the dashboard and the separate analytics page.',
      'Tracking page tab: a cleaner layout in the same style as the rest of the dashboard. It shows sales from the page (Buy now, your code, helped), reach and open rate, a funnel from shipment to order, day-by-day charts, most clicked products, offer results, and ratings by courier, city and product. It follows the dashboard\'s sales channel filter and dark mode.',
      'Tracking page tab: a short "How an order is credited" note explains exactly how a sale is linked back to the tracking page.',
    ],
    fixes: [],
  },
  'v1.242.0': {
    date: '14 Oct 2026',
    features: [
      'Velocity now connects with an API key. Velocity is switching off mobile + password logins on 25 Oct 2026. In Velocity, go to Settings → API Keys → Generate New Key (expiry 365 days), then on the Couriers page click "Switch to API key" and paste it. Your locations, shipped orders and tracking webhook stay exactly as they are.',
      'Couriers: the Velocity card shows when your API key expires, and warns you 15 days before. The System Status page and banner warn you too.',
    ],
    fixes: [
      'Velocity: an expired or disabled key now says "make a new key in Velocity → Settings → API Keys" instead of a general "login failed".',
    ],
  },
  'v1.241.2': {
    date: '13 Oct 2026',
    features: [],
    fixes: [
      'Shipping Cost: the Blue Dart COD fee now follows Blue Dart\'s rule — ₹35 or 1.5% of the order value, whichever is higher (plus GST). It was showing a flat ₹60 (or 3%). Past Blue Dart shipments have been corrected too.',
      'Shipping Cost: when Blue Dart does not return a price at booking (credit accounts billed monthly), the shipping cost now shows as pending instead of ₹0.00. It fills in when you upload your Blue Dart bill in Cost Reconciliation.',
    ],
  },
  'v1.246.1': {
    date: '13 Oct 2026',
    features: [],
    fixes: [
      'Returns & Exchanges: a reverse pickup the courier marked "Not Picked" (failed pickup attempt) no longer shows as Picked Up / In Transit. It now stays at Pickup Booked until the courier actually collects the parcel.',
    ],
  },
  'v1.241.0': {
    date: '13 Oct 2026',
    features: [
      'Branded Tracking: a fresh, colourful tracking page. The page takes the colour of the delivery status, every step has an icon, and everything is in plain English.',
      'Branded Tracking: sell from the tracking page. Show an offer with your Shopify discount code and a row of products near the top of every tracking page. Buy now opens your own Shopify checkout with the product in the cart and the code already applied.',
      'Branded Tracking: customers can rate the delivery and each product on the delivered page. 1–2 star ratings are emailed to you.',
      'Branded Tracking: use your channel logo or upload a separate logo for the tracking page.',
      'New Tracking Analytics page (and a card on your Dashboard): page views, how many customers opened their page, product clicks, orders and revenue from the page, and delivery ratings by courier, city and product.',
      'Settings: Branded Tracking now has its own card on the Settings page while the add-on is on.',
    ],
    fixes: [],
  },
  'v1.240.3': {
    date: '10 Oct 2026',
    features: [],
    fixes: [
      'Orders: a fully refunded order — returned and refunded, or refunded instead of cancelled — now shows its products again. They were disappearing from the order list, including the Returned and Exchanged tabs.',
    ],
  },
  'v1.240.2': {
    date: '9 Oct 2026',
    features: [
      'Orders: for a branded shipment, the tracking link in your order list and order details now opens your branded page — the one your customer received.',
    ],
    fixes: [
      'Branded Tracking: the ModCommerce logo at the bottom of the tracking page is now the real logo.',
      'Branded Tracking: Delhivery locations read as place names (“Kolkata Panihati (West Bengal)”) instead of internal codes (“Kolkata_Panihati_C”).',
    ],
  },
  'v1.240.1': {
    date: '9 Oct 2026',
    features: [
      'Branded Tracking is now live. Find it under Mod Add-ons → Branded Tracking → Configure: pick a page address for each store, open Preview to check it, then switch it on.',
    ],
    fixes: [],
  },
  'v1.240.0': {
    date: '9 Oct 2026',
    features: [
      'New add-on — Branded Tracking: your customers get your own tracking page instead of the courier’s. It shows your logo, the expected delivery date, every courier scan, and your support email and phone. ₹0.50 per branded shipment; turn it on under Mod Add-ons → Branded Tracking → Configure.',
      'Branded Tracking: each store gets a search page (track.modcommerce.in/yourstore) where customers find their order by order number or tracking number — add it to your store menu as “Track order”.',
      'Branded Tracking: if your wallet is empty, the shipment still goes out with the courier’s link. Cancelling before pickup refunds the ₹0.50 with the shipping charge.',
    ],
    fixes: [
      'Tracking: in-transit Velocity and Blue Dart orders are no longer rewritten every 15 minutes when nothing about them changed. This is background work you won’t see, but it makes the panel lighter.',
    ],
  },
  'v1.239.0': {
    date: '9 Oct 2026',
    features: [
      'Tidier side menu: about 10 rows instead of 22. Related pages now sit together in groups — Shipping, Returns, Payments, Inventory and Setup — that open with a click. Nothing was removed.',
      'The group you are working in opens by itself, and groups you leave open stay open next time.',
      'New “Search pages”: press Ctrl + K (or click Search pages in the menu), type a few letters — e.g. “rem” for Remittance — and press Enter to jump straight there.',
      'Billing now opens from the Wallet card (“View billing →”).',
    ],
    fixes: [],
  },
  'v1.238.0': {
    date: '9 Oct 2026',
    features: [
      'New dark graphite side menu — a cleaner, more premium look.',
      'The side menu now tucks itself away into a slim strip of icons, giving every page more room. Move your mouse onto it and it slides open; move away and it slides closed.',
      'Prefer it always open? Click the pin at the top of the menu (or press the [ key). Your choice is remembered.',
    ],
    fixes: [
      'Clicking the wallet icon while the menu is closed now opens Recharge (it did nothing before).',
    ],
  },
  'v1.237.0': {
    date: '9 Oct 2026',
    features: [
      'Dashboard has a new, calmer look: Net revenue is the headline, with orders, gross revenue, order value and shipped in one row beneath it — instead of 14 separate coloured cards.',
      'Dashboard: COD vs Prepaid is now a ring, “Money in motion” groups in transit, out for delivery, RTO, shipping cost and product cost in one place, and delivery health shows as three small rings.',
      'Dashboard: colour now means something — green for revenue and prepaid, orange for COD, red for RTO — and is only used where it helps you read the number.',
      'Dashboard and its Revenue tab open in the light theme. Prefer dark? Switch once with the Dark button and it stays dark.',
    ],
    fixes: [
      'Dashboard: a rise in cancelled orders now shows in red (it used to show green, as if it were good news).',
    ],
  },
  'v1.236.0': {
    date: '8 Oct 2026',
    features: [
      'New free add-on: Return Address by Product. Send returns (RTO) of chosen products to a different address than the pickup location. Turn it on in Mod Add-ons, then set a Return address on a product (or for many at once in the Products Excel bulk edit).',
      'Ship popup (single and bulk): with the add-on on, a Return address dropdown shows where returns will go. It is filled in from the products and you can change it for any shipment.',
      'Return / Exchange: the warehouse now defaults to the product’s return address. You can still pick another.',
    ],
    fixes: [
      'Shipping labels: when an order returns to a different address than its pickup location, the label’s “Return to” line now shows that return address instead of the pickup address.',
    ],
  },
  'v1.235.0': {
    date: '7 Oct 2026',
    features: [
      'Orders: new “Return / Exchange” filter. A delivered order stays in Delivered after it is returned or exchanged; now you can show only Returned, only Exchanged, any return/exchange, or orders with none.',
      'Dashboard: Net Revenue now takes out refunds on returned orders and adds exchange price differences, not just cancellations.',
      'Dashboard: Total COGS takes out the cost of returned goods put back in stock, and counts the goods sent in exchange replacements. Total Shipping Cost shows how much of it went on returns and exchanges.',
      'Order details: new “Money” section. Order value, refunds, exchange difference, every shipping charge (forward, return pickup, replacement, reship), product cost and profit for that one order.',
    ],
    fixes: [],
  },
  'v1.234.2': {
    date: '7 Oct 2026',
    features: [],
    fixes: [
      'Split orders: cancelling the shipment of one part no longer un-fulfils the other parts on your sales channel. Before, a part that was already shipped (even delivered) could show as Unfulfilled there. Affected orders have been repaired: their fulfilment and tracking are back, without emailing the customer again.',
      'Split orders: “Cancel order” on one part now cancels only that part’s shipment. It no longer cancels the whole order on your sales channel along with the other parts.',
      'Split orders: a part whose items sit at a different location on the sales channel now fulfils correctly, and a product split across parts is fulfilled with each part’s own quantity.',
    ],
  },
  'v1.234.1': {
    date: '7 Oct 2026',
    features: [
      'Courier Cost Reconcile is now a monthly plan: a 7-day free trial, then 399 credits a month. Reconcile as many bills and AWBs as you like, with no per-AWB charge. If you already had it on, your next 30 days are free and the monthly plan starts after that. Manage it in Mod Add-ons.',
      'Cost Reconciliation: “AWB not found” now says why. The order now has a different AWB (this one was cancelled or replaced, so claim it back), the order was not shipped through ModCommerce, it belongs to another ModCommerce account, or it was booked outside. Filter by reason, and see it in the CSV.',
      'Cost Reconciliation: each reconciliation gets a reference like CR-DL-2610-0007 (courier, month, running number), shown in history, on the page and in wallet entries.',
      'Cost Reconciliation: cleaner header. Courier, invoice, status and reference on top; the bill and invoice PDF as buttons; Edit and Delete under the ⋯ menu.',
      'Delhivery: the box size (length × width × height) is now sent with every shipment, not just the weight. When an order has no box size, the ship popup warns that the courier may charge extra later.',
    ],
    fixes: [
      'Cost Reconciliation: large bills (up to 1,00,000 lines) upload and match without slowing the site. Lines are sent one piece at a time with automatic retry, there is a progress bar, and the bill is matched in small parts.',
      'Cost Reconciliation: AWBs saved as numbers on older orders are now matched.',
      'Delhivery shipments costed before 2 Oct: the freight / COD split is corrected (the COD fee’s GST had been counted in freight). The total was already right and does not change.',
    ],
  },
  'v1.234.0': {
    date: '3 Oct 2026',
    features: [
      'Products: new “Bulk Edit (Excel)”. Download your products as an Excel sheet with missing details (SKU, HSN, weight, size, cost) highlighted in yellow, fill them in, and upload it back. You see every change before anything is saved, and an empty cell never deletes a value.',
    ],
    fixes: [],
  },
  'v1.233.0': {
    date: '3 Oct 2026',
    features: [
      'ModIQ: switch between Today, 7 days and 30 days. The longer views show COD collected per day, courier performance and every number compared with the period before.',
      'ModIQ: new “COD at risk” — the COD most likely to come back as RTO (failed deliveries, 3rd+ attempts, and orders stuck with no courier update). Hover it to see the split.',
      'ModIQ: new “Why COD is short” — how much of today’s COD is still not collected, which courier holds it and on which attempt, plus yesterday’s forecast vs what actually came in.',
      'ModIQ: new “Moments” — big COD orders delivered or out for delivery, big orders going back (RTO), and any shipment the courier marks LOST. Lost shipments are also flagged in Needs attention.',
      'ModIQ: new “RTO by state” — delivered vs returned per state, with the return rate, so you can see where RTO comes from.',
      'ModIQ: optional morning summary email at 9 AM with yesterday’s results and what needs you today. Turn it on, and set your “big order” amount, from ModIQ → Settings.',
    ],
    fixes: [],
  },
  'v1.232.0': {
    date: '3 Oct 2026',
    features: [
      'ModIQ has a new look: a clean, light, calmer design. Today’s predicted COD is the headline, with your day’s numbers in one row underneath.',
      'ModIQ: new “Needs attention” list brings stuck orders, courier spikes, 3rd+ attempt COD and today’s failed deliveries to the top, with a link to each.',
      'ModIQ: new “Insights” explain today’s numbers in plain words, e.g. how much COD is already in and which courier is most reliable.',
      'ModIQ: the 3-day COD forecast is now a chart, shipment status is a donut, and the courier scorecard shows your best courier and the one with the highest return rate. The full Courier Analysis table is tucked under “Show details”.',
    ],
    fixes: [],
  },
  'v1.231.2': {
    date: '3 Oct 2026',
    features: [
      'Cost Reconciliation: Edit details (invoice number, date, period, total) or Delete a reconciliation, even after confirming. Deleting puts every AWB back to its estimate, refunds the credits, and lets you upload the right bill again.',
      'Cost Reconciliation: add the courier’s invoice PDF along with the bill — the invoice number, date, billing period and total are filled in for you. Delhivery and Velocity invoices are read automatically.',
      'Cost Reconciliation: the bill file and invoice PDF are kept with each reconciliation — open them any time with “View”.',
      'Declared weight: the weight and box size we send the courier are now saved at shipping, and shown next to the charged weight on the bill (hover for dead / volumetric / box). Older shipments use the weight from their shipping estimate.',
    ],
    fixes: [
      'Shipping Cost loads much faster on large accounts, and the page buttons (Previous / Next, with 100 / 250 / 500 per page) are always shown. Courier, store and Forward/Reverse filters now apply to all pages, not just the one on screen.',
    ],
  },
  'v1.231.1': {
    date: '2 Oct 2026',
    features: [
      'Shipping Cost: return and exchange pickups now show up the moment you book the pickup, with an estimated cost and a purple “Reverse” label — instead of ₹0 until the courier’s bill arrives. Filter by Forward / Reverse.',
    ],
    fixes: [
      'When you reconcile a courier bill, the reverse pickup’s estimate is replaced by the real billed cost. Cancelling the return before pickup sets its cost to ₹0.',
    ],
  },
  'v1.231.0': {
    date: '2 Oct 2026',
    features: [
      'New add-on — Courier Cost Reconcile (Payments › Cost Reconciliation): upload your courier’s bill exactly as they sent it and every AWB is matched to your shipments, showing the cost we estimated at shipping next to what the courier actually billed, and the difference.',
      'Reads Delhivery billing files (CSV or Excel) and Velocity itemised bills automatically. Any other courier’s bill works too: match the AWB and Total columns once and it is remembered.',
      'Spots weight disputes (courier charged more weight than you declared), RTO and COD-fee reversals, return pickups, AWBs not in your account, and lines already reconciled — and blocks the same bill from being counted twice.',
      'Velocity bills one shipment across several invoices; each upload adds to that shipment’s total instead of replacing it, even if you upload an older invoice later.',
      'History of every bill you reconciled, a courier-wise Estimated vs Billed table, and a list of shipments not billed yet. Previewing a bill is free; you pay only when you confirm, once per AWB.',
      'Shipping Cost: new Variance column, a Reconciled badge, and a filter for reconciled / not reconciled / high variance / weight disputes.',
    ],
    fixes: [
      'Shipping Cost: for Delhivery, the Est and COD Fee columns were split slightly wrong (GST on the COD fee was counted inside freight). The Net total was already right and does not change.',
    ],
  },
  'v1.230.0': {
    date: '2 Oct 2026',
    features: [
      'Dashboard › Revenue: a new Revenue tab next to Overview shows Gross, Net and Shipped revenue, and where every rupee of Gross is now: Delivered, In Transit, RTO, Unshipped or Cancelled. The five always add up to Gross, each with its share in %.',
      'Revenue tab: filter by sales channel and by product. With a product picked, the money shown is only that product’s own value in each order, not the whole order total.',
      'Revenue tab: a day-by-day chart of orders placed and where they are now, and a product table showing each product’s revenue by stage.',
    ],
    fixes: [
      'Dashboard: the top cards on Overview (Total Orders, Revenue, Shipped and the rest) now follow the sales-channel filter. Before, only the sections below them did.',
    ],
  },
  'v1.229.7': {
    date: '2 Oct 2026',
    features: [],
    fixes: [
      'Speed: customer history (orders by the same phone or email) loads faster, and the server keeps track of anything that slows it down so it can be fixed quickly.',
      'Shipping cost: during bulk ship with Delhivery, the estimated cost is now filled in at ship time more often, instead of waiting for the background job to fill it later.',
    ],
  },
  'v1.229.6': {
    date: '2 Oct 2026',
    features: [],
    fixes: [
      'Speed: pages across the panel load faster. Courier and Shopify updates were making the database look through every order to find one, which slowed everyone else down at the same time.',
      'Fewer random "something went wrong" errors: a connection setting between our web server and the app was dropping a few requests every hour, including sign-ins and order counts.',
      'Bulk print: large label batches no longer fail with "Navigation timeout". A batch that normally takes a few seconds could sometimes hang for a minute and fail the whole job.',
      'Labels: product names you renamed yourself now show on shipping labels again, instead of the sales-channel title.',
      'Address edits: fixing a customer\'s address now reaches Shopify again. The update was being rejected by Shopify and retried without success.',
    ],
  },
  'v1.229.5': {
    date: '29 Sep 2026',
    features: [],
    fixes: [
      'Dashboard: a COD order no longer moves into "Prepaid" once it is delivered. Delivery marks the order as paid, and the Prepaid vs COD split, the In-Transit, Out-for-Delivery and RTO Lost values, and the COD/Prepaid shipping-cost split were reading that as prepaid. They now go by how the order was shipped, so your COD share goes up to its real figure.',
      'Orders: search on the Prepaid RTO and Manually Fulfilled tabs now also finds orders by product name, SKU, phone and email, like every other tab.',
    ],
  },
  'v1.229.4': {
    date: '26 Sep 2026',
    features: [],
    fixes: [
      'Cancelled orders: an order cancelled in your sales channel or in ModCommerce now keeps its products, SKU, weight and amount. Before this, the Cancelled tab showed no product and ₹0, because the cancellation was mistaken for every item having been removed.',
      'Cancelled orders you already had are restored too. "Total Revenue" on the Orders page and "Gross Revenue" on the Dashboard include cancelled orders by design (the Dashboard shows them separately as a deduction), so those two figures go up by the value of your cancelled orders, and the cancelled amount shown under "Cancelled Orders" is now correct.',
    ],
  },
  'v1.229.3': {
    date: '26 Sep 2026',
    features: [],
    fixes: [
      'Blue Dart: a second Blue Dart service on a warehouse (for example Surface next to Bharat Dart+) no longer fails with "User not authorized to register pickup". If you have not typed a pickup Area for that service, it now uses the Area already set for your other Blue Dart service on the same warehouse instead of guessing one from the pincode.',
      'Blue Dart: when the Area really is wrong, the error now names your customer code and the Area we sent, and tells you where to correct it, instead of repeating Blue Dart’s message that names neither.',
    ],
  },
  'v1.229.2': {
    date: '26 Sep 2026',
    features: [],
    fixes: [
      'Orders: an order line whose Shopify variant was later removed now shows the product photo again. Shopify drops the photo together with the variant, so the order now shows the photo of the product it belongs to.',
    ],
  },
  'v1.229.1': {
    date: '26 Sep 2026',
    features: [],
    fixes: [
      'Orders: an order for a product whose Shopify variant was later removed now keeps the SKU the customer actually bought. Before this it lost its SKU, showed no photo, landed in "No stock", and a second empty copy of the product appeared in your catalogue.',
      'Orders: when several of your products share the same name (for example the colours of one bag), an order no longer borrows another colour’s SKU, photo, weight or size. Before this it could show the wrong colour’s SKU — which is what a picker would pack.',
      'Products: duplicates we have re-linked to the real product no longer appear in your product list. Nothing is deleted; your stock, orders and purchase orders stay on the real product.',
      'Products: when an old listing has been re-linked to the product that replaced it, that product’s stock statement now also shows the old listing’s history, in its own section, so none of your records disappear with it.',
    ],
  },
  'v1.229.0': {
    date: '25 Sep 2026',
    features: [],
    fixes: [
      'Products: adding variants in Shopify to a product that previously had none no longer creates a second, empty copy of that product here. Before this, the original kept your stock while a duplicate appeared under the same SKU with no photo and zero stock, and it could come back after every sync. Your orders, stock, purchase orders and stock statement now stay on the original product.',
      'Products: a product whose Shopify variant was removed no longer loses its variant name (for example "Blue / M") on the next sync.',
      'If duplicates were already created on your account, tell us and we will point them back at the right product. Nothing was deleted — the stock is still on the original product, and re-linking is reversible.',
    ],
  },
  'v1.228.1': {
    date: '23 Sep 2026',
    features: [],
    fixes: [
      'Velocity: if Velocity stops accepting the login session we hold for your account, we now sign in again and retry straight away, instead of every shipment failing with “authentication failed” until the session would have expired.',
      'Velocity: Test Connection now always checks with Velocity directly, so it can no longer report “connected” from a saved session while shipping fails.',
    ],
  },
  'v1.228.0': {
    date: '23 Sep 2026',
    features: [
      'Tab badge counts with a store selected now load in well under a second instead of around 14 seconds, and the badges now match the lists behind them exactly — Self Fulfilled, Prepaid RTO and Manually Fulfilled included.',
      'The Self Fulfilled, Prepaid RTO and Manually Fulfilled tabs now load only the page you are looking at, instead of reading your whole order history each time.',
    ],
    fixes: [
      'Orders you fulfilled yourself (shipped outside ModCommerce) are now marked finished once they are 60 days old. Nothing is deleted — they stay in the Self Fulfilled tab and in search, they simply stop being carried in the live working set, which is what was slowing every page down.',
      'Activity log entries are now kept for 60 days instead of 180.',
      'The app now has far more memory headroom, and the server watches its own memory, CPU and disk.',
    ],
  },
  'v1.227.3': {
    date: '22 Sep 2026',
    features: [],
    fixes: [
      'Locations: the Blue Dart service a warehouse ships on (Bharat Dart, Apex, Priority, Surface/Express) is now shown on the courier row, with the Customer Code and Area in its tooltip.',
      'Locations: editing a warehouse’s Blue Dart courier no longer wipes its service, Customer Code and Area. Before this, any edit — even changing only the priority — quietly reset the warehouse to the account default, which for most accounts is Dart Apex (air): the most expensive lane, and the wrong one for heavy parcels. Check your warehouses once: if the service tag is missing, set it again.',
      'Blue Dart: two rejections now say what to do instead of quoting Blue Dart — a destination your account is not allowed to ship to (extended-delivery pincodes), and a pincode the chosen service does not cover.',
    ],
  },
  'v1.227.2': {
    date: '22 Sep 2026',
    features: [],
    fixes: [
      'Blue Dart: a shipment is no longer lost when Blue Dart refuses to book the pickup for your Customer Code. The label is created anyway and the order ships — the pickup then shows as "failed" in Pickups, so arrange that one with Blue Dart directly. Before this, the whole booking was rejected ("not authorized to register pickup for specified Ares Customer code") and the order could not be shipped at all on any Blue Dart service.',
      'Blue Dart returns: if the account cannot register pickups, the reverse pickup now says exactly that, instead of Blue Dart’s wording.',
    ],
  },
  'v1.227.1': {
    date: '22 Sep 2026',
    features: [],
    fixes: [
      'Fixed the outage this morning: the panel, sign-in and admin login all stopped responding. The server ran out of memory because the dashboard figures were being rebuilt in a very wasteful way on large accounts.',
      'Dashboard figures now load several times faster on large accounts (about 25 seconds down to 4 on the largest).',
      'If the server ever stops responding again, it now restarts itself within 10 minutes instead of waiting for someone to notice.',
    ],
  },
  'v1.227.0': {
    date: '21 Sep 2026',
    features: [
      'Bulk Ship on Velocity is no longer stopped by Velocity’s request limit. Orders that have to wait for a free slot now queue and ship automatically in a follow-up batch instead of landing in Courier Error — the batch finishes on its own.',
    ],
    fixes: [
      'More of Velocity’s allowance is now reserved for creating shipments. Background jobs (cost updates and backfills) take far less of it, because tracking now arrives by webhook and no longer uses it at all.',
      'An order that could not even be sent to Velocity because the allowance was full is no longer marked as a courier error — nothing was sent, so it is simply tried again.',
    ],
  },
  'v1.226.2': {
    date: '19 Sep 2026',
    features: [],
    fixes: [
      'Pickups: shipments that Delhivery cancelled before pickup no longer show as "not picked" and are no longer carried into a new pickup every day.',
      'Delhivery: when Delhivery cancels a shipment before it is picked up, the order now goes back to Pending (Courier Error) so you can ship it again, and the shipping charge is refunded to your wallet — the same as other couriers. Orders already stuck this way have been moved back.',
      'Delhivery: parcels the customer refused at the door now appear in the NDR tab (they were hidden until they turned into RTO).',
    ],
  },
  'v1.226.1': {
    date: '19 Sep 2026',
    features: [
      'System Status page redesigned: a one-line summary at the top, a "Needs attention" list saying whose side each problem is on and what to do, then your account and all services side by side in a compact view.',
      'System Status in the left menu now shows a small dot: OK, Slow or Issue.',
    ],
    fixes: [],
  },
  'v1.226.0': {
    date: '19 Sep 2026',
    features: [
      'New System Status page: see at a glance whether ModCommerce, each courier (Delhivery, Velocity, Ekart, Shiprocket, Blue Dart) and Shopify are working. Every problem tells you whose side it is on (ModCommerce, the courier, your setup or the sales channel) and what to do.',
      'My Account status: your courier logins, sales-channel sync, recent bulk jobs and wallet balance, with a Check now button for each courier account.',
      'New System Status button in the left menu: click it for a quick summary, then open the full page for details.',
      'A banner appears across the app when there is an outage on our side, or at a courier or sales channel you use.',
      'Bulk Ship and Bulk Print: when some orders fail, a new Check system status link shows whether the courier or ModCommerce is having trouble.',
    ],
    fixes: [],
  },
  'v1.225.3': {
    date: '19 Sep 2026',
    features: [
      'Pickup manifest now has a Download PDF button that saves a clean PDF file, just like shipping labels.',
    ],
    fixes: [
      'Shipping label no longer gets cut off on the right side (QR code, amounts) when printing.',
      'Printing a pickup manifest no longer adds the browser’s date, page title and web address at the top and bottom of the page.',
      'Download PDF on a single label, GST invoices and stock statements now produces a real PDF.',
    ],
  },
  'v1.225.2': {
    date: '19 Sep 2026',
    features: [],
    fixes: [
      'Shipping labels, pickup manifests and order invoices: the Print button works again, and Download PDF now saves a real PDF instead of an .html file.',
    ],
  },
  'v1.225.1': {
    date: '19 Sep 2026',
    features: [
      "ModIQ: Today's Highlights and Stuck-in-OFD rows redesigned — fixed the dead blank space on wide screens, added the Sora display font for a more premium feel.",
    ],
    fixes: [],
  },
  'v1.225.0': {
    date: '19 Sep 2026',
    features: [
      'ModIQ: new "Today\'s Highlights" card — VIP/Repeat customers with a big order out for delivery or delivered today.',
      'ModIQ: new "Stuck in Out for Delivery" alert — flags orders sitting OFD 2+ days with no courier update.',
      'ModIQ: new COD Pipeline strip — In Transit vs Out for Delivery COD value, plus a modeled Expected Receivable.',
      'ModIQ: new 3-day cash forecast (today / tomorrow / day after) for COD collection.',
      'ModIQ: courier scorecard now flags an NDR/RTO rate spike vs its own 7-day baseline, and badges the best-performing courier to prefer.',
    ],
    fixes: [],
  },
  'v1.224.1': {
    date: '19 Sep 2026',
    features: [],
    fixes: [
      'Fixed: for about 20 minutes this afternoon (19 Sep), new Shopify orders from most stores were not imported by the automatic 15-minute sync. Nothing was lost — the next sync picks up every order from that window.',
    ],
  },
  'v1.224.0': {
    date: '19 Sep 2026',
    features: [],
    fixes: [
      'Sales Channels: syncing your stores no longer also tries to sync the Custom Channel (it has no online store behind it, so that attempt always failed quietly).',
    ],
  },
  'v1.223.3': {
    date: '19 Sep 2026',
    features: [],
    fixes: [
      'Fixed: shipping could fail with “That delhivery account is not active on this location” even though the courier was set up. When selecting orders moved the pickup location, the courier dropdown kept the account from the previous location. The courier list now always follows the pickup location, and a courier that location does not have goes back to Auto.',
    ],
  },
  'v1.223.2': {
    date: '19 Sep 2026',
    features: [],
    fixes: [
      'Orders page: the ⚙ Tabs button now opens its panel. It is next to the tab bar instead of at the end of it, so it is always visible. Tick to show or hide each status tab, and drag (or use ↑↓) to reorder them.',
    ],
  },
  'v1.223.1': {
    date: '19 Sep 2026',
    features: [],
    fixes: [
      'Blue Dart: orders with more than one item always failed with \u201CBad Request\u201D, and retrying never helped. We were telling Blue Dart the order had one piece in one place and several in another, and Blue Dart rejects that mismatch. Fixed \u2014 multi-item orders now ship. If you have orders stuck in Courier Error on Blue Dart, please try shipping them again.',
      'Blue Dart: re-shipping an order (for example after cancelling it) now sends Blue Dart a new reference number, so it cannot be rejected as a duplicate.',
      'Blue Dart: the real reason for a rejection is now shown in more cases, instead of \u201CBad Request\u201D.',
    ],
  },
  'v1.223.0': {
    date: '19 Sep 2026',
    features: [
      'If you have two accounts with the same courier on one location \u2014 for example Blue Dart Bharat Dart and Blue Dart Surface \u2014 you can now pick exactly which one to ship with, in Bulk Ship, the Ship popup and Retry. The shipment goes through that account only.',
    ],
    fixes: [
      'Fixed: choosing your second account of the same courier in the courier dropdown jumped back to the first one, so it could never be selected.',
      'Blue Dart: before booking, we now check that the service you picked actually delivers to that pincode for that payment type. Instead of a bare \u201CBad Request (HTTP 400)\u201D, you now see, for example, \u201CBlue Dart Surface does not deliver COD to pincode 509375\u201D, so you know to use another courier.',
      'Blue Dart: when Blue Dart rejects a shipment, you now see Blue Dart\u2019s actual reason instead of \u201CBad Request\u201D.',
    ],
  },
  'v1.222.4': {
    date: '17 Sep 2026',
    features: [],
    fixes: [
      'Velocity is working again \u2014 Velocity has lifted the block on our server. Shipping, rates and cancelling are back.',
      'Velocity tracking now comes only from Velocity\u2019s own real-time updates (webhooks), as Velocity asked. Nothing changes for you if your webhook is already set up.',
      'The Velocity Health check now also tells you if no tracking updates have arrived for 24 hours while parcels are on the move, which means the webhook is missing or wrong. The URL, header and key to paste are on your Velocity account card.',
    ],
  },
  'v1.222.3': {
    date: '17 Sep 2026',
    features: [],
    fixes: [
      'Velocity confirmed their system blocked our server for sending too many requests at once, and is lifting the block. Every part of ModCommerce that talks to Velocity now shares one allowance that stays inside Velocity\u2019s limit, so this cannot happen again.',
      'Velocity tracking now relies on Velocity\u2019s own real-time updates, which were already switched on for your account. We only double-check shipments Velocity has not updated for six hours.',
      'If you ship a very large Velocity batch in one go, it may now go through at a steady pace instead of all at once. Your ship requests always get priority over background checks.',
    ],
  },
  'v1.222.2': {
    date: '17 Sep 2026',
    features: [],
    fixes: [
      'The courier Health check and Update credentials no longer say “Login failed” when the courier’s firewall is refusing our server. They now say plainly that it is not a credentials problem, so nobody changes a password that was never wrong.',
    ],
  },
  'v1.222.1': {
    date: '17 Sep 2026',
    features: [],
    fixes: [
      'Velocity: if you saw \u201Cauthentication failed\u201D over the last few days, your password was never the problem. Velocity\u2019s firewall has been refusing connections from the ModCommerce server since 11 September, before it even checks a password \u2014 which is why reconnecting did not help. Connecting Velocity now tells you exactly that instead of asking you to check your credentials. We are working with Velocity to restore access; you do not need to do anything.',
      'Our Velocity tracking now asks for many shipments in a single request instead of one request per parcel, and backs off completely the moment Velocity refuses us. The old behaviour sent hundreds of requests every 15 minutes, which is very likely what triggered the block in the first place.',
    ],
  },
  'v1.222.0': {
    date: '12 Sep 2026',
    features: [
      'Orders page: a new Columns button beside Sort. Show or hide any column, drag them into the order you want, and switch between three ready-made views — Full, Packing and Finance. Your choice is yours: two people on the same account can look at the same tab differently at the same time.',
      'Save your own views with “Save as…”. Saved views are shared with your team, so you can set up a Packing view once and your warehouse gets it.',
      'Tabs can be hidden and reordered too — the ⚙ at the end of the tab bar. Hiding a tab never hides orders: a link straight to a hidden tab still opens it. All Orders cannot be hidden, since it is the one tab guaranteed to contain everything.',
    ],
    fixes: [
      'Removed the “Push to Shopify” button. It re-sent a delivery update for EVERY order on the account — 29,482 on the largest one, about 98 minutes of work per click — including the 85% that had already been sent. Worse, re-sending a “Delivered” for an order delivered months ago stamped it as happening today, which rewrote the customer’s tracking timeline in your Shopify with a false date. Nothing is lost: your Shopify is already kept in step automatically every 15 minutes, and any fulfilment that failed at ship time is retried every 30.',
      '“Sync Tracking” now refreshes the orders on the page you are looking at, instead of every shipment on the account. It used to try to poll up to 15,319 parcels inside a request that could never finish. A page of orders now takes about two seconds. The full sweep still runs automatically every 15 minutes.',
    ],
  },
  'v1.221.0': {
    date: '12 Sep 2026',
    features: [
      'The Dashboard now has two date controls instead of one. The row at the top still opens on Today — that is your daily operating number. Everything below it (RTO by State, Top Selling States, Channel and Courier Performance, Top Products) has its own period and opens on Last 30 Days, because a single day is too short for those to say anything.',
      'The product filter now lists every product you have ever sold, with a search box and the SKU under each name. Before it only offered the top products of whatever date range you were looking at — on “Today” that was about 13 out of 86, and the rest were unreachable.',
      'Clicking a row in Top Products by Revenue now filters the whole Performance section to that product. Click it again to clear.',
    ],
    fixes: [
      'Fixed: RTO by State showed nothing on short date ranges. It was counting by the date the order was PLACED — but an order placed today cannot have shipped, failed delivery and come back today, so the panel could only ever be empty. It now counts by the date the parcel actually came back, so “today” means “what returned today”.',
      'Fixed: a product you had renamed in Shopify appeared TWICE in Top Products by Revenue, with its sales split between the old name and the new one. Products are now matched properly and shown under their current name. Five products on one account were affected.',
      'Empty panels now say which period they are empty for — “No parcels returned in last 30 days” instead of “No RTO yet”, which read as “never”.',
      'The Dashboard was quietly loading its analytics twice on every open. Now once.',
    ],
  },
  'v1.220.0': {
    date: '12 Sep 2026',
    features: [
      'The date filter on the Dashboard now drives Top Products by Revenue, RTO by State and Top Selling States. Until now those three ignored the dates completely and always showed your entire order history, so “last 7 days” and “all time” looked identical.',
      'Two new filters sit beside the dates: filter by sales channel and filter by product. Picking a product shows what that product alone earned, not the full value of every order that happened to contain it.',
      'ModIQ has moved out of the Dashboard and into its own place at the top of the left menu.',
    ],
    fixes: [
      'The “Money in motion” row and Avg Delivery TAT, Shipping Cost / Revenue and Repeat Customers are deliberately NOT affected by the date filter — a parcel is in transit now or it is not — and they now say so on screen.',
      'The Dashboard is also faster: asking for one month now reads one month of orders instead of every order you have ever had.',
    ],
  },
  'v1.219.1': {
    date: '12 Sep 2026',
    features: [],
    fixes: [
      'Your GST invoices now show the tax correctly split. A West Bengal business sees CGST and SGST as separate lines; everyone else sees IGST. Place of supply is shown too. Previously every invoice showed a single “GST 18%” line, which a registered business cannot claim input credit against. The amounts are unchanged — only how they are presented — and past invoices show the correct split when re-downloaded.',
    ],
  },
  'v1.218.0': {
    date: '11 Sep 2026',
    features: [],
    fixes: [
      'Fixed: on an order where the customer had already paid part of the amount up front, the courier was told to collect the FULL order value — so the customer would have paid their advance twice. Example: ₹699 order with ₹100 already paid; the label and the courier both said ₹699 instead of the ₹599 actually owed. This now works for every courier and the label matches what the courier is told.',
      'This affects partial-prepaid COD orders (the kind where a customer pays a small amount at checkout to confirm the order). Existing orders have been corrected.',
    ],
  },
  'v1.217.0': {
    date: '11 Sep 2026',
    features: [],
    fixes: [
      'The NDR section has been taken off the menu for now while it is designed further. Nothing about it is lost — failed deliveries are still recording why they failed in the background, so when it returns it will have a full history rather than starting from empty.',
    ],
  },
  'v1.216.2': {
    date: '11 Sep 2026',
    features: [
      'Delhivery NDR actions are now LIVE. Reattempt, address fix and RTO work from the NDR page for every Delhivery parcel — verified with a real reattempt that Delhivery accepted. The other couriers stay locked until each is tested the same way.',
    ],
    fixes: [
      'Reattempt is no longer offered when the courier has already rescheduled the delivery. If the customer has agreed a new date, sending a reattempt overrides it with “tomorrow” — pushing the parcel out on a day they already said they would not be there, and spending an attempt to do it.',
    ],
  },
  'v1.216.0': {
    date: '11 Sep 2026',
    features: [
      'The NDR page now has action buttons — Reattempt, Fix address, RTO, Defer, Escalate — one per courier, showing exactly what each courier can and cannot do.',
      'Nothing is sent to a courier yet. Clicking an action shows you the exact request that WOULD be sent, so each courier can be checked before it is switched on. A delivery attempt is spent whether or not it succeeds, so no button goes live until that courier has been tested on a real shipment.',
      'Actions that would waste an attempt are refused with the reason: reattempting a parcel with a bad address, one the customer refused, or one the courier says is finished. Returning to origin stays available, because giving up is always safe.',
    ],
    fixes: [],
  },
  'v1.215.2': {
    date: '11 Sep 2026',
    features: [],
    fixes: [
      'The NDR and Reports pages are now proper tables with column headings — Order, Customer, Reason, Courier / AWB, Attempts, Waiting, Value — the same as the Orders and Return / Exchange lists. They were stacked blocks of text before, which made them hard to scan down a column.',
    ],
  },
  'v1.215.1': {
    date: '11 Sep 2026',
    features: [],
    fixes: [
      'Fixed: the new NDR and Reports pages sat in a narrow column in the middle of the screen instead of filling the width like every other page, which made them look bolted on rather than part of the panel. Both now match the rest of the app.',
    ],
  },
  'v1.215.0': {
    date: '11 Sep 2026',
    features: [
      'New “NDR” section in the left menu — every parcel a courier could not deliver, in one place, oldest first. Each one shows WHY it failed in the courier’s own words, how many attempts have been made, how long it has been stuck, the customer’s phone number, and what the order is worth.',
      'Reasons are grouped so you can see the shape of the problem at a glance — how many are “customer unavailable” versus a bad address versus a refusal — and filtered by reason or courier.',
      'The page is view-only for now. Reattempt, address fix and force-RTO buttons will appear per courier once each one has been tested against a real shipment, because a reattempt is spent whether or not it works.',
    ],
    fixes: [
      'Velocity parcels are marked view-only with the reason stated: Velocity provides no way for us to action a failed delivery from here, so those must still be handled in the Velocity panel.',
    ],
  },
  'v1.214.0': {
    date: '11 Sep 2026',
    features: [],
    fixes: [
      'Delivery failures now record WHY. Until today a failed delivery was saved only as “NDR” with the courier’s explanation thrown away — every Delhivery failure in the order timeline read “Delhivery: Pending”, which told you nothing. The reason is now kept, and the timeline shows it (for example “Consignee Unavailable” or “Incomplete address”).',
      'This is the groundwork for the NDR panel. It only starts collecting from today, which is why it has been released on its own ahead of the rest.',
    ],
  },
  'v1.213.0': {
    date: '10 Sep 2026',
    features: [
      'The export now has a “Line Revenue” column — what each product actually contributed to the order. Add it up and you get your real revenue, matching the dashboard. It uses the figure Shopify itself calculates for each product, not a share we work out ourselves.',
      'New columns for Order Discount, Order Tax and Order Shipping, and a “Line Tax” per product. Tax is already inside the revenue figure, never on top of it.',
    ],
    fixes: [
      'Fixed: Order Total was repeated on every row of a multi-product order, so adding up that column counted a 3-product order three times. It now appears once per order, on its first row, and is safe to add up.',
      'Fixed: the file used to say “add up Line Total for revenue”. That was wrong whenever an order had a discount or shipping — only 40% of multi-product orders (and 80% of single-product ones) added up correctly. Line Total is now clearly the price before order discounts, and Line Revenue is the figure to add up.',
      'A “Revenue Basis” column says whether each figure came from Shopify or was worked out here, so a number is never presented as more certain than it is.',
    ],
  },
  'v1.212.0': {
    date: '09 Sep 2026',
    features: [
      'Export your orders to CSV. There is now an “Export” button on the Orders page: it exports exactly what the tab is showing, with the date range and every filter you have applied. It tells you how many orders you are about to export before you commit.',
      'One row per product, with the order’s details repeated on each row so you can filter and pivot in Excel without lookups. Sum “Line Total” for revenue, or filter “Order Row = 1” for one row per order — both are correct, neither double-counts.',
      'New “Reports” section in the left menu. Every export is listed there and can be downloaded again without re-running it, so a big export never has to be repeated. Files are kept for 15 days; after that the row stays but the file is removed.',
      'Customer contact details (phone, email, street address) are only included if you tick the box. Name, city, state and pincode are always there. Which exports included contact details is recorded on the Reports page.',
    ],
    fixes: [
      'Blue Dart shipments now get a working tracking link pushed to your sales channel. The carrier list used for those links had never included Blue Dart.',
    ],
  },
  'v1.211.0': {
    date: '09 Sep 2026',
    features: [
      'New “Order Type” filter. Reship clones and exchange replacements are the same parcel going out again, or a replacement for something returned — neither is new revenue, and the dashboard already leaves them out. You can now filter the list to Original only, or to a specific type to see what has been re-sent.',
    ],
    fixes: [
      'Fixed: date filters were missing the first 5½ hours of the start day. The “from” date was being read in the wrong time zone, so orders placed between midnight and 5:30am on that day were left out — about 5% of all orders. A single-day view was quietly short by that much. Both ends of the range now use IST.',
      'Fixed: “Clear All” in the filter panel left one filter still applied. There were two Clear buttons that cleared slightly different lists, so one always missed something.',
      'Housekeeping: generated files (bulk label PDFs) are now deleted after 7 days as intended. The rule existed but stopped working when we changed servers, so nothing had been cleaned up since August.',
    ],
  },
  'v1.210.0': {
    date: '08 Sep 2026',
    features: [],
    fixes: [
      'Faster first load after a restart or update. The server keeps a saved copy of your open-order list so it can start from that and top it up, instead of rebuilding the whole list from scratch. That saving had stopped working when we moved servers and had been quietly rebuilding everything since — it now works again, cutting the first load on the largest account from about 4 seconds to about 2.4.',
    ],
  },
  'v1.209.0': {
    date: '08 Sep 2026',
    features: [],
    fixes: [
      'Fixed: order tabs hanging, loading slowly, or failing to open. The server was running out of memory and restarting under load. On a busy account every incoming order update marks the order list as needing a refresh, and several page loads arriving together each rebuilt the entire open-order set separately — seven full rebuilds in one minute on the largest account, until the server ran out of memory and restarted. Those rebuilds are now shared: however many requests arrive at once, the list is rebuilt once and everyone gets the same result.',
      'Fixed: the server was limited to half the memory it had been given, so it ran out sooner than it needed to. It now uses the full allocation.',
    ],
  },
  'v1.208.0': {
    date: '08 Sep 2026',
    features: [],
    fixes: [
      'Fixed: the admin Edit Client form had a large gap under every field. Two different form styles had been given the same name, so the compact one written for that form was being overridden by the roomy one meant for a single-field dialog.',
      'Fixed: the admin Reset Password dialog was partly unstyled — its heading and button row referred to styles that were never defined, which fails silently rather than showing an error.',
      'Fixed: the welcome popup appeared without its fade-in. Its animation referred to a definition that was only loaded by a different popup.',
    ],
  },
  'v1.207.0': {
    date: '08 Sep 2026',
    features: [
      'The Customer tab now names the courier next to the waybill, so you can see who is carrying a parcel without opening the order.',
    ],
    fixes: [
      'Fixed: the tracking number in the Customer tab was not actually clickable. It was being shown as plain text because the tracking link was looked up under the wrong field name. It is now a working link that opens the courier’s tracking page in a new tab, marked with ↗ like the one on the Orders list.',
      'Fixed: the customer summary left a band of empty space. The First order and Last order dates were sitting in the same row of tiles as the counts, wrapping onto a second row and leaving most of it blank. They now read as a line beneath the counts — and a customer with a single order sees “Ordered” once rather than the same date twice.',
    ],
  },
  'v1.206.0': {
    date: '08 Sep 2026',
    features: [
      'The Customer tab now shows the sales channel and a clickable tracking number under each order in the history — click the waybill to open the courier’s tracking page directly, instead of copying the number and finding the site yourself. The AWB on the Order tab is now a link too.',
    ],
    fixes: [
      'Fixed: COD orders showed as “Prepaid” in the Customer tab. Once a COD is collected on delivery the order is marked paid, and this panel read that as prepaid — so every delivered COD order claimed to be prepaid. It now says “COD (Paid)”, exactly as the Delivered tab does. This affected 11,422 orders on display; nothing was miscounted or mischarged, only mislabelled here.',
      'Fixed: the sales channel never appeared in the Customer tab for any order. It is now shown under each order in the history.',
      'Fixed: the First order and Last order dates in the customer summary were cut off mid-date (“04 Sept 202”). The summary now wraps instead of cropping, so every figure is readable.',
      'Fixed: the sort buttons in Compare Couriers were picking up the wrong styling from an old rule with the same name.',
    ],
  },
  'v1.205.0': {
    date: '08 Sep 2026',
    features: [],
    fixes: [
      'Fixed: the Delivered tab went blank. An icon used by the new return/exchange badge was never imported, so opening a tab containing an exchanged order crashed the page. Only exchanges hit it, which is why it showed up on Delivered and nowhere else.',
      'A page that hits an error no longer blanks the whole screen. Until now any single mistake anywhere in a page wiped out everything — no message, no menu, nothing to report. You now get an explanation, the menu stays usable so you can carry on elsewhere, and there is a button to retry the page.',
      'Added an automatic check that catches this class of mistake before a build can ship, so a missing import fails the build instead of reaching you.',
    ],
  },
  'v1.204.0': {
    date: '08 Sep 2026',
    features: [
      'Orders that came back now say so. A delivered order that the customer later returned or exchanged carries a badge on its row — “Return requested”, “Return coming back”, “Refund pending”, “Returned”, “Exchanged” — so you can see it at a glance instead of only finding out inside Return & Exchange. It names which it was, and shows a count when an order has more than one request.',
    ],
    fixes: [
      'Fixed: cancelling a return or exchange request left the original order marked as returned for ever. The marker is now recalculated from the requests themselves every time one changes, so cancelling clears it.',
      'Existing orders have been backfilled, so the badge is correct on past returns too, not only new ones.',
    ],
  },
  'v1.203.0': {
    date: '08 Sep 2026',
    features: [],
    fixes: [
      'Fixed: Confirm could push an order with no stock straight into Ready to Ship. The Confirm button never checked stock at all — it simply switched the check off — so an order with nothing available could be confirmed silently, and in bulk, and then sat in Ready to Ship with no location to ship from. Confirm now only works when stock is actually available at a location, or when Auto PO is switched on for the products. Otherwise it is refused and tells you which item is short and by how much.',
      'Confirming for other reasons is unchanged. An order held for an address or other detail can still be confirmed — you are the authority on those. Stock is different: the parcel either exists or it does not.',
      'Bulk actions now tell you why something failed. "12 confirmed, 8 failed" used to be a dead end; the reason is now shown with the result.',
    ],
  },
  'v1.202.0': {
    date: '07 Sep 2026',
    features: [
      'Search several orders at once. Paste a list of order IDs or AWBs separated by commas — or paste a column straight out of a spreadsheet — and every one of them comes back together, instead of searching them one at a time. Works in both the top search bar and the search box on the Orders list.',
      'When you paste a list, the panel tells you how many were found and names the ones that were not — so a mis-typed or missing order number is obvious instead of silently absent.',
    ],
    fixes: [],
  },
  'v1.201.0': {
    date: '07 Sep 2026',
    features: [],
    fixes: [
      'Fixed: re-shipping an order after you cancelled it could book the OLD, cancelled tracking number instead of a new one. The order looked shipped and sat in Ready to Pickup, but the courier had already closed that waybill, so it could never be collected. Re-shipping now always books a genuinely new tracking number, and as a second safeguard the system refuses any tracking number the courier reports as already cancelled.',
      'Fixed: cancelling a shipment left the order attached to its old pickup batch. That did two things — the batch showed a count including orders no longer in it, and if you re-shipped the order it could never join a new pickup batch, so it stayed in Ready to Pickup indefinitely. Cancelling now releases the order from its batch and corrects the count.',
      'Fixed: the nightly pickup roll-over only released orders that still had a tracking number, so an order cancelled just before the roll stayed locked to a closed batch for good. It now releases every order that was not actually collected.',
      'Repaired the 21 orders already affected: they have been returned to Ready to Ship, unfulfilled in your sales channel so customers no longer see a dead tracking link, stock put back, and the shipping charge refunded. Re-ship them normally and they will get fresh tracking numbers.',
    ],
  },
  'v1.200.0': {
    date: '02 Sep 2026',
    features: [
      'Loading placeholders everywhere. Returns & Exchange, RTO Reconcile, Pickups, Customers, Purchase Manager, Vendors, Locations, Payments, Team, Couriers, Stores, Billing and Bulk Actions now show a greyed-out outline of the page while data is on its way, instead of the word “Loading…”. You can see the shape of what is coming, and nothing jumps around when it lands. Orders, Products and the Dashboard already worked this way — now the whole panel does.',
      'Big money figures are shortened on the Dashboard tiles: ₹68,67,841 now reads ₹68.7L, and anything over a crore reads like ₹4.2Cr. Hover any tile to see the exact rupee figure. Nothing else was shortened — order tables, invoices, wallet, billing and COD amounts still show the full number, because those are what you reconcile against your bank.',
      'Every number column now lines up. Digits used to be different widths, so a column of amounts wobbled slightly from row to row. They are now fixed-width, which makes long lists of money and quantities far easier to scan.',
    ],
    fixes: [
      'Emoji have been replaced with proper drawn icons throughout the panel — around 150 of them. Emoji are drawn by your operating system, so the same icon looked different on Windows, a Mac and a phone. The new icons look identical everywhere and match the rest of the interface.',
      'Card and popup shadows now follow one consistent scale instead of 79 slightly different hand-written values, so panels sit at believable depths rather than each having its own grey blur.',
      'Keyboard users can now see where they are. Buttons, links and inputs show a clear blue outline when reached by Tab — previously there was no visible indicator at all. It does not appear when clicking with a mouse.',
      'Animations across the app now share one timing and easing curve, so transitions feel like part of the same product rather than each screen having its own rhythm.',
    ],
  },
  'v1.199.2': {
    date: '30 Aug 2026',
    features: [],
    fixes: [
      'The bulk card now respects your device’s “reduce motion” setting. If you have that turned on — some people use it because sliding movement makes them feel unwell — the card no longer flies across the screen. It fades away in place instead.',
      'The copy buttons next to a customer’s name, phone and email are easier to hit, and screen readers now announce what each one copies.',
    ],
  },
  'v1.199.1': {
    date: '30 Aug 2026',
    features: [],
    fixes: [
      'The summary no longer opens by itself when a batch finishes. It used to throw a full-screen popup at you the moment shipping completed, covering the page and hiding the very card that tells you where the record went. The result is still worked out immediately — click the card and it opens instantly, with nothing to wait for.',
      'The card animation is now slow enough to actually follow. It was jumping straight to the end instead of travelling, so there was nothing to see. It now blinks, then glides across to Bulk Actions over about a second, and the sidebar item stays lit until it arrives.',
      'The card lands on Bulk Actions accurately. It was aiming at where the sidebar had been a moment earlier, so it finished slightly off; it now takes its bearing at the moment it sets off.',
    ],
  },
  'v1.199.0': {
    date: '30 Aug 2026',
    features: [
      'The finished bulk-action card now clears itself. When a batch completes, the small card in the bottom-right waits ten seconds, then blinks and flies across to Bulk Actions in the sidebar before disappearing — so you can see where the record went instead of wondering whether the batch was lost. Closing it with the X does the same thing.',
      'A finished card can now be opened. Click it and the full result opens in place, with the summary one click away — useful for reading which orders failed without leaving the page.',
    ],
    fixes: [
      'The ten seconds never runs out while you are reading. Hovering the card stops the clock, opening it stops the clock, and it does not count down at all while you are in another browser tab — so the card cannot vanish unseen.',
      'On a phone, with the sidebar collapsed, or if your account does not show Bulk Actions, the card simply fades away instead of flying to something that is not there.',
    ],
  },
  'v1.198.2': {
    date: '30 Aug 2026',
    features: [],
    fixes: [
      'Invoices now work for older orders too. Some orders are no longer held by the sales channel — it purges them after a while — so their discount could not be looked up and their invoice was refused. For those, the discount is now worked out from the order itself: where the products add up to more than the customer paid and no shipping was charged, the difference is the discount. 44,816 orders can now produce a correct invoice, up from about 32,700.',
      'Around 190 orders still refuse, and deliberately so: they have a missing product price, are split sub-orders, or add up to less than was paid. Stretching them to fit would invent a taxable value, so they are flagged instead of quietly guessed at.',
    ],
  },
  'v1.198.0': {
    date: '29 Aug 2026',
    features: [],
    fixes: [
      'GST invoices were showing the price before your discount. A discount code applied to the whole order — a prepaid discount, for example — is held by the sales channel against the order, not against each product line, and we were building the invoice by adding up the product lines alone. An order paid at ₹629.10 printed an invoice for ₹699.00, with the tax overstated to match. Invoices now show the discount, and the taxable value and tax are calculated on what the customer actually paid.',
      'The discount is spread across the products in proportion to their value, rather than shown as one line at the bottom, so each product’s taxable value is correct for its own GST rate.',
      'Shipping, when you charge for it, now appears as its own line with its own SAC code instead of being mixed into product prices.',
      'An invoice whose total does not match the amount paid is now refused, with the reason, instead of being printed. A tax invoice stating a figure nobody paid is worse than no invoice.',
      'Existing orders are being updated in the background so their invoices come out right. Orders imported before this change may briefly refuse to print an invoice until that finishes.',
    ],
  },
  'v1.197.0': {
    date: '29 Aug 2026',
    features: [
      'See a customer’s whole history without leaving the Orders page. Click a customer’s name, phone or email in the list and the order window opens on a new Customer tab — their contact details, how many orders they have placed, delivered and returned, what they have spent, and every past order with its products, store, payment type, amount and status. The order you came from is highlighted so you never lose your place.',
      'Copy buttons on customer name, phone and email. They appear when you hover a row, alongside the ones already on order number, SKU and AWB — so a number can go straight into WhatsApp or a courier’s support tool without retyping.',
      'The order window now has Order and Customer tabs. Everything you had before is on the Order tab, unchanged and slightly shorter to scroll. The Customer tab only loads when you open it, so ordinary order views are no slower.',
    ],
    fixes: [
      'Customer Intelligence subscribers also see the VIP / Repeat / Risky / Dormant tier, the customer’s RTO rate and their spend against your account average, right on the Customer tab.',
    ],
  },
  'v1.196.0': {
    date: '29 Aug 2026',
    features: [
      'Volumetric weight is now used everywhere. Couriers charge on whichever is greater — the actual weight or the size of the box (L×B×H ÷ 5000) — and we were only ever using the actual weight. A large, light parcel was therefore quoted and declared far under what the courier would bill. Rates now match the courier’s own panel exactly, and services you were not being shown (a “2 Kg” lane that only applies to heavier parcels, for instance) now appear.',
      'The comparison shows both weights — actual and volumetric — with the one being charged highlighted, plus the delivery zone where the courier reports it. So a price that looks high is explainable at a glance instead of surprising.',
    ],
    fixes: [
      'Order weights now follow a clear rule: an edited weight always wins, and otherwise the weight synced with the order is used. Previously the weight synced with the order was ignored entirely, so an order carrying 1.5 kg could be quoted and shipped at whatever the product list happened to say — 0.98 kg in one real case.',
      'This applies to shipping as well as to quotes, so parcels are now declared at the weight the courier will actually bill for. If your product weights or box sizes are wrong, they will now show up as higher quotes rather than as surprise charges on your invoice.',
    ],
  },
  'v1.195.1': {
    date: '29 Aug 2026',
    features: [],
    fixes: [
      'Compare Rates was overcharging every COD row. The price a courier quotes already includes its COD fee, and we were adding the fee on top of it — so a ₹170 courier showed as ₹217. Corrected: the price shown is now the courier’s own total, with the freight and COD split spelled out underneath so you can check it against the courier’s panel yourself.',
      'Rates now match the courier’s own panel to the rupee, apart from the flat ₹5 the panel adds on top of the API price on every row.',
      'The comparison now tells you where the weight came from, and warns when your product master disagrees with the weight synced with the order. That mismatch is why the courier’s panel sometimes shows a heavier parcel — and a heavier price — than we quoted.',
      'Couriers with a minimum billable weight now show it. A service with a 2 kg minimum quoting a 0.8 kg parcel says “billed 2 kg”, which is the only thing that explains why that row costs more.',
    ],
  },
  'v1.195.0': {
    date: '29 Aug 2026',
    features: [
      'Compare Rates now works on Velocity too, not just Shiprocket. Velocity was already fetching every courier and its price on each shipment and quietly keeping only the cheapest — the same list is now shown to you, with the delivery estimate and RTO charge for each.',
      'The comparison window has been rebuilt as a proper table. Courier, service and rating, delivery and charges, and total cost each get their own column, so six couriers can be compared down a straight line instead of read one block at a time. Each courier gets a coloured badge so you can find the one you want at a glance.',
      'The price shown is now the real total — freight plus the COD fee — for COD orders. Quoting bare freight understated every row by a different amount, which is exactly when a comparison misleads.',
    ],
    fixes: [
      'The Create Shipment buttons no longer look cramped. Cancel now takes only the space its label needs, leaving Compare Rates and Create Shipment room for their full labels.',
      '"RTO ₹0.00" no longer appears on couriers that simply do not quote an RTO charge — it now says so instead of showing a price of zero.',
    ],
  },
  'v1.194.0': {
    date: '29 Aug 2026',
    features: [
      'Compare courier rates before shipping a single order. Shiprocket normally picks the sub-courier for you. Now, when you ship one order through it, the Create Shipment window has a new “Compare Rates” button that shows every courier available for that route — what each one charges, how many days it takes, and which one Shiprocket would have picked — and you ship with the one you choose, straight from the list. The cheapest is marked, and each row shows how much more it costs than the cheapest, so “is a day faster worth ₹21?” is answerable at a glance.',
      'Shipping costs are now recorded for Shiprocket shipments. They never were — Shiprocket was the one courier with no rate lookup wired up, so its shipments showed no cost at all in your reporting. Every new Shiprocket shipment now records an estimate like the other couriers do.',
    ],
    fixes: [
      'Shipping in bulk is unchanged and still assigns couriers automatically — comparing rates is for one order at a time, where it is worth the extra few seconds. The “Ship” button you use today behaves exactly as before.',
      'If the courier you picked is not the one that ends up assigned, you are now told so plainly instead of finding out from the invoice.',
    ],
  },
  'v1.193.0': {
    date: '29 Aug 2026',
    features: [
      'Edit a delivery address before you ship it. Customers ring up with a corrected flat number or a new phone the moment after they order, and until now the only place to fix it was in Need Attention — so an order that had passed all the checks could not be corrected at all. Ready to Ship orders now have “Edit Address & Contact” in the ⋮ menu, and the change is pushed back to your sales channel too.',
      'The same edit is now available on No Stock and Courier Error orders. A courier rejecting an order for a bad pincode is exactly when you need to change the pincode, and those orders had no Fix button because nothing was technically invalid about them.',
    ],
    fixes: [
      'A corrected address can no longer be quietly undone. Previously, if the update could not reach your sales channel at that moment, it was saved here and the next sync — within 15 minutes — copied the old address straight back over it, with nothing to show that had happened. The correction is now protected until it reaches your channel, and retried automatically every 20 minutes in the background. Your parcel always goes to the corrected address regardless.',
      'Addresses cannot be edited once a tracking number has been booked. At that point the courier and the printed label both hold the old address, so changing it here would only have made the panel disagree with the parcel. Cancel the shipment first, then edit.',
      'Every address change is now recorded — which field changed, from what to what, by whom and when — so a delivery dispute can actually be answered.',
    ],
  },
  'v1.192.1': {
    date: '29 Aug 2026',
    features: [],
    fixes: [
      'Printing labels from a Bulk Ship entry no longer reports “completed with errors”. It was being given every order in the batch, including any that failed to ship — those have no tracking number, so no label can exist for them and they were counted as print failures. It now prints only the orders that actually shipped, and tells you how many were left out.',
    ],
  },
  'v1.192.0': {
    date: '29 Aug 2026',
    features: [
      'Filter orders by quantity. The Filter panel has a new Quantity option — 1, 2, 3, 4 or 5+ units — so you can pull up just the single-unit orders to pack quickly, or find the multi-unit ones that need a bigger box. It counts total units in the order, so one product ordered twice and two different products both count as 2.',
    ],
    fixes: [],
  },
  'v1.191.0': {
    date: '29 Aug 2026',
    features: [],
    fixes: [
      'Orders shipped from ModCommerce are now reliably marked as fulfilled in your sales channel. If that step ever failed — a momentary problem at the channel’s end — nothing retried it, so the order stayed unfulfilled there: your customer got no shipping email and no tracking link, and there was no sign anything was wrong. It is now retried automatically every half hour until it succeeds. 564 affected orders going back to June have been fulfilled and their tracking sent.',
    ],
  },
  'v1.190.4': {
    date: '29 Aug 2026',
    features: [],
    fixes: [
      'The small bulk-progress card now says when a batch has finished. It kept reading “Creating shipments” even after the batch was done, which made a completed job look like it was still running. It now reads “Shipping complete”, or “Shipping finished with errors” when something failed.',
    ],
  },
  'v1.190.3': {
    date: '29 Aug 2026',
    features: [],
    fixes: [
      'Products with an emoji in the title can be shipped through Shiprocket again. Shiprocket cannot store emoji, and rejected every order for such a product with a message saying to try again later — so the orders looked temporarily stuck when in fact no retry could ever have worked. Emoji are now removed from the product name we send to the courier; the name in your store, on your invoices and in the panel is unchanged.',
    ],
  },
  'v1.190.2': {
    date: '29 Aug 2026',
    features: [],
    fixes: [
      'While a bulk batch is running, the order list no longer flickers. On a large selection the list was redrawing every second or so; it now redraws only when an order actually changes state.',
      'The bulk action bar is hidden while a batch is running, and orders cannot be selected until it finishes. Previously the bar stayed on screen offering “Bulk Ship” and “Cancel Orders” over orders that were already being shipped.',
      'The small progress card can no longer be closed mid-batch — only minimised. Closing it never cancelled anything, but it did stop the batch being tracked, which re-enabled the Ship button on orders that were still shipping. The close button appears once the batch finishes.',
      'Shiprocket: an order is now shipped even when Shiprocket’s automatic courier selection declines it. It was reporting “no courier service” for destinations its own pricing service could serve — which is why those orders could be shipped by hand from the Shiprocket panel but not from here.',
    ],
  },
  'v1.190.1': {
    date: '29 Aug 2026',
    features: [],
    fixes: [
      'Orders that clearly show a SKU no longer fail to ship with a “Courier error”. If a product’s SKU was added in your store after an order came in, the order itself still held a blank SKU — the panel filled it in from your product list when displaying it, but the courier was sent the blank. One courier rejects a shipment outright for that. The SKU is now filled in from your product list before the shipment is created, so what the courier receives matches what you see.',
    ],
  },
  'v1.190.0': {
    date: '29 Aug 2026',
    features: [
      'Bulk Ship and Bulk Print can now be minimised, so you can carry on working while a batch runs. Click the “–” on the progress popup and it shrinks to a small card in the bottom-right corner. Move to Customers, Pickups, anywhere you like — the batch keeps going and the card keeps counting. Click the card to bring the full view back, and when it finishes click it once more to see the summary.',
      'Run more than one batch at a time. Ship 100 orders, minimise, then start another 50 — each batch gets its own card and its own progress, instead of the second one taking over the first.',
      'Orders that are currently being shipped by a batch now show a “Processing” spinner in place of the Ship button, and cannot be selected into a second batch. If a colleague started the batch, their name is shown — so a greyed-out button is never a mystery. This also stops those orders being reported as failures when two batches overlap.',
    ],
    fixes: [
      'An order that was already shipped no longer counts as a failure in a Bulk Ship summary. If an order was shipped individually while a batch was still working through its list, the batch reported it as failed even though it had shipped correctly.',
    ],
  },
  'v1.189.6': {
    date: '29 Aug 2026',
    features: [],
    fixes: [
      'Shipping cost and COD charges are showing again on orders shipped in bulk. When many orders were shipped at once, the courier’s pricing service refused the rapid burst of price lookups, so those orders were saved without a cost. Prices are now fetched steadily in the background just after shipping instead of all at once, and every affected order has been filled in — 134 of them, going back to yesterday. Shipping itself was never affected, only the cost figure shown against the order.',
    ],
  },
  'v1.189.5': {
    date: '29 Aug 2026',
    features: [],
    fixes: [
      'Bulk Ship now tells you exactly why an order could not be shipped. Previously a failed order just said “Courier error”, even though the courier had given a clear reason — most often that it does not deliver to that pincode. You now see the courier’s own words, so you can act on it instead of guessing.',
      'Shipping-cost estimates are more reliable during large batches. When a courier’s pricing service was temporarily rate-limiting us, we kept asking and kept being refused; we now pause those lookups briefly and let the daily cost sweep fill the figures in. Shipping itself was never affected.',
    ],
  },
  'v1.189.4': {
    date: '29 Aug 2026',
    features: [
      'New orders now appear within seconds of being placed, instead of waiting for the next 15-minute sync. Your sales channel notifies us the moment an order comes in. The regular sync still runs in the background as a safety net, so nothing can be missed if a notification is ever delayed or lost.',
    ],
    fixes: [
      'ModIQ and the dashboard load correctly again. Some orders had their timeline and delivery-attempt history saved in the wrong format after the recent system move, which stopped those panels from opening. The affected records have been repaired and their history restored.',
      'Order tab counts refresh correctly again on all accounts. A few accounts had a stale internal counter that could show out-of-date totals until the page was reloaded.',
    ],
  },
  'v1.189.1': {
    date: '28 Aug 2026',
    features: [
      'Store View on the Orders page now lets you pick more than one store at a time. Tick as many as you like — the tab counts, the summary tiles, the order list and the totals all update to cover exactly the stores you selected. Leave everything unticked for all stores, as before. Useful when you want two or three of your channels together without having to look at each one separately, or at everything at once.',
    ],
    fixes: [],
  },
  'v1.189.0': {
    date: '28 Aug 2026',
    features: [
      'The whole panel is dramatically faster. Order lists, tabs, counts and search that used to take two to three seconds now come back in well under a tenth of a second. Nothing about how you work has changed — the same pages, the same buttons, just without the wait. This is the result of moving the system onto our own servers, closer to you.',
    ],
    fixes: [
      'Order tracking is more accurate about what it has already told your sales channel. A number of shipments were not recording that their status had been sent, which could cause the same shipping update to be sent to a customer more than once. Those records have been corrected and the cause is fixed.',
      'Automatic daily jobs — next-day pickup carry-forward, purchase order settlement and shipping cost updates — are now guaranteed to run on the new system. Two of them could previously have been skipped after the move.',
    ],
  },
  'v1.188.4': {
    date: '28 Aug 2026',
    features: [],
    fixes: [
      'Planned maintenance is now explained properly instead of failing silently. During a short upgrade window the panel stays open and everything you can already see keeps loading — orders, customers, tracking, labels — but actions that change data (shipping, edits) are paused, and a banner tells you what is happening and that it is temporary. Previously an action attempted during that window simply errored with no explanation.',
    ],
  },
  'v1.188.3': {
    date: '28 Aug 2026',
    features: [],
    fixes: [
      'Order tracking no longer re-saves a shipment on every check when nothing about it has actually changed. Parcels in transit were being written back to the database every 15 minutes with no new information, which added avoidable load and churn. Tracking updates, delivery attempts and status changes all behave exactly as before — only the pointless repeat saves are gone.',
    ],
  },
  'v1.188.2': {
    date: '26 Aug 2026',
    features: [],
    fixes: [
      'Order search is faster and far lighter on the system: searching by an exact order number or AWB now looks it up directly instead of scanning the whole account, which was a major driver of the recent cost spikes. Partial and name searches work exactly as before.',
    ],
  },
  'v1.188.1': {
    date: '26 Aug 2026',
    features: [
      'Bulk Actions: a Bulk Ship entry now has a “Print Labels” button (row + summary). It generates one merged label PDF for that batch’s shipped orders and moves them from Ready to Print → Ready to Pickup — so you can print a specific bulk-ship batch’s labels straight from history.',
    ],
    fixes: [],
  },
  'v1.188.0': {
    date: '26 Aug 2026',
    features: [],
    fixes: [
      'Large accounts load faster and much cheaper to run: a new server saved the open-order context to shared storage, so a freshly started server reuses it (reading one small file + only what changed) instead of re-reading every open order and every customer from scratch. This removes the repeated heavy scans behind the Firestore cost spikes, with no change to what you see.',
    ],
  },
  'v1.187.7': {
    date: '25 Aug 2026',
    features: [],
    fixes: [
      'Completes v1.187.6. The re-read-only-what-changed improvement was not actually taking effect for changes made on the same server that then served the next page — the old data was being discarded before it could be reused, so those requests still re-read everything. They now take the fast path as intended.',
      'A change saved while the page was mid-load could also be treated as already included, which could leave a just-shipped order showing in Pending until the next update. It is now always picked up on the following request.',
    ],
  },
  'v1.187.6': {
    date: '25 Aug 2026',
    features: [],
    fixes: [
      'Large accounts load dramatically less data per action. Whenever anything changed — an order syncing in, a courier scan arriving, a single ship — the Orders page threw away everything it knew and re-read every open order on the account from scratch. On the biggest account that was 16,841 orders, plus a customer-profile lookup for every one of their phone numbers, and it happened on roughly a third of all requests.',
      'It now re-reads only the orders that actually changed since it last looked, and merges them in. Nothing is shown any less accurately: an order that has just shipped still leaves the Pending tab immediately, and tab badges remain exact. The full re-read still happens periodically as a safety net.',
      'This was the cause of the runaway database bill and the read-rate alerts firing each morning.',
    ],
  },
  'v1.187.4': {
    date: '24 Aug 2026',
    features: [],
    fixes: [
      'Dashboard summary figures and the performance panels are now reused for a few minutes instead of being recalculated on every page load. These are reporting numbers that nobody acts on order by order, so a slightly older figure is harmless — and each recalculation was reading every order on the account, which is the single biggest driver of database cost.',
      'Order tab badges are deliberately unchanged: they still show exact numbers every time you load or refresh a page, because staff act on them order by order. Only the silent 60-second background refresh is allowed to lag.',
    ],
  },
  'v1.187.2': {
    date: '22 Aug 2026',
    features: [],
    fixes: [
      'Orders page is significantly faster on large accounts. Loading the order list was reading every open order in full — including the full product list on each one — even though only 25 rows are shown and none of that product detail is needed to decide which tab an order belongs in. It now reads only what it needs and fetches the rest just for the rows on screen, cutting the heaviest part of the load by roughly 80%.',
      'The dashboard summary figures (total orders, revenue, shipped, delivered) are now saved and shared between servers instead of being recalculated from scratch on every page load. On the largest account that calculation was reading every order ever placed, and was the slowest part of opening the page.',
    ],
  },
  'v1.187.1': {
    date: '22 Aug 2026',
    features: [
      'Product names you set in the Product Master now print on shipping labels and customer invoices too. Rename a product once and it appears everywhere — the packing label your team reads and the invoice the customer receives — instead of the long storefront title Shopify sent. Products you have not renamed are unchanged and still show exactly what the customer ordered.',
    ],
    fixes: [],
  },
  'v1.187.0': {
    date: '22 Aug 2026',
    features: [
      'You can now rename products in the Product Master. Shopify titles are written for a storefront and are often far too long to be useful on a picking list or a vendor purchase order — edit the name to whatever suits your team. The product stays fully linked to the same Shopify product and variant: syncing, stock and order matching are unaffected, and your name is not overwritten the next time the catalog syncs. Leave the name blank to go back to following Shopify. Renamed products are marked so you can see at a glance which ones no longer track Shopify.',
    ],
    fixes: [
      'Fixed edited SKUs being silently reverted. The SKU field could always be edited, but the next catalog sync quietly overwrote it with the Shopify value — so the change looked saved and then disappeared. An edited SKU now stays put, and clearing it goes back to following Shopify.',
    ],
  },
  'v1.186.5': {
    date: '22 Aug 2026',
    features: [
      'Bulk Ship now finishes large batches on its own. A single run can only get through around 260 shipments before it hits its time limit, so a 655-order batch was reporting 276 shipped and 379 "failed" — even though 378 of those had simply never been attempted, and you were being asked to run Bulk Ship again yourself to finish your own batch. The remaining orders are now handed to a follow-up batch automatically, which continues until everything is shipped. The summary tells you how many are still going, and you can watch them in Bulk Actions.',
    ],
    fixes: [
      'Orders that ran out of batch time are no longer counted as failures. Only orders a courier actually rejected are shown under Failed, so the number you see is the number that genuinely needs attention.',
    ],
  },
  'v1.186.4': {
    date: '22 Aug 2026',
    features: [],
    fixes: [
      'Courier pickups are being requested again. On any location whose courier credentials were moved to the account level, the pickup request was never actually sent to Delhivery — the code was still looking for the key on the location itself, found nothing, and skipped the call without reporting anything. Four locations had been silently affected. Pickup requests now use the same credentials as shipping, and if one cannot be sent it is recorded as failed instead of looking like it was never needed.',
      'Orders page is much faster on large accounts. The tab counts and dashboard tiles were being recalculated separately by every server instance, and the saved results were being discarded dozens of times a minute by routine courier tracking updates. Results are now shared across servers and tracking updates clear them once per run instead of once per parcel. On the largest account the counts went from 19-45 seconds to under 2 seconds.',
      'Opening the orders list itself is still slower than it should be the very first time a server handles a large account (around 30 seconds, then about 2 seconds after). That last piece is planned separately — see the GitHub release notes.',
    ],
  },
  'v1.186.3': {
    date: '22 Aug 2026',
    features: [],
    fixes: [
      'Fixed the Orders page hanging for minutes on large accounts. Requests were taking four to five minutes and then failing outright. Two causes: the saved figures the page relies on were being thrown away after every store sync — even when that sync had changed nothing — so a large account rebuilt everything from scratch on almost every page load; and the server was allowing far too many requests to share one processor, so a single heavy page load stalled every other request on the same machine. Both fixed: the page now loads instead of timing out, and the worst case dropped from about five minutes to well under a minute.',
      'Note for very large accounts: the first load after new orders arrive can still take up to about a minute while the figures are rebuilt. Further work on that is planned — see the release notes on GitHub for detail.',
    ],
  },
  'v1.186.2': {
    date: '22 Aug 2026',
    features: [
      'Bulk Ship now lets you choose the pickup location. It was fixed to whichever location the stock was first assigned from, shown as plain text with no way to change it — so if you added stock at a second warehouse, there was no way to bulk ship from there even though single-order shipping allowed it. It is now a dropdown, and any location that cannot cover every selected order is greyed out with "No stock", exactly like single ship.',
    ],
    fixes: [
      'Fixed the order tab counts failing when a specific store was selected at the top. The counts would not load at all for a store-filtered view — the numbers came back empty while the unfiltered view worked fine. This had been happening intermittently since 20 August; it only occurred when the figures had to be recalculated rather than served from cache, which is why it came and went.',
      'Fixed misleading reasons on failed shipments. When an order is tried across several couriers by priority, the failure reason shown was picked from whichever courier happened to match an internal rule first — so an order whose main courier hit a temporary outage could be reported as "No serviceable carrier", which reads as "this address cannot be delivered to". Staff then left those orders alone instead of retrying them. Fixable and temporary problems (wallet balance, login, courier timeouts) are now reported ahead of "not serviceable", and "not serviceable" only appears when that is genuinely the best explanation.',
      'Failed shipments now record which couriers were actually tried and what each one said, and the Bulk Ship summary names them. Previously there was no way to tell whether one courier had been tried or all three.',
      'Fixed a case where a courier with an unreadable saved credential would stop an order\'s courier fallback early, so the remaining lower-priority couriers were never attempted. Each courier is now isolated — one bad courier can no longer cost an order its other options.',
    ],
  },
  'v1.186.1': {
    date: '21 Aug 2026',
    features: [],
    fixes: [
      'Fixed orders arriving late or not appearing at all. The 15-minute Shopify sync was asking Shopify for "orders changed since last time", but the way the date was written meant Shopify quietly ignored that part and handed back the store\'s entire order history instead — every 15 minutes, for every store. A check that should have returned a handful of orders was returning nearly two thousand. That is now fixed: the same check returns only what actually changed, and a full sync of every store finishes in seconds instead of never finishing at all.',
      'The sync job was also being cut off part-way through the store list on every single run because it had not been given enough time or memory. Stores near the end of the list could go hours — in one case three days — without being pulled at all, which is why some orders never showed up. It now has the room it needs and works through several stores at once, so no store waits behind another.',
      'A store with a large backlog now resumes where it left off instead of starting over. Previously a store that could not finish in one run made no progress at all, so it fell further behind on every attempt and never recovered on its own.',
      'Orders can no longer be skipped permanently. If a single order failed to import, it was quietly left behind and never retried — it simply never appeared. Failed orders are now remembered and re-fetched on the next run until they come through.',
      'Fixed a gap that could lose orders placed or edited while a sync was running. The "synced up to" marker is now taken from the start of the run with a small overlap, instead of the end, so nothing can fall between two runs.',
      'Added a safety net: a few times a day the sync re-checks a wider window regardless of its progress marker, so if anything is ever missed for any reason it now corrects itself within hours instead of staying missing.',
      'Orders and products are no longer rewritten when nothing about them has changed, which makes each sync considerably faster and cuts database cost.',
    ],
  },
  'v1.186.0': {
    date: '21 Aug 2026',
    features: [
      'Shiprocket labels now name the real carrier. Shiprocket is an aggregator — it picks Bluedart, Delhivery, DTDC, Amazon or Ekart for each parcel — but every label, manifest and order row just said "Shiprocket". They now read the actual carrier with "via Shiprocket" underneath, exactly like Velocity: "Bluedart / via Shiprocket". The pickup manifest also sorts your printed labels by carrier so a handover can be split correctly.',
      'Amazon parcels booked through Shiprocket now print Amazon\'s own label. Amazon Shipping does not accept a self-branded label. When Shiprocket routes a parcel to Amazon we now save Amazon\'s label at booking and print that instead, for both single and bulk print — the same rule that already applied to Velocity.',
    ],
    fixes: [
      'The Compact, Grid and Modern label designs were being cut off at the bottom when printed. On an ordinary four-product order with SKU and return address switched on, roughly a quarter of the label was lost — the order barcode, return address, care line and footer simply did not print. Labels now shrink to fit the page instead of losing whatever runs past the edge, so everything on the label is always printed. This applies to the Default design too, on long orders.',
      'Fixed the Modern design printing with no frame and no divider lines on thermal printers. Its lines were too pale to survive a thermal print head, and are now dark enough to come out properly.',
      'Fixed the product table on the Grid and Modern designs running into the label edge, which shaved the last digits off the Total column, and fixed SKUs wrapping onto three lines in a column too narrow to hold them.',
      'Courier performance now reports Shiprocket carriers as one line each. Shiprocket names its services with the weight slab included, so the same carrier was being split into several unrelated rows.',
    ],
  },
  'v1.185.13': {
    date: '21 Aug 2026',
    features: [],
    fixes: [
      'Faster Dashboard on large accounts. Opening the Dashboard was recalculating every tab count from scratch — on an account with thousands of open orders that alone made the page wait several seconds. The summary tiles now accept figures that may be a few seconds old, which is invisible on a dashboard. Order tab badges are unchanged and still exact, so they update immediately after you ship or print.',
    ],
  },
  'v1.185.11': {
    date: '21 Aug 2026',
    features: [],
    fixes: [
      'The left menu now only shows what a team member actually has access to. Anything their role does not include is hidden rather than shown-and-blocked, so there is nothing confusing to click. Section headings disappear too when everything under them is hidden. Owners see the full menu exactly as before.',
      'Opening a page they do not have access to — from a bookmark, a shared link or a typed address — now takes them to the Dashboard instead of showing a page where nothing loads.',
    ],
  },
  'v1.185.6': {
    date: '20 Aug 2026',
    features: [
      'Team & Access — add your staff to your own account. Settings → Team & Access lets you invite someone by name and email; they get an email with a link to set their own password, and sign in with their own login. No extra charge, and everything still bills to your account as usual.',
      'Choose what each person can do, without fiddling with dozens of switches. Pick a role: Shipping Staff (process and ship orders, pickups and returns), Purchase Staff (products, vendors and purchase orders), Finance (payments, credit notes, shipping costs, reconciliation), Manager (everything except billing and managing the team), or Viewer (look, don\'t touch).',
      'Hide sales data from anyone you choose. A per-person switch that keeps order values, revenue and analytics out of sight — useful when someone only needs to pack and ship.',
      'You stay in control: only the account owner can invite people or change what they can do. You can disable someone instantly, resend or cancel an invitation, and remove a login — removing a person keeps all the orders they processed and their activity history intact.',
    ],
    fixes: [],
  },
  'v1.185.5': {
    date: '20 Aug 2026',
    features: [],
    fixes: [
      'Fixed Bulk Print failing with "Navigation timeout of 60000 ms exceeded" on large batches. Every label carried a small ModCommerce mark loaded over the internet, and the label renderer waits for all images before producing the PDF — so one slow response could stall a batch until it timed out and failed the whole job (a 320-label print died because 2 of its 8 sections stalled, discarding the 6 that had already rendered fine). The mark is now drawn directly into the label, so label rendering no longer touches the network at all. Any section that does stall is retried instead of failing the entire print.',
      'Bulk print jobs no longer compete with each other for processing power — each large print now gets its own dedicated capacity, so running two big batches close together can no longer slow both to a crawl.',
      'Fixed the panel appearing to freeze or "crash" after large Bulk Print / Bulk Ship runs, where the dashboard would stop loading until you signed in again or opened an incognito window. A progress check that failed could keep retrying forever, quietly using up the session\'s request allowance until nothing else could load. It now stops after a reasonable number of attempts, and stops entirely when you leave the Orders page — the job itself keeps running on the server either way and is always recoverable from Bulk Actions.',
      'Delhivery tracking: courier errors are now recorded with the courier\'s own explanation instead of a bare status code, and tracking looks up the same Delhivery account the parcel was actually shipped on (warehouses pinned to a second Delhivery account were being tracked against the wrong one).',
    ],
  },
  'v1.185.1': {
    date: '20 Aug 2026',
    features: [],
    fixes: [
      'Fixed "Too many requests" errors for teams working from one office. Rate limits were counted per internet connection, so several people using the panel at the same time shared one budget and could lock each other out. Each signed-in session now gets its own allowance.',
    ],
  },
  'v1.185.0': {
    date: '20 Aug 2026',
    features: [],
    fixes: [
      'Fixed a serious Bulk Print bug: printing more than ~500 orders at once generated the PDF correctly but silently failed to move ANY of them to Ready to Pickup — they stayed in Ready to Print with their labels already printed, with no error shown. (A database write limit was being exceeded and the failure was only logged, never surfaced.) Large prints now write in safe chunks, and anything that still fails is reported on screen instead of hidden.',
      'Fixed pickup batches occasionally not being created (or created only partly) after a large Bulk Print. Pickup creation was running detached in the background where it could be cut off mid-way — it now completes before the job is reported done.',
      'Bulk Ship no longer loses track of what it shipped. On very large batches that ran past the time limit, the record of which orders succeeded could be lost entirely even though the shipments were real. Results are now saved continuously as it works, and any orders it did not get to are listed explicitly so you know exactly what to re-run.',
      'Much faster dashboard on busy accounts. The analytics panel cache was being wiped by every single order update, so on high-volume accounts it rebuilt from scratch on nearly every load during the busiest hours — exactly when it was slowest. It now serves for up to a minute during heavy activity.',
      'Safer Shopify retries: automatic retries no longer apply to actions that change data (creating fulfillments, updating or cancelling orders) when the outcome is unknown, which could previously have double-fulfilled an order. Rate-limit waits now follow Shopify\'s own timing.',
      'Bulk Print/Ship result popup no longer briefly appears stacked behind the progress popup, and reports skipped/failed counts more accurately.',
    ],
  },
  'v1.184.8': {
    date: '20 Aug 2026',
    features: [],
    fixes: [
      'Restored the original Bulk Print / Bulk Ship popup — a live progress bar (now genuinely fed by the server-side job as it works, not just a toast) and a detailed result screen at the end (labels rendered / shipments made, courier & location breakdown, and exactly which orders failed and why), matching how it always looked before. The Bulk Actions log page is still there separately for revisiting or re-downloading a past run later — this is only about restoring the in-context experience while an action is actually running.',
    ],
  },
  'v1.184.7': {
    date: '20 Aug 2026',
    features: [],
    fixes: [
      'Bulk Print no longer silently under-delivers. If some orders in a batch don\'t have an AWB yet (not booked/shipped), it now honestly reports how many labels it actually produced, opens the PDF for the ones that succeeded, and lists exactly which orders were skipped and why in the Bulk Actions log — before, it always said "Labels ready" and opened a PDF with fewer pages than requested, with no indication anything was missing.',
      'Fixed an ordering bug where a batch could be marked "printed" (and pickups scheduled) before its label PDF had actually finished rendering — if the render step then failed, those orders were left showing as printed with no real label ever produced, undetectable until the courier pickup. Orders are now only marked printed, and only pickups scheduled, after their label is confirmed generated.',
    ],
  },
  'v1.184.6': {
    date: '20 Aug 2026',
    features: [],
    fixes: [
      'Bulk Print for very large batches (400+ orders) is now fast and reliable — a 482-order batch that previously failed after 8+ minutes now completes in under a minute. The chunked rendering from the previous fix was accidentally launching a separate browser process per chunk, which fought over the same limited resources and undid the speed-up; it now shares one process across all chunks.',
    ],
  },
  'v1.184.5': {
    date: '20 Aug 2026',
    features: [],
    fixes: [
      'Fixed Bulk Print failing outright on very large batches (400+ orders) — it wasn\'t just slow, it was timing out and never completing. The PDF was being built as one giant single document (every label + barcode in one page-render), which stopped scaling well past a few hundred orders. It\'s now rendered in smaller chunks in parallel and merged into one file, so batches of any size finish reliably and much faster.',
    ],
  },
  'v1.184.4': {
    date: '19 Aug 2026',
    features: [],
    fixes: [
      'Faster dashboard for high-volume accounts. The performance analytics panel (courier/product performance, RTO by state, top states, channel performance) re-scanned every order in the account\'s entire history on every dashboard load and every "Sync All Stores" click, with no caching. It\'s now cached the same way the rest of the dashboard already is — repeat loads are near-instant, and a real change (new order, ship, etc.) still refreshes it immediately.',
    ],
  },
  'v1.184.3': {
    date: '19 Aug 2026',
    features: [],
    fixes: [
      'Fixed Bulk Print failing on large batches (400+ orders) with "Navigation timeout of 30000 ms exceeded" — an internal 30-second limit in the PDF renderer, unrelated to and far shorter than the actual time the server allows for the job. Large batches now get the full time they need.',
    ],
  },
  'v1.184.2': {
    date: '19 Aug 2026',
    features: [],
    fixes: [
      'Fixed Bulk Print jobs failing with "the specified bucket does not exist" — Cloud Storage had never actually been provisioned for this project. A dedicated bucket + permissions are now set up.',
      'Fixed the bulk print/ship notification getting stuck open showing a raw technical error instead of a short message and auto-dismissing.',
    ],
  },
  'v1.184.1': {
    date: '19 Aug 2026',
    features: [],
    fixes: [
      'Fixed tracking sync silently stalling for every courier for a full 15-minute cycle whenever Delhivery hit a transient error — orders could sit in Ready to Pickup without moving until a manual "Sync Tracking" click. Delhivery tracking now fails on its own without blocking the other couriers, same as they already do for each other.',
      'Fixed Shopify order imports occasionally appearing 1-2 sync cycles (~30-40 min) late. A single order hitting a transient error used to abort the rest of that sync run silently; now it\'s logged and skipped so the rest of the batch still imports on time, and Shopify API rate-limit responses are retried automatically instead of failing the whole run.',
      'Fixed a case where Shopify webhooks could fail verification for a store that has a duplicate account record from an earlier connection attempt — verification now checks every matching record for a valid signature instead of only the first one Firestore happens to return.',
    ],
  },
  'v1.184.0': {
    date: '19 Aug 2026',
    features: [
      'New: Bulk Actions page (below Pickups). Bulk Print and Bulk Ship now always run as a server-side job instead of in your browser — click Print or Ship and it starts immediately in the background, safe to close the tab or keep working. A small batch finishes in a couple seconds and opens automatically like before; a larger one shows up in Bulk Actions when it\'s ready. Every run is logged there with its status (Processing / Success / Completed with Errors), a re-openable summary, and a Download Labels button for print jobs. Entries clear automatically after 7 days.',
      'Bulk label generation is also noticeably faster now — orders, stores, and locations are fetched and rendered in parallel instead of one at a time.',
    ],
    fixes: [],
  },
  'v1.183.0': {
    date: '19 Aug 2026',
    features: [],
    fixes: [
      '🔒 Security: label/invoice/manifest/PDF download links no longer carry your full session token in the URL. They now use a separate, restricted token that can only open that one type of download — even if a download link were ever exposed (browser history, a shared screenshot, a proxy log), it could not be used to access anything else in your account. If a page was already open before this update, refresh it once for downloads to keep working.',
    ],
  },
  'v1.182.3': {
    date: '18 Aug 2026',
    features: [],
    fixes: [
      'Fixed the browser crashing / going blank when bulk-printing large batches (~89+ orders). Labels now render as a real PDF on the server (existing crisp-barcode technology, previously only used for single-order downloads) instead of a live page the browser had to auto-print — no more browser-side strain regardless of batch size.',
    ],
  },
  'v1.182.2': {
    date: '17 Aug 2026',
    features: [],
    fixes: [
      'Fixed a caching gap that could leave a just-failed shipment (e.g. courier error) invisible in Need Attention for a couple of minutes. The order list cache now refreshes after every action, not only successful ones — a failed ship attempt still writes real data (the error reason) even though the request itself returns an error.',
    ],
  },
  'v1.182.1': {
    date: '17 Aug 2026',
    features: [],
    fixes: [
      'Fixed the new manifest-date filter (Ready to Print / Ready to Pickup) showing far fewer orders than it matched — e.g. "9 of 51 shown." A separate client-side date re-filter was still comparing against order-placed date instead of manifest date on those two tabs, silently excluding correctly-matched orders that were placed on an earlier day but shipped today.',
    ],
  },
  'v1.182.0': {
    date: '17 Aug 2026',
    features: [
      'Ready to Print / Ready to Pickup: the Today/Yesterday/Custom date filter now filters by manifest date (when the order was pushed to the courier) instead of order date on these two tabs, so a mixed batch can be narrowed down to exactly one day\'s shipments before bulk-printing or picking up.',
    ],
    fixes: [],
  },
  'v1.181.1': {
    date: '17 Aug 2026',
    features: [],
    fixes: [
      'Fixed bulk label print opening a blank tab (regression from the v1.181.0 URI-too-long fix, only showed up in some browsers). Label printing now uses a real form submission instead of a background fetch, which is a more reliable way to open a same-tab result for a large batch.',
    ],
  },
  'v1.181.0': {
    date: '17 Aug 2026',
    features: [],
    fixes: [
      'Fixed "Error: URI Too Long" when bulk-printing labels for 200+ orders at once. The order IDs now travel in the request body instead of the URL, so there\'s no batch-size limit on printing — a side effect of raising the page-size cap to 1000 that only showed up at real scale.',
    ],
  },
  'v1.180.1': {
    date: '17 Aug 2026',
    features: [],
    fixes: [
      'Fixed: QA/QC custom keyword rules silently dropped anything past the 20th rule on save, with no warning — accounts that added more than 20 lost the rest without knowing. The limit is now 200, and going over it rejects the save with a clear error instead of quietly discarding rules.',
    ],
  },
  'v1.180.0': {
    date: '17 Aug 2026',
    features: [
      'New: Settings → Order QA/QC Validation. Turn any Need Attention check on/off, edit the COD value cap, the high-first-COD multiplier, and the COD velocity threshold — and build your own custom keyword rules (name / address / email / order note, with contains / starts-with / exact matching) that route matching orders to Need Attention with your own reason.',
    ],
    fixes: [],
  },
  'v1.179.0': {
    date: '17 Aug 2026',
    features: [
      'Ready to Ship / Ready to Print: page size options now go up to 500 and 1000 (was capped at 200), so a larger batch can be selected and bulk-shipped or bulk-printed in one go.',
    ],
    fixes: [],
  },
  'v1.178.4': {
    date: '14 Aug 2026',
    features: [],
    fixes: [
      'Per-user Rate Card overrides now actually apply to charges. When an admin sets a custom rate for a user (e.g. 2 credits/order instead of 5), shipments and add-ons now deduct at that custom rate — previously the rate card showed the override but billing still used the global rate.',
      'Courier Error now shows the real reason (e.g. “No serviceable carrier”, “Wallet balance low”) instead of a generic “Courier error”.',
    ],
  },
  'v1.178.3': {
    date: '14 Aug 2026',
    features: [],
    fixes: [
      'Pickup manifest print/PDF: the Manifest ID no longer overlaps the courier name in the top-right corner. A top band is reserved for the running header on every printed page.',
    ],
  },
  'v1.178.2': {
    date: '14 Aug 2026',
    features: [],
    fixes: [
      'Edited-order total now matches Shopify. After a Shopify order edit, the order amount showed the old and new totals summed (e.g. ₹5,190 instead of ₹2,595). It now uses Shopify’s current total, so the amount — and the COD value couriers collect — is correct. Existing affected orders were repaired.',
    ],
  },
  'v1.178.1': {
    date: '14 Aug 2026',
    features: [],
    fixes: [
      'Shopify order edits now reflect correctly. When a customer or you edit an order in Shopify — removing, reducing, or adding items — the order in ModCommerce now mirrors the current items instead of showing the old and new lines together. Edits sync automatically (and update within seconds via webhook); existing affected orders were repaired.',
    ],
  },
  'v1.178.0': {
    date: '13 Aug 2026',
    features: [
      'Prepaid-RTO reship tracking now flows back to the original order. When you reship an order that came back (AB1234 → AB1234-RT) and the reship is delivered — via ANY courier — the original order shows a “↻ Reshipped · Delivered” badge in the list and a full “Reship history” trail in its popup (Base → Reship 1 → …). The latest parcel’s status is also pushed onto the original order in your sales channel. The original keeps its own RTO/Returned status — nothing is un-returned; the reship info sits on top. Reship again and the newest leg always wins. Existing reshipped orders were backfilled.',
    ],
    fixes: [],
  },
  'v1.177.0': {
    date: '13 Aug 2026',
    features: [
      'New Rate Card on the Billing page — a read-only grid showing every way ModCommerce bills you (shipping, Manual Ship, Auto PO, RTO/Return Reconcile, Customer Intelligence) with each rate, its unit, whether the service is active for you, and a “Custom” badge if we’ve set a special rate on your account.',
    ],
    fixes: [],
  },
  'v1.176.1': {
    date: '13 Aug 2026',
    features: [
      'Orders list: new State column (delivery state, between Customer and Amount) and a State filter in the Filter panel — filter orders by destination state.',
    ],
    fixes: [],
  },
  'v1.176.0': {
    date: '08 Aug 2026',
    features: [
      'Customer Returns & Exchanges now go into batches like courier RTO. When you receive one (scan or Receive), it joins that location’s batch instead of restocking instantly. You then Confirm the batch with Restock (into the receiving location) or Buyback (draft credit note) — the same flow as RTO — and it records who reconciled it, with a batch number and receipt. Refunds stay independent (record anytime); exchange replacements are still created immediately at receive. Reconciling returns is free (only courier-RTO orders are billed). Accounts without the RTO Reconcile add-on keep the old instant-restock behaviour.',
    ],
    fixes: [],
  },
  'v1.175.6': {
    date: '08 Aug 2026',
    features: [],
    fixes: [
      'Scanning customer returns/exchanges: after Submit it now correctly says how many returns were received, instead of “0 orders closed”. Returns are received the moment you scan them and go to the Closed Returns tab (not an RTO batch) — courier RTOs are what get batched. Nothing was lost; the message was just misleading.',
    ],
  },
  'v1.175.5': {
    date: '08 Aug 2026',
    features: [],
    fixes: [
      'Fixed “Scan failed” when receiving a return/exchange whose item came from an exchange/replacement — the product reference wasn’t in the expected format and broke the restock step. Receiving (scan or button) now restocks correctly.',
    ],
  },
  'v1.175.4': {
    date: '08 Aug 2026',
    features: [],
    fixes: [
      'Return/Exchange scanning is now more forgiving: the scanner matches a return by its reverse AWB, its request number (…-CR), OR the original order number — so you can scan whichever barcode is on the parcel/label. Scanning a delivered order that has a return now receives that return instead of erroring “not a returning shipment”.',
    ],
  },
  'v1.175.3': {
    date: '08 Aug 2026',
    features: [],
    fixes: [
      'Receiving a return that was already refunded (e.g. refunded early, before the parcel came back) now correctly marks it Completed instead of moving it back to “refund pending” — so it never asks you to refund a second time. The Receive confirmation also says so.',
    ],
  },
  'v1.175.2': {
    date: '08 Aug 2026',
    features: [],
    fixes: [
      'Return reverse-tracking hardened for all couriers: besides the Delhivery fix, Velocity / Ekart / Shiprocket now fall back to a courier-agnostic “delivered / closed / returned” check, so a return can’t get stuck showing “in transit” just because a courier reports a reverse-specific status code. (Shiprocket already handled this; Velocity/Ekart are now covered too.)',
    ],
  },
  'v1.175.1': {
    date: '08 Aug 2026',
    features: [],
    fixes: [
      'Return/Exchange reverse tracking now correctly detects when a Delhivery return parcel has reached your warehouse. Delhivery’s reverse shipments report their own status codes (DTO / “Closed Reverse Pickup”) that weren’t being recognised, so returns got stuck showing “in transit” and the Received step never lit up even after delivery. Fixed the mapping — the Received status now updates automatically (and existing stuck returns were corrected).',
    ],
  },
  'v1.175.0': {
    date: '08 Aug 2026',
    features: [
      'RTO / Return Reconcile — new filters: by Type (RTO / Customer Return / Customer Exchange) and a finer State filter (In transit back / To receive on the open tab; Received / Reconciled on the closed tab).',
      'Customer Return/Exchange parcels can now be received directly from the reconcile list (a Receive button) — no scan needed as a fallback.',
    ],
    fixes: [
      'BIG FIX: customer returns no longer disappear from RTO / Return Reconcile once the reverse pickup starts moving. Previously a return dropped off the list the moment the courier picked it up (only “pickup booked” was shown), so parcels in transit or already at your warehouse were invisible and couldn’t be received/reconciled. Now the whole in-flight journey (picked up → in transit → at warehouse) stays on the screen.',
    ],
  },
  'v1.174.1': {
    date: '08 Aug 2026',
    features: [],
    fixes: [
      'Courier Analysis moved into the ModIQ tab (out of the normal dashboard).',
      'Courier Analysis now reports Delivered% and RTO% over RESOLVED shipments (delivered + RTO + lost) instead of all dispatched — so in-transit orders no longer dilute the true RTO rate. Added a Resolved column (the denominator) and an In-Transit count. Results are cached briefly for speed.',
    ],
  },
  'v1.174.0': {
    date: '08 Aug 2026',
    features: [
      'Dashboard → new Courier Analysis section (under Courier Performance). Pick a window (7 / 15 / 30 / 90 days by ship date) and optionally a channel, and see each courier’s Shipped / Delivered / RTO / NDR counts, Delivered %, RTO %, average delivery days (dispatch → delivered), and a COD-vs-Prepaid split (delivered% / rto% for each). Aggregators (Velocity, Shiprocket) and manual couriers expand to show per-sub-carrier performance, so you can see who delivers best.',
    ],
    fixes: [],
  },
  'v1.173.1': {
    date: '08 Aug 2026',
    features: [],
    fixes: [
      'Purchase Manager loads much faster again — the planning + analysis queries no longer pull the heavy raw order data.',
      'Purchase Planning cards tidied: the urgency tag no longer overlaps the checkbox, the stat boxes sit in a clean grid, and the colours are clearer.',
    ],
  },
  'v1.173.0': {
    date: '08 Aug 2026',
    features: [
      'Purchase Planning — new filters: by Channel, Stock status (out / low / healthy / overstock), Vendor, “Needs ordering”, Auto-PO, plus sorting (Urgency, To-order, Best-seller, Days of cover, A–Z).',
      'Every product card now shows Reserved (stock already promised to unshipped orders) and Available (free-to-sell = In stock − Reserved), a sales rate (units/day, 30d), days of cover, and a colour reorder badge (order now / soon / healthy).',
      'Click any product for a detail popup: full stock breakdown, a 30-day daily sales graph with 7/15/30-day totals + average per day, and a restock forecast — “sells X/day, runs out in N days, restock takes ~L days → order about M units by <date>”. Lead time is learned from your own purchase-order history (with a safe default), plus a one-click Create Purchase Order.',
    ],
    fixes: [],
  },
  'v1.172.0': {
    date: '08 Aug 2026',
    features: [
      'Return / Exchange detail popup revamped. Record Refund and Notes now live inside the popup (the row is cleaner). Record a refund right there with a highlighted amount + a clear Refunded / Refunded early / Pending status chip. Notes are now a proper thread — each note is stamped with who added it and when, you can add as many as you like, and delete your own. Product images now show for the returned (and replacement) items, like the orders page.',
    ],
    fixes: [
      'Return / Exchange list: tidied row alignment (Journey + Action columns).',
    ],
  },
  'v1.171.0': {
    date: '08 Aug 2026',
    features: [
      'Labels: product variant is now always shown. Even when a long product name is shortened with “…”, the variant (size/colour, e.g. “Navy Blue, L”) prints on its own line right under it — on every template.',
      'New “Multi-page for long orders” option (Settings → Label Configuration). When on, an order with too many products to fit one label prints the rest on a 2nd page — with Total, order barcode and return address intact and the ModCommerce footer on every page — instead of cutting off the bottom. Off by default; applies to the Grid & Modern templates. Toggle it and the live preview shows the 2-page result.',
    ],
    fixes: [],
  },
  'v1.170.1': {
    date: '08 Aug 2026',
    features: [],
    fixes: [
      'Customer exchange replacement orders now resolve their product correctly for stock + Auto-PO. An exchange whose product has Auto-PO enabled (or has stock) now moves to Ready to Ship instead of getting stuck in No Stock. Also ran a one-time cleanup so existing split orders with a part fulfilled outside ModCommerce now show the right status.',
    ],
  },
  'v1.170.0': {
    date: '08 Aug 2026',
    features: [],
    fixes: [
      'Split orders now track fulfillment per part. If you split an order and one part is fulfilled outside ModCommerce (self-fulfilled on your sales channel), that specific part now correctly moves out of Ready to Ship into Self Fulfilled — the other parts stay put. Previously every part inherited the whole order’s “partially fulfilled” status and stayed stuck in Ready to Ship. Applies automatically as each order next syncs.',
    ],
  },
  'v1.169.1': {
    date: '08 Aug 2026',
    features: [],
    fixes: [
      'Delivered tab COD filter — actually fixed now. The list also had a second (client-side) filter still using the old rule, which kept hiding delivered COD orders even after the server side was corrected. Both layers now use the same COD signal, so filtering Delivered by COD lists them correctly.',
    ],
  },
  'v1.169.0': {
    date: '08 Aug 2026',
    features: [
      'Prepaid RTO → new Dismiss button. If you handled a returned prepaid order some other way, Dismiss removes it from Need Attention without touching Shopify (no cancel, no refund). It still reconciles in your RTO batch as normal.',
    ],
    fixes: [
      'Delivered tab: filtering by COD now correctly lists delivered COD orders. Previously a delivered COD order (shown as “COD (Paid)”) was mistaken for a prepaid order by the filter and hidden.',
      'Exchange replacement orders no longer land in Need Attention → No Stock when stock actually exists (or Auto-PO is on). The replacement’s items weren’t matching your product/stock records; they now resolve correctly, so an exchange with stock goes straight to Ready to Ship.',
      'No Stock queue: removed the Fix / Confirm buttons that could force a no-stock order to Ready to Ship. No-stock orders now move to Ready to Ship automatically the moment stock (or an Auto-PO) is available — nothing to click.',
    ],
  },
  'v1.168.0': {
    date: '08 Aug 2026',
    features: [
      'New: Change your password from Profile Settings. Enter your current password + a new one (with confirm), then confirm the change with a 6-digit code emailed to you — so a password change can’t happen without access to your inbox. The code expires in 10 minutes; Resend if needed.',
    ],
    fixes: [],
  },
  'v1.167.3': {
    date: '08 Aug 2026',
    features: [],
    fixes: [
      'Custom Label Builder: the ready-made starter layout is tidied so a new template opens clean — the order barcode/number and return address no longer sit under the ModCommerce footer.',
    ],
  },
  'v1.167.2': {
    date: '08 Aug 2026',
    features: [],
    fixes: [
      'Custom Label Builder layout: the live preview now has its own wide column on the right (much bigger, and it stays in view while you scroll), and the Layers list is taller with a clear scroll area so you can see and pick every element.',
    ],
  },
  'v1.167.1': {
    date: '08 Aug 2026',
    features: [],
    fixes: [
      'Custom Label Builder is easier to use: bigger editing canvas, a Layers list to select any element (even when they overlap) plus bring-forward / send-back, a slimmer properties panel, and a drag threshold so a single click just selects an element instead of nudging it. The builder’s live preview now shows the whole label (it was being cropped).',
    ],
  },
  'v1.167.0': {
    date: '08 Aug 2026',
    features: [
      'New: Custom Label Builder — design your own shipping label with drag-and-drop (Settings → Label Configuration → Label Setting → “Build a Custom Template”). Add elements (store logo, courier, AWB number + barcode, order number + barcode, QR, customer address, payment, product table, GST, return address, free text, dividers), drag to position, and resize by the corner. A live preview shows exactly what will print. Required elements (logo, courier, AWB, order, customer) are locked so a label can’t be shipped incomplete. Save your design and it appears in the template picker under a “Custom” tag — pick it and it prints for your orders, filled with each order’s real data. Edit or delete your designs anytime. Up to 20 templates.',
    ],
    fixes: [],
  },
  'v1.166.3': {
    date: '08 Aug 2026',
    features: [],
    fixes: [
      'Grid & Modern templates: removed the remaining blank area below the product list. The order barcode now sits large and centred in that space (like a real courier waybill), with the return address pinned at the bottom — so the whole label is filled no matter how few items an order has.',
    ],
  },
  'v1.166.2': {
    date: '08 Aug 2026',
    features: [],
    fixes: [
      'Labels now print with a ~2mm bleed/safe margin on all four sides — the framed label sits just inside the page edge, so if your cutter or printer drifts slightly it trims the blank border, never the actual data (barcode, address, totals). Applies to every template and both print formats (Thermal 4×6 and A4). Tip: use Download PDF for an exact cut.',
    ],
  },
  'v1.166.1': {
    date: '08 Aug 2026',
    features: [],
    fixes: [
      'Grid & Modern label templates now fill the whole label — no more blank gap under the product list. The product table is ruled all the way to the bottom (like a real courier waybill), a Total row (item count + amount) was added, and the text size across the address/product blocks was enlarged for a fuller, cleaner label.',
    ],
  },
  'v1.166.0': {
    date: '08 Aug 2026',
    features: [
      'New: Label Template picker (Settings → Label Configuration → Label Setting tab). Choose the design your labels print in — Default (the classic), Compact (denser, more room for long addresses), Grid (fully-framed, edge-to-edge, no blank space), or Modern (dark header band with accent lines). Each shows a live thumbnail, and a big Live Preview updates as you switch templates and toggle content on/off. Your existing labels keep the Default design until you change it.',
    ],
    fixes: [],
  },
  'v1.165.2': {
    date: '05 Aug 2026',
    features: [],
    fixes: [
      'Shipping label now fits firmly on ONE page: the label height was reduced to leave a safety margin (so your printer’s built-in margins can’t push the footer onto a second page), the sections were tightened, and the whole label is clipped to the page. Tip: for browser Print, set Margins = None (or use Download PDF, which is exact) for a perfect single-page label.',
    ],
  },
  'v1.165.1': {
    date: '05 Aug 2026',
    features: [],
    fixes: [
      'Shipping label print fixes: the footer + return-address band no longer spill onto a second page — the whole label is now forced onto one 4×6 page. Added a solid 4-side outline around the label (cut guide). And the brand logo + courier name in the header are smaller, freeing up space so everything fits cleanly.',
    ],
  },
  'v1.165.0': {
    date: '05 Aug 2026',
    features: [
      'New Settings section (left menu) → Label Configuration: choose exactly what prints on your shipping labels — Product Name, SKU, Seller Address, Customer Phone, Product Price, GST No, Customer Care Email/Number, Show Seller Name, Return Address — and the print format: Thermal 4×6 (default) or A4 with four labels per sheet (cut lines included). COD/Prepaid is auto per order. Not applicable to Amazon native labels.',
      'Customer-care email + phone can now be set per Sales Channel (Sales Channels → Logo & size), and appear on the label when you enable those toggles — picked dynamically per order’s channel.',
    ],
    fixes: [
      'Shipping label print quality: section separators were near-white and faded/vanished on thermal & laser prints — they’re now solid dark lines so Ship-to / Seller / Product blocks stay clearly divided after printing. The AWB and order barcodes are also taller for more reliable scanning (same label size).',
    ],
  },
  'v1.164.2': {
    date: '05 Aug 2026',
    features: [],
    fixes: [
      'Blue Dart COD fee now shows in Shipping Cost (was ₹0). Blue Dart’s API doesn’t return a COD-fee breakup — it gives one all-in freight figure and nets the COD fee from the remittance later — so we now record an estimate (Blue Dart standard: 3% of the COD amount or ₹60, whichever is higher) on COD shipments, reconciled to the real amount when the Blue Dart bill lands. Prepaid shipments stay ₹0.',
    ],
  },
  'v1.164.1': {
    date: '05 Aug 2026',
    features: [],
    fixes: [
      'Blue Dart Service selection is now per warehouse and deterministic: when you add Blue Dart to a Location you pick the Service (Bharat Dart / Dart Apex / Domestic Priority / Surface), and every shipment from that warehouse uses exactly that product — no more mixing (e.g. some parcels going Bharat Dart and others Apex when a location has more than one Blue Dart account). The location’s choice always wins; falls back to the account default if left blank.',
    ],
  },
  'v1.164.0': {
    date: '05 Aug 2026',
    features: [
      'Blue Dart — pick your Service at connect: Bharat Dart (Economy, cheapest ≈₹62/500g), Dart Apex (Air), Domestic Priority, or Surface/Express. We push the exact Blue Dart product + pack codes for the service you choose, and COD vs Prepaid is selected automatically per order. If your account isn’t enabled for a service, Blue Dart tells you at ship so you can pick another. This fixes accounts that were quietly billed on a pricier slab — Bharat Dart is dramatically cheaper for light ecom parcels.',
    ],
    fixes: [],
  },
  'v1.163.2': {
    date: '05 Aug 2026',
    features: [],
    fixes: [
      'Blue Dart connect — fixed the Service / Product list: the old “Dart Plus / Surfaceline (Surface)” option actually sent product code E, a premium air slab that costs ~2× and fails on many lanes — picking it for “cheap surface” quietly overcharged. The list now shows only the correct, verified products: Dart Apex (Air, recommended), Domestic Priority / Express, and Dart Surfaceline (Surface). Surfaceline must be provisioned on your Customer Code or shipments are rejected. If you were on the old Surface option, re-check your service — Apex is usually the right, cheaper choice.',
    ],
  },
  'v1.163.1': {
    date: '05 Aug 2026',
    features: [],
    fixes: [
      'Delhivery ship reliability (follow-up): fixed a bug where shipping an order on a courier account that had no explicit account tag would error with “userId is not defined” right after Delhivery had already made the AWB — leaving another orphaned label. Affected accounts can now ship normally; any labels orphaned by this have been recovered onto their orders.',
    ],
  },
  'v1.163.0': {
    date: '05 Aug 2026',
    features: [],
    fixes: [
      'Delhivery bulk-ship reliability: fixed shipments that showed as “failed” in the panel even though Delhivery had already generated the AWB (an orphaned label, not fulfilled on the store). The create timeout was raised, the reference is now stable so a retry returns the same AWB instead of a duplicate, and if a create times out we look the AWB up and recover it automatically.',
    ],
  },
  'v1.162.1': {
    date: '02 Aug 2026',
    features: [],
    fixes: [
      'Blue Dart: the per-location Customer Code you set on a warehouse now always wins when booking, so shipments are billed on the exact rate account you chose (fixes small parcels being charged on a heavier-slab code).',
    ],
  },
  'v1.162.0': {
    date: '02 Aug 2026',
    features: [
      'Multiple accounts of the same courier on one location: you can now add, say, two different Blue Dart accounts to the same warehouse. Each is its own lane with its own priority; shipping falls back between them, and every order remembers which account it shipped on. Enable/disable, priority and RTO are managed per account.',
    ],
    fixes: [
      'Pickup Manifest: removed all money (amount column, COD/Prepaid ₹, order value). It now shows only counts — total shipments, total quantity, and COD/Prepaid shipment counts.',
    ],
  },
  'v1.161.0': {
    date: '01 Aug 2026',
    features: [],
    fixes: [
      'You can now switch a courier account on a location that has already shipped — e.g. remove one Blue Dart account and add a different one to the same location. It’s no longer “locked”. Each order remembers the exact account it shipped on, so tracking and cancellation of older shipments keep working on the original account while new orders use the new one.',
    ],
  },
  'v1.160.0': {
    date: '01 Aug 2026',
    features: [],
    fixes: [
      'Blue Dart: fixed Surface (Dart Plus) shipments failing and pickups not registering — parcels were being sent as “documents” which broke Surface bookings. Now booked correctly, so the service you pick at connect (e.g. Surface, cheaper) is what’s charged — no more defaulting to the pricier Air slab.',
      'Blue Dart: the pickup is registered with Blue Dart at ship time and its pickup token now shows in the Pickups page (Courier Pickup column), like Delhivery.',
    ],
  },
  'v1.159.3': {
    date: '01 Aug 2026',
    features: [],
    fixes: [
      'Pickup Manifest polish: courier name now shows the actual carrier big with the aggregator as a small “via …” line (like the shipping label); a unique Manifest ID is shown at the top and repeated on every printed page (proof if it spans pages); and a Handover Summary (Manifest ID, date, courier, total shipments, total quantity, COD/Prepaid, order value) now sits right above the signatures so both parties sign against those totals.',
    ],
  },
  'v1.159.2': {
    date: '01 Aug 2026',
    features: [],
    fixes: [
      'Pickup Manifest: Velocity shipments no longer show up under a blank “Velocity” group. Freshly-printed orders don’t always have the sub-courier name yet, so the manifest now resolves it from the Velocity carrier code (learned map) — they group under the correct sub-courier (e.g. Velocity · Delhivery).',
    ],
  },
  'v1.159.1': {
    date: '01 Aug 2026',
    features: [],
    fixes: [
      'Pickup Manifest moved to the Pickups page — the “Manifest” button now sits next to Reprint All on each location row (removed from the Locations page). Same courier-wise manifest.',
    ],
  },
  'v1.159.0': {
    date: '01 Aug 2026',
    features: [
      'Pickup Manifest: each location now has a “📋 Manifest” button. It shows a courier-wise list of shipments ready to hand over (AWB assigned, not yet picked) — aggregators split by sub-courier — and downloads a print-ready pickup manifest per courier. Includes seller + pickup details, all waybills, COD/Prepaid totals, and a pickup-executive signature block.',
    ],
    fixes: [],
  },
  'v1.158.0': {
    date: '01 Aug 2026',
    features: [
      'Import all products: on the Sales Channels page each Shopify store now has an “Import Products” button that pulls every active product + variant into the Products master (not just ones you’ve had orders for). It seeds HSN, weight and image from Shopify and never overwrites your cost / GST / HSN / dimensions.',
      'MOD SKU: every product now has a system-assigned internal code (MOD-000001…), shown as a new column on the Products page and searchable. All existing products have been assigned one, and every new product gets one automatically.',
    ],
    fixes: [],
  },
  'v1.157.0': {
    date: '01 Aug 2026',
    features: [
      'Return / Exchange: click any row to open a full detail popup — customer, items, pickup, the complete journey with dates, and a Refund Details section. You can now edit a recorded refund (amount, method, reference, note) and revert a refund made by mistake — every change is logged in the request’s timeline.',
    ],
    fixes: [],
  },
  'v1.156.5': {
    date: '01 Aug 2026',
    features: [],
    fixes: [
      'Label: the item Quantity is now just slightly bigger and bold (removed the highlighted box from the previous update). Barcode height + side margins unchanged.',
      'Label & Invoice: the Print button is more reliable — it now focuses the window and waits for the barcode/logo images to finish loading before printing, so the first print isn’t blank and the button responds after an earlier print dialog.',
    ],
  },
  'v1.156.4': {
    date: '01 Aug 2026',
    features: [],
    fixes: [
      'Shipping label polish: the main AWB barcode is a little taller (clearer for scanners), a 2mm safe margin was added on both sides so thermal printers don’t clip the barcode/QR at the paper edge, and the item Quantity is now a bigger highlighted badge so it’s easy to read while packing.',
    ],
  },
  'v1.156.3': {
    date: '01 Aug 2026',
    features: [],
    fixes: [
      'The manual “Update Status” override (a testing tool that stops real courier tracking on an order) is now hidden by default and enabled per account by admin only. Admin → user → “Debug tools” toggle. Off for all live accounts.',
    ],
  },
  'v1.156.2': {
    date: '01 Aug 2026',
    features: [],
    fixes: [
      'Cancelling a full order now stays Cancelled. Before, when you cancelled an order we cancelled the shipment at the courier, and the courier’s cancellation echo was mistaken for a courier-side cancel — so the order bounced back into Need Attention → Courier Error instead of Cancelled. It now recognises our own cancel and leaves the order Cancelled. 13 orders already affected have been moved back to Cancelled.',
    ],
  },
  'v1.156.1': {
    date: '01 Aug 2026',
    features: [],
    fixes: [
      'Prepaid RTO: COD orders no longer wrongly appear in the Prepaid RTO tab. A split-COD part reads “partially paid” once a sibling part’s COD is collected, which was being mistaken for a prepaid order. It now uses the real prepaid/COD flag. 5 wrongly-listed COD orders have been removed from the tab.',
    ],
  },
  'v1.156.0': {
    date: '01 Aug 2026',
    features: [],
    fixes: [
      'Major RTO fix — the FULL delivery address (address line 1 + line 2 + landmark/company) is now sent to every courier (Delhivery, Velocity, Shiprocket, Ekart, Blue Dart), forward and reverse. Previously some couriers received only line 1, so parcels went out with a half address and RTO’d. City / state / pincode stay in their own fields; the address is capped to each courier’s limit. Our shipping labels now print the full address too. Applies to new shipments (already-booked AWBs can’t be changed at the courier).',
    ],
  },
  'v1.155.2': {
    date: '01 Aug 2026',
    features: [],
    fixes: [
      'Major fix — Delhivery shipments were being created with only the first line of the delivery address (address line 1), dropping line 2 (building / street / area). Parcels went out with a half address and were more likely to RTO. Delhivery now sends the full address. Affects new shipments only — already-booked AWBs can’t be changed at the courier. (Velocity, Shiprocket, Ekart and Blue Dart already sent the full address.)',
    ],
  },
  'v1.155.1': {
    date: '31 Jul 2026',
    features: [],
    fixes: [
      'Order invoice: the invoice number now uses the order number (e.g. NSD/2026-27/HG26811) instead of a running serial; the invoice date is the order date; added a Payment line (Paid / COD Paid / COD Pending with the date); and the Print / Save PDF button now works reliably.',
    ],
  },
  'v1.155.0': {
    date: '31 Jul 2026',
    features: [
      'Download Invoice: every order’s ⋮ menu now has “Download Invoice” — a print-ready GST tax invoice (From = your billing details, To = the customer, with store name + logo, HSN, and CGST+SGST for same-state or IGST for other-state, computed on the discounted price). Prices are treated as GST-inclusive.',
      'Products: new HSN column (next to GST %) — set the HSN code per product. The Download Invoice button needs an HSN on every item in the order; it tells you which products are missing one.',
      'Profile → Billing Details: set your Invoice Number Prefix (e.g. NSD → NSD/2026-27/0001). Numbers are sequential per financial year and locked once an invoice is issued. No GSTIN → invoices print as a Bill of Supply.',
    ],
    fixes: [],
  },
  'v1.154.0': {
    date: '31 Jul 2026',
    features: [
      'Import orders from a date: when you connect a Shopify channel you can now choose how far back to pull orders (1st of this month / today / a custom date / all time). Orders before your chosen date aren’t imported, and any already imported that haven’t shipped are removed. Change it anytime from the channel’s “Import from” button.',
    ],
    fixes: [],
  },
  'v1.153.3': {
    date: '31 Jul 2026',
    features: [],
    fixes: [
      'Setup checklist (“Nice progress” popup): now updates live after you add a location or courier — it was showing the status from login only, so completed steps stayed pending and the popup wouldn’t clear. It refreshes on open and once on load, and disappears when all 3 steps are done.',
    ],
  },
  'v1.153.2': {
    date: '31 Jul 2026',
    features: [],
    fixes: [
      'Exchange replacement orders (-EX) and Return/Exchange requests now carry the product image — the row showed a blank 📦 before because the image was never copied from the product. Existing ones have been backfilled.',
    ],
  },
  'v1.153.1': {
    date: '31 Jul 2026',
    features: [
      'Orders header: new “Shipped Revenue” block — the value of every order shipped via ModCommerce (has an AWB, not cancelled). Sits between Total Revenue and Delivered Revenue so you can read booked → shipped → delivered at a glance.',
    ],
    fixes: [],
  },
  'v1.153.0': {
    date: '31 Jul 2026',
    features: [
      'Orders header: new “Delivered Revenue” block — the value of orders the courier actually delivered (COD collected + prepaid that reached the customer). Excludes RTO, in-transit and cancelled, so you can see realized revenue next to total booked revenue.',
    ],
    fixes: [],
  },
  'v1.152.2': {
    date: '31 Jul 2026',
    features: [
      'Prepaid RTO: Cancel is now available only once the parcel has actually Returned. While it is still Returning you can Reship (ships a fresh replacement), but Cancel is held back so you don’t cancel a parcel that might still get delivered.',
    ],
    fixes: [
      'Prepaid RTO: an order you already reshipped/cancelled no longer pops back into the tab when its parcel is later received at the warehouse.',
    ],
  },
  'v1.152.1': {
    date: '31 Jul 2026',
    features: [
      'Prepaid RTO: select multiple orders to get Bulk Reship and Bulk Cancel, same as the other tabs.',
    ],
    fixes: [],
  },
  'v1.152.0': {
    date: '31 Jul 2026',
    features: [
      'Prepaid RTO now appears in Need Attention → Prepaid RTO the moment a prepaid order starts returning (courier RTO), not only after it is physically back. Reship / Cancel are available right away, and the order leaves the tab once you act. All existing prepaid RTOs have been backfilled in.',
      'Orders: the AWB is now a clickable link that opens the courier’s tracking page — the same tracking URL your customer sees on the sales channel.',
    ],
    fixes: [],
  },
  'v1.151.0': {
    date: '31 Jul 2026',
    features: [
      'Return / Exchange now opens on the Requested tab (your working queue), and the All tab moved to the end — matching the Orders page.',
      'Exchange → “Instantly” option: create the replacement order the moment you create the request (ships from Ready to Ship without waiting for pickup or receipt). Alongside the existing After-pickup and After-QC options.',
    ],
    fixes: [
      'Exchange with different replacement items: you can now add more than one product. (A bug matched every product against a blank id, so only the first pick stuck.)',
    ],
  },
  'v1.150.1': {
    date: '30 Jul 2026',
    features: [],
    fixes: [
      'Cancelling a Blue Dart shipment now also cancels the waybill on Blue Dart (it was only being cancelled inside ModCommerce before). Cancel / RTO / order-cancel all propagate to Blue Dart now.',
    ],
  },
  'v1.150.0': {
    date: '30 Jul 2026',
    features: [
      'Blue Dart connect now has a Service / Product selector — Dart Apex (Air), Domestic Priority, or Dart Surfaceline (Surface). Pick the service your Blue Dart account is enabled for; it’s used on every shipment (Surface is cheaper but must be provisioned on your Customer Code). Defaults to Dart Apex.',
    ],
    fixes: [],
  },
  'v1.149.2': {
    date: '30 Jul 2026',
    features: [],
    fixes: [
      'Fixed Blue Dart shipping — it was reporting “not connected” at ship time (the credential check didn’t recognise Blue Dart’s licence-key format). The Origin Area is now also auto-derived from each warehouse’s pincode, so you don’t need to enter it. (A warehouse in a different origin city still needs that city’s Blue Dart Customer Code — add it on the location.)',
    ],
  },
  'v1.149.1': {
    date: '30 Jul 2026',
    features: [],
    fixes: [
      'The auto-calculated L×B×H now shows in the Total Wt column for every order (not only after editing), so you can see the package size at a glance; editing still overrides it.',
    ],
  },
  'v1.149.0': {
    date: '30 Jul 2026',
    features: [
      'Order weight & dimensions: every order now carries L×B×H (not just weight), pushed to all couriers at ship time for correct volumetric pricing. Dimensions are auto-calculated from your products (widest footprint, stacked height), and you can fine-tune weight + L/B/H per order via the ✎ pen on the Total Wt column — it applies on every courier.',
      'Blue Dart tracking is now automatic — once a Blue Dart shipment is picked up, its status updates on the same background tracking job as every other courier (no manual sync).',
    ],
    fixes: [],
  },
  'v1.148.0': {
    date: '30 Jul 2026',
    features: [
      'Blue Dart shipping is now live end-to-end — ship an order on Blue Dart to auto-generate the waybill (AWB), register the pickup, and capture the real shipping cost at ship time (straight into your Shipping Cost panel). Reverse pickup, cancel and tracking are wired too. One Blue Dart account can serve multiple warehouses; add an optional per-location Customer Code / Origin Area for warehouses in a different city.',
    ],
    fixes: [],
  },
  'v1.147.1': {
    date: '30 Jul 2026',
    features: [
      'Blue Dart can now be added to a location (Locations → Add Courier). It supports a different RTO / return address (via API) just like Delhivery — pick any warehouse as the return address.',
    ],
    fixes: [],
  },
  'v1.147.0': {
    date: '30 Jul 2026',
    features: [
      'Blue Dart courier can now be connected under Integrations → Couriers: enter your Blue Dart account keys (Login ID, Shipping Licence Key, Tracking Key, Customer Code). Client ID / Secret are optional — leave blank to use ModCommerce’s shared app, or paste your own for isolated credentials. Verify checks the connection.',
    ],
    fixes: [],
  },
  'v1.146.1': {
    date: '30 Jul 2026',
    features: [],
    fixes: [
      'ModIQ attempt table: filled the empty space by spreading each Out-for-Delivery / Delivered cell into Total · COD · Prepaid (colour-coded), so the payment breakdown is clear at a glance instead of cramped.',
    ],
  },
  'v1.146.0': {
    date: '30 Jul 2026',
    features: [
      'ModIQ Predicted COD is now self-correcting. It learns from the last few days’ predicted-vs-actual accuracy and auto-adjusts today’s forecast, so the predicted figure tracks what you actually collect much more closely (an “auto-tuned” tag shows when it’s active). Also finished the panel speed/cost improvements (v1.140.0) — faster loads, and completed orders no longer counted as open.',
    ],
    fixes: [],
  },
  'v1.145.0': {
    date: '30 Jul 2026',
    features: [
      'ModIQ: “Out for Delivery vs Delivered — by attempt” is now a single combined table — each attempt shows Out-for-Delivery and Delivered side by side (with COD / Prepaid split) plus a Delivery % column, and a bold TOTAL row with the overall delivery rate.',
      'ModIQ: the Predicted COD hero now highlights “Collected so far” as a big green figure alongside “Still expected”, so you can see progress at a glance.',
    ],
    fixes: [],
  },
  'v1.144.0': {
    date: '30 Jul 2026',
    features: [
      'Locations: added a “↻ Re-sync” button on Velocity/Ekart courier integrations. If a location’s pickup warehouse was created before you added its contact details, click Re-sync to push the current name / phone / email to the courier — no need to remove and re-add the courier (which is blocked once shipments exist). Works even on locked integrations.',
    ],
    fixes: [],
  },
  'v1.143.1': {
    date: '26 Jul 2026',
    features: [
      'Return & Exchange: the In Progress tab now has sub-tabs — Waiting Pickup and In Transit (with counts) — so you can drill into exactly where each return is, just like the Need Attention section.',
    ],
    fixes: [],
  },
  'v1.143.0': {
    date: '26 Jul 2026',
    features: [
      'Return & Exchange filters: filter by store (Store View), by date (All / Today / Yesterday / Last 7 / Last 30 / This Month / Custom — defaults to All time), and by journey stage (Request received, Pickup booked, Picked up, Received, Refund pending, Completed, Cancelled). Tab counts update to match your filters.',
    ],
    fixes: [
      'Tidied the Actions column — one clear action button per row with the note and cancel controls grouped as small icons, so the column no longer looks cluttered.',
    ],
  },
  'v1.142.0': {
    date: '26 Jul 2026',
    features: [
      'Return & Exchange: add an editable note to any request (📝 on each row) — jot anything, edit and re-save anytime. Rows with a note show a small marker.',
      'Record a refund early — as soon as the parcel is picked up, without waiting for it to reach the warehouse. The refund shows on the journey right away, and the parcel still flows through Receive / QC so you get your stock back.',
      'The journey now shows the date under each completed step, so you can see exactly when each stage happened.',
    ],
    fixes: [],
  },
  'v1.141.2': {
    date: '26 Jul 2026',
    features: [],
    fixes: [
      'Return & Exchange journey redesigned as a compact horizontal tracker in its own boxed column (placed just before Actions), keeping each row clean and organised. The current pending step is highlighted so you can spot where each request is stuck at a glance.',
    ],
  },
  'v1.141.0': {
    date: '26 Jul 2026',
    features: [
      'Return & Exchange page now shows a clear journey on every request — Request received → Pickup booked → Picked up → Received at warehouse → Refund — with each step marked done, pending, or not required, so you can see exactly where every return/exchange is stuck at a glance.',
      'Simplified the tabs (All · Requested · In Progress · Received · Completed · Cancelled) into one unified view — returns and exchanges sit together in the same list, each tagged with its type.',
    ],
    fixes: [],
  },
  'v1.140.0': {
    date: '26 Jul 2026',
    features: [
      'The panel is now noticeably faster to load and refresh. Order counts and the ModIQ board are reused between refreshes instead of being recalculated from scratch every time, and they still update the moment anything actually changes.',
    ],
    fixes: [
      'Completed orders (delivered, returned, cancelled) were still being treated as open in the background. They are now correctly marked as closed, so the All Orders tab shows your genuinely open orders and existing completed orders were tidied up automatically. Your delivered/returned/cancelled orders are all still there in their own tabs — nothing was removed.',
      'Background browser tabs no longer keep refreshing in the background. Leaving the panel open in a spare tab used to keep it working all day; it now pauses and refreshes the moment you switch back to it.',
      'Courier tracking updates now check only shipments that are still moving, instead of re-checking every parcel ever sent. Tracking is unchanged — it just does far less redundant work.',
    ],
  },
  'v1.139.0': {
    date: '26 Jul 2026',
    features: [
      'Credit Notes are now grouped by batch, not by product. Buying back an RTO batch creates ONE credit note for the whole batch under the chosen vendor, with each product as a line item (same product across multiple orders is combined into one line with the total quantity). Each note shows its batch number, date, and which orders it covers.',
      'Per-unit cost is now prefilled from your product cost (last purchase price, else the product’s cost price — GST-exclusive) and stays fully editable per line before you Confirm. The note total updates live.',
    ],
    fixes: [
      'Existing draft credit notes were reorganised into the new batch-wise format automatically.',
    ],
  },
  'v1.138.6': {
    date: '26 Jul 2026',
    features: [],
    fixes: [
      'RTO returns no longer cancel the Shopify order. When a returned parcel is received/reconciled, the Shopify order is kept intact (with its failed-delivery status) and simply tagged “RTO” + “Returned”, with an order note “Returned to warehouse, Reconciled - Batch <number>”. You can still see the RTO in Shopify — without the order being cancelled. (Prepaid RTOs are unchanged — they still wait in Need Attention → Prepaid RTO for a Cancel/Reship decision.)',
    ],
  },
  'v1.138.5': {
    date: '25 Jul 2026',
    features: [],
    fixes: [
      'Search on the All Orders tab now finds completed orders too — delivered, returned and cancelled orders were being skipped, so searching a customer’s phone/order could miss their older (e.g. returned) orders. It now searches across every order.',
    ],
  },
  'v1.138.4': {
    date: '25 Jul 2026',
    features: [],
    fixes: [
      'Fixed an order status mismatch: an order held for a courier problem (e.g. “Invalid weight / dimensions”) could show “Ready to Ship” in the All tab yet be missing from the Ready to Ship tab. It now correctly shows as Need Attention everywhere, so it lines up with where the order actually sits.',
    ],
  },
  'v1.138.3': {
    date: '25 Jul 2026',
    features: [
      'ModIQ — redesigned the “Out for Delivery vs Delivered” comparison as two clean accent cards (with attempt pills, COD / Prepaid split per attempt, and a gradient Total footer) for much easier reading.',
    ],
    fixes: [],
  },
  'v1.138.2': {
    date: '25 Jul 2026',
    features: [],
    fixes: [
      'Fixed a white screen on the Dashboard (a missing icon import in the new ModIQ tab). Dashboard loads normally again.',
    ],
  },
  'v1.138.1': {
    date: '25 Jul 2026',
    features: [
      'ModIQ is now its own tab on the Dashboard (Overview | ModIQ) — it loads on demand with a smooth reveal, so the main dashboard stays clean.',
      'Live Shipment Status moved into ModIQ (now a premium view), restyled to match.',
      'Out-for-Delivery and Delivered are merged into one attempt-by-attempt comparison table, each cell showing Total with its COD / Prepaid split — much easier to compare.',
      'Courier scorecard rebuilt: every courier now shows COD delivery success at 1st / 2nd / 3rd / 4th+ attempt, an overall rate and an RTO %.',
    ],
    fixes: [
      'Courier scorecard now includes all couriers (small-volume ones like Ekart/DTDC were hidden before).',
    ],
  },
  'v1.138.0': {
    date: '25 Jul 2026',
    features: [
      'ModIQ — a new premium add-on. Enable it in Mod Add-ons and a live “Today’s intelligence” panel appears at the top of your Dashboard.',
      'ModIQ shows today’s Out-for-Delivery and Delivered broken down by attempt (1st / 2nd / 3rd / 4th+) with COD vs prepaid and revenue, your first-attempt delivery rate, NDR / RTO / cancelled today, and prepaid + COD collection by channel and courier — all versus yesterday and auto-refreshing.',
      'Predictive COD: ModIQ learns each courier’s COD delivery success by attempt and forecasts how much COD you’ll collect today, versus what’s collected so far — so at day end you can compare predicted vs actual.',
    ],
    fixes: [],
  },
  'v1.137.0': {
    date: '24 Jul 2026',
    features: [
      'RTO / Return Reconcile now shows customer Returns and Exchanges alongside courier RTO — previously only courier RTO parcels appeared, even though returns come back physically too.',
      'New Type column classifies every row as RTO, Return or Exchange, so you can tell at a glance why a parcel came back.',
      'New “Product cost” figure at the top and a per-row Product Cost column — the cost of the goods actually coming back into stock, not just their sale value.',
    ],
    fixes: [
      'Orders with several products no longer run together in one long line — each product is on its own line with a “+N more” count.',
      'Clearer column names: “Returned by Courier” and “Received at Warehouse” (was just Return / Received).',
    ],
  },
  'v1.136.4': {
    date: '24 Jul 2026',
    features: [],
    fixes: [
      'Orders list: removed the “RTO: <location>” chip under the From location — sitting under the pickup location it read as part of it and confused things. The RTO / return address still shows in the order detail popup (“Pickup → Returns to”).',
    ],
  },
  'v1.136.3': {
    date: '23 Jul 2026',
    features: [],
    fixes: [
      'Locations card: swapped the colorful emoji for minimal monochrome line icons and neutral tiles for a cleaner, quieter look.',
    ],
  },
  'v1.136.2': {
    date: '23 Jul 2026',
    features: [
      'Locations page redesign — cleaner location cards with an icon header, Active / Locked / last-updated badges, a Phone · Warehouse PIN · RTO Scanner info strip, and courier rows with a Priority + Primary/Secondary/Backup label, and the RTO address selector inline.',
    ],
    fixes: [
      'RTO / Return address now shows correctly after reload (the saved value was being persisted but not sent back to the browser).',
    ],
  },
  'v1.136.0': {
    date: '23 Jul 2026',
    features: [
      'Separate RTO / Return address — ship from one warehouse but have undelivered (RTO) parcels come back to a different one. Pick it when adding a courier to a location, or change it any time from the courier row (applies to future orders).',
      'Supported on Delhivery, Velocity and Ekart (verified live with real shipments). Shiprocket shows the field greyed out — its RTO address can only be set inside the Shiprocket panel.',
      'Orders now show an “↩ RTO: <location>” chip only when the return address differs from the pickup, plus a “Pickup → Returns to” line in the order detail popup.',
      'RTO / Return Reconcile now files returning parcels under the RTO location instead of the pickup location, so the right warehouse sees them. A parcel physically received elsewhere still scans in fine and is flagged as cross-location.',
    ],
    fixes: [],
  },
  'v1.135.0': {
    date: '23 Jul 2026',
    features: [],
    fixes: [
      'Split COD orders: a remaining split part now correctly ships as COD (was shipping Prepaid once a sibling part was delivered and the order went “Partially Paid”). The Payment column now shows “COD (Pending · split)” for these instead of “Partially Paid”.',
    ],
  },
  'v1.134.1': {
    date: '18 Jul 2026',
    features: [],
    fixes: [
      'Return / Exchange list: added copy buttons on the original order number, request number and reverse AWB, plus a COD / Prepaid chip on the original order for reference.',
    ],
  },
  'v1.134.0': {
    date: '18 Jul 2026',
    features: [
      'Redesigned the order detail popup: header with status badge + placed date, an icon-tile info grid (customer, email, phone, amount, courier, AWB with copy), shipping address, and item cards.',
      'New Order Journey timeline showing only the events that actually happened — Order Received → AWB Generated → Printed → In Transit → Out for Delivery → Delivered, plus delivery attempts, RTO, cancels, reships and return/exchange events, each with its date. Horizontal on desktop, vertical on mobile.',
    ],
    fixes: [],
  },
  'v1.133.0': {
    date: '18 Jul 2026',
    features: [
      'Global order search in the Orders header — search any order across all tabs by number, AWB, customer, phone or email; pick a result to jump to it (All Orders, filtered).',
      'The Need Attention tab now gently pulses when there are orders needing action, and stays calm when there’s nothing to fix.',
    ],
    fixes: [],
  },
  'v1.132.1': {
    date: '18 Jul 2026',
    features: [],
    fixes: [
      'Fixed reshipped orders printing the OLD label: if a Velocity→Amazon shipment was cancelled and reshipped (e.g. via Delhivery, or a non-Amazon carrier), the old Amazon label was still printed. The stored native label is now cleared on the new shipment, and stale Velocity carrier/label fields are reset.',
    ],
  },
  'v1.132.0': {
    date: '18 Jul 2026',
    features: [
      'Need Attention moved back into the Orders page as the first tab (before Ready to Ship). Selecting it reveals the QC/Validation · No Stock · Courier Error · Prepaid RTO · All sub-tabs. Orders still opens on Ready to Ship by default; the separate Need Attention menu item is gone (old links redirect).',
    ],
    fixes: [],
  },
  'v1.131.1': {
    date: '18 Jul 2026',
    features: [],
    fixes: [
      'Batch card tidy-up: replaced the big progress bar with a plain “X / Y received”, added a “Generated via Scan/Manual” column, removed the redundant “Receipt” label, and dropped the COD Value tile from the summary.',
    ],
  },
  'v1.131.0': {
    date: '18 Jul 2026',
    features: [
      'RTO / Return Reconcile batch cards redesigned: a cleaner header (ID · location/date · status · received progress bar · Confirm), a summary-tile bar (Total SKUs, Qty, COD value, Product cost, Est. margin), and a per-item table with product image, MRP, cost, product total, received-by and status.',
    ],
    fixes: [],
  },
  'v1.130.0': {
    date: '18 Jul 2026',
    features: [
      'RTO / Return Reconcile batches now stay “Open” until you Confirm them (Restock/Buyback); after confirming they’re “Closed”. New Open / Closed / All status filter on the Batches tab (default Open) and the date range defaults to the last 30 days.',
    ],
    fixes: [],
  },
  'v1.129.0': {
    date: '18 Jul 2026',
    features: [
      'Courier errors are now shown as a short label (e.g. “Wallet balance low”, “Pincode not serviceable”) in the Courier Error tab and the bulk-ship summary, with the full message on hover — instead of the long raw courier text.',
    ],
    fixes: [
      'Dashboard/analytics revenue no longer double-counts prepaid RTO reships or exchange replacements — they’re re-sends of an already-counted order, so they’re excluded from sales metrics (their real value stays on the order for the courier label).',
    ],
  },
  'v1.128.0': {
    date: '18 Jul 2026',
    features: [
      'Bulk Print now shows a confirmation popup before opening labels: total orders, COD/Prepaid counts + values, a location + courier breakdown, and a product pick-list (name × quantity) with a Copy button to hand to the packer.',
    ],
    fixes: [],
  },
  'v1.127.0': {
    date: '18 Jul 2026',
    features: [
      'Mixed orders (a normal in-stock product + an auto-PO “sell at zero” product) now flow to Ready to Ship from the stocked product’s location instead of being held — the auto-PO item rides along and drafts a PO on pickup. Previously the out-of-stock auto-PO line wrongly held the whole order in Need Attention.',
    ],
    fixes: [],
  },
  'v1.126.1': {
    date: '18 Jul 2026',
    features: [],
    fixes: [
      'Split orders: a split part now always ships/collects ONLY its own value. Fixed a case where a split part could push the full order total as the COD amount (over-charging the customer). The part value is recomputed from its items at ship time, so a stale total_price can never overcharge.',
    ],
  },
  'v1.126.0': {
    date: '18 Jul 2026',
    features: [
      'RTO / Return Reconcile: each scan session is now its own batch (was one batch per day/location). Manual mark-received still groups into a daily batch per location.',
      'Batch view is now flat (one card per batch, no location grouping), and opening a batch shows a summary: total orders, total qty, COD value, and product cost (flagged partial with the missing products when some costs aren’t set).',
      'Re-scanning an already-received parcel now says “Already received in batch #X” instead of silently counting.',
    ],
    fixes: [],
  },
  'v1.125.0': {
    date: '18 Jul 2026',
    features: [
      'RTO / Return Reconcile scanner now also receives customer returns: scan a return’s reverse AWB and it’s marked received (QC good + restock) — Returns go to Refund Pending, Exchanges create the replacement. Manual Receive still available.',
      'Exchange price difference: when the customer owes money, choose to collect it Online (prepaid replacement) or via Courier COD (replacement ships COD collecting only the difference).',
    ],
    fixes: [],
  },
  'v1.124.0': {
    date: '18 Jul 2026',
    features: [
      'Return refund: Mark Refunded now records the method (UPI/IMPS/NEFT/Cash/Payout Link, or “Original payment method” for prepaid) + reference + note.',
      'On completion, the original order is auto-tagged in Shopify: “Customer Returned [ (Partial) ] - <ID>” for returns and “Customer Exchange Request - Full/Partial - <ID>” for exchanges; the replacement order carries an “Exchange <ID>” tag. Split orders tag the real Shopify order.',
    ],
    fixes: [],
  },
  'v1.123.0': {
    date: '18 Jul 2026',
    features: [
      'Return / Exchange table: new Date column colour-coded by who created the request, a Processed-by filter, and a dedicated Courier / AWB column (aggregators show the sub-carrier, e.g. Velocity · Bluedart).',
      'Receive now only appears once a return reaches At Warehouse (it needs physical QC). Menu renamed to “RTO / Return Reconcile”.',
    ],
    fixes: [],
  },
  'v1.122.1': {
    date: '18 Jul 2026',
    features: [],
    fixes: [
      'Return / Exchange: Cancel is now only available before pickup (Requests / Waiting Pickup). Once the courier has collected the parcel it’s on its way to the warehouse and can’t be recalled — it must be received.',
    ],
  },
  'v1.122.0': {
    date: '18 Jul 2026',
    features: [
      'Return / Exchange: after booking, a request now sits in a new “Waiting Pickup” tab and auto-advances (Waiting Pickup → In Transit → Out for Delivery → At Warehouse) as the courier scans it — just like the Orders tabs.',
      'Cancelling a booked return now also cancels the reverse pickup on the courier side (pre-pickup); if the courier already collected it, you’re told to cancel in their panel.',
      'Return / Exchange table redesigned: Channel · Original Order (+ date) · Request (+ AWB) · Type (+ who/when) · Customer · Items · Refund/Price diff · Warehouse.',
    ],
    fixes: [],
  },
  'v1.121.0': {
    date: '18 Jul 2026',
    features: [
      'Return / Exchange: Shiprocket reverse pickup is now integrated too — all four couriers (Delhivery, Velocity, Ekart, Shiprocket) can auto-book a return pickup with AWB.',
    ],
    fixes: [],
  },
  'v1.120.0': {
    date: '18 Jul 2026',
    features: [
      'Return / Exchange: Ekart reverse pickup is now integrated (auto AWB) alongside Delhivery and Velocity. Only Shiprocket remains manual for reverse pickup.',
    ],
    fixes: [],
  },
  'v1.119.0': {
    date: '18 Jul 2026',
    features: [],
    fixes: [
      'Order tags cleaned up: reship orders show “RTO Prepaid Reship”, exchange replacements “Customer Exchange”, and returns/exchanges “Customer Return” / “Customer Exchange” — coloured highlights, no emoji.',
    ],
  },
  'v1.118.3': {
    date: '18 Jul 2026',
    features: [],
    fixes: [
      'Book Reverse Pickup: Delhivery/Velocity now default to automatic booking (no manual AWB field unless you choose it). The courier list shows only the couriers linked to that warehouse, with their linked account.',
    ],
  },
  'v1.118.2': {
    date: '18 Jul 2026',
    features: [],
    fixes: [
      'Fixed automatic reverse pickup for Delhivery and Velocity — the request payloads now match each courier’s reverse-shipment API (Delhivery payment_mode “Pickup”; Velocity pickup_/shipping_ address blocks). Auto-booking a return should now generate the AWB instead of erroring.',
    ],
  },
  'v1.118.1': {
    date: '17 Jul 2026',
    features: [],
    fixes: [
      'Reverse pickup now defaults to entering the AWB manually (reliable) with an optional “Try auto-booking (beta)” for Delhivery/Velocity. Integrated auto reverse pickup still needs each courier’s account/API verification.',
    ],
  },
  'v1.118.0': {
    date: '17 Jul 2026',
    features: [
      'Return / Exchange: Velocity reverse pickup is now integrated (auto AWB) alongside Delhivery. The Book Pickup courier list shows the couriers configured on that warehouse; Delhivery + Velocity auto-book, others are manual (enter AWB).',
    ],
    fixes: [],
  },
  'v1.117.3': {
    date: '17 Jul 2026',
    features: [],
    fixes: [
      'Reverse pickup: you can now book manually (enter the AWB) even for Delhivery — use it if Delhivery’s auto reverse pickup is rejected (e.g. RVP not enabled on the account).',
    ],
  },
  'v1.117.2': {
    date: '16 Jul 2026',
    features: [],
    fixes: [
      'Delhivery reverse pickup now includes the warehouse return address + weight in the request (was causing a generic Delhivery error). If Delhivery still rejects it, use a manual courier + AWB.',
    ],
  },
  'v1.117.1': {
    date: '16 Jul 2026',
    features: [],
    fixes: [
      'Fixed “Enter courier + AWB” error when booking a Delhivery reverse pickup — Delhivery auto-books (no AWB needed); only manual couriers require the AWB.',
    ],
  },
  'v1.117.0': {
    date: '16 Jul 2026',
    features: [
      'Return / Exchange now runs end-to-end: Book Pickup books an integrated Delhivery reverse pickup (auto AWB + label; other couriers = enter the AWB manually), then Receive records QC (Good/Damaged) and restocks the returned items.',
      'Returns move to Refund Pending → Mark Refunded records the refund (you issue it in Shopify). Exchanges auto-create the replacement order in Ready to Ship (on pickup or after QC, per your choice).',
    ],
    fixes: [
      'Create Return/Exchange popup now opens centered.',
    ],
  },
  'v1.116.1': {
    date: '16 Jul 2026',
    features: [],
    fixes: [
      'Fixed the “Create Return / Exchange” option not appearing in the delivered order menu — it was added to the wrong menu. It now shows in the ⋮ menu on Delivered orders.',
    ],
  },
  'v1.116.0': {
    date: '16 Jul 2026',
    features: [
      'New Return / Exchange section (left menu). Create a return or exchange from a delivered order’s ⋮ menu — pick items/qty/reason, choose the return warehouse, and for exchanges pick same or different replacement items with a price-difference calculator.',
      'Return / Exchange management page with Requests · In Transit · Out for Delivery · At Warehouse · Refund/QC · Completed tabs, filters, and a Book Pickup action (reverse AWB). This is the foundation; integrated reverse-pickup, warehouse receiving and refunds land next.',
    ],
    fixes: [],
  },
  'v1.115.0': {
    date: '16 Jul 2026',
    features: [
      'Reship (prepaid RTO) orders now live under their original sales channel instead of Custom Channel, with a “🔁 Reship” badge showing the origin — so they stay traceable and reportable per channel (still excluded from revenue).',
    ],
    fixes: [],
  },
  'v1.114.2': {
    date: '16 Jul 2026',
    features: [],
    fixes: [
      'Remittance no longer double-counts prepaid reship/clone orders (Custom Channel). These copy the original order’s amount, which is already counted, so their prepaid amount is now excluded. COD collections and genuine manual sales are unaffected.',
    ],
  },
  'v1.114.1': {
    date: '16 Jul 2026',
    features: [],
    fixes: [
      'Orders page: the loading skeleton now also shows when using Next / Previous pagination or changing the per-page count (25/50/100…), not just on tab switches.',
    ],
  },
  'v1.114.0': {
    date: '16 Jul 2026',
    features: [
      'Pickups page redesigned: each location is a clean card with icon stat blocks — couriers · batches / orders, Prepaid / COD, and Picked / total — plus Reprint All and Packer (All).',
      'Pickups now default to Today with status All.',
    ],
    fixes: [],
  },
  'v1.113.1': {
    date: '16 Jul 2026',
    features: [],
    fixes: [
      'Fixed RTO orders wrongly jumping to the Cancelled tab after a sync. When an RTO order is cancelled in Shopify as part of the return close, sync no longer treats that as a fresh cancellation — the order stays in Returning/Returned. 18 affected orders were restored.',
    ],
  },
  'v1.113.0': {
    date: '16 Jul 2026',
    features: [
      'Need Attention → Courier Error now works like Ready to Ship: each order shows its assigned location, with a Retry (single) and Bulk Retry action. Bulk Retry locks to one pickup location at a time and lets you pick the courier (Auto by priority or a specific one), exactly like Ready to Ship. The Confirm button is hidden here (these orders are already ready).',
    ],
    fixes: [],
  },
  'v1.112.0': {
    date: '16 Jul 2026',
    features: [
      'Bulk ship: the progress popup now shows the location and courier it’s shipping from/via, and the completion summary shows the shipped count, location(s) and a courier breakdown (with an “auto by priority” note when couriers were auto-picked).',
      'Purchase Order edit: added a searchable “Add a product” box (type name / variant / SKU to filter the list).',
    ],
    fixes: [
      'Purchase Order edit now shows the product variant name on each line.',
      'Tidied the bulk-ship confirmation popup — product names and quantities now align cleanly.',
    ],
  },
  'v1.111.0': {
    date: '16 Jul 2026',
    features: [
      'Need Attention date filter now works on the “attention date” — when an order entered the queue. Courier Error filters by when the error actually happened (so a 3-day-old order that errored today shows under Today), other reasons fall back to the order date.',
    ],
    fixes: [],
  },
  'v1.110.8': {
    date: '16 Jul 2026',
    features: [],
    fixes: [
      'Need Attention: added a Prepaid RTO count to the top summary cards, and the “Needs Attention” total now includes Prepaid RTO orders.',
    ],
  },
  'v1.110.7': {
    date: '16 Jul 2026',
    features: [],
    fixes: [
      'Fixed the Need Attention filter (e.g. “Reason: No stock”) carrying over into the Orders tab when switching between them. Each page now resets its own tab and filters.',
    ],
  },
  'v1.110.6': {
    date: '16 Jul 2026',
    features: [],
    fixes: [
      'Need Attention now opens on QC / Validation by default, with tab order QC → No Stock → Courier Error → Prepaid RTO → All (All moved to the end, like the Orders tabs).',
    ],
  },
  'v1.110.5': {
    date: '16 Jul 2026',
    features: [],
    fixes: [
      'Removed the courier-account reference suffix from the orders list (it showed inconsistently per courier). It still appears in the Ship popup where you pick the account.',
    ],
  },
  'v1.110.4': {
    date: '16 Jul 2026',
    features: [],
    fixes: [
      'Fixed split orders showing the FULL order amount (label total & COD) instead of the split part’s amount after a channel re-sync. Re-sync no longer overwrites a split part’s items/total/weight. Existing affected split orders were corrected.',
      'Shipping label: the courier name is now smaller and wraps cleanly so long names (e.g. “Delhivery Standard (2 Kg)”) no longer overlap.',
    ],
  },
  'v1.110.3': {
    date: '16 Jul 2026',
    features: [],
    fixes: [
      'Fixed Velocity orders showing just “Velocity” without the actual carrier (e.g. Delhivery Standard (2 Kg)) in the orders list. The sub-carrier name now resolves correctly on the list, matching the label and order detail.',
    ],
  },
  'v1.110.2': {
    date: '16 Jul 2026',
    features: [],
    fixes: [
      'Fixed the tracking link sent to the sales channel for Ekart shipments — it was pushing a Shiprocket tracking URL with the Ekart AWB. Each courier now sends its own correct tracking URL and company name.',
    ],
  },
  'v1.110.1': {
    date: '16 Jul 2026',
    features: [],
    fixes: [
      'Prepaid RTO reship now behaves like a fresh order: the return still reconciles Restock/Buyback as normal, and the reship goes to Ready to Ship if stock is available, or Need Attention → No Stock if not (it moves to Ready to Ship automatically once stock is restocked).',
    ],
  },
  'v1.110.0': {
    date: '16 Jul 2026',
    features: [
      'New “Prepaid RTO” sub-tab under Need Attention: prepaid orders that came back RTO are no longer auto-cancelled — they wait here for you to decide.',
      'Reship: one click creates a new order under the Custom Channel (same number + “-RT”) in Ready to Ship, restocks the returned unit, keeps the original paid (tagged RTO-Reshipped, no refund), and updates the customer’s Shopify tracking to the new shipment when it ships.',
      'Cancel: cancels + RTO-tags the order in Shopify (no automatic refund).',
      'Reship clones carry the original channel + order number for later analytics; a re-returned reship comes back to Prepaid RTO to reship (-RT2, -RT3…) or cancel.',
    ],
    fixes: [],
  },
  'v1.109.0': {
    date: '16 Jul 2026',
    features: [
      'New “Need Attention” section in the left menu (below Orders) for orders that need action before they can ship — with a red count badge so nothing is missed.',
      'It has reason sub-tabs: All · No Stock · Courier Error · QC/Validation, each with its own count, plus all the usual Store view, date range and other filters.',
      'The Orders tab no longer starts on Pending — it now opens on “Ready to Ship”. The old Pending list lives in Need Attention.',
    ],
    fixes: [],
  },
  'v1.108.1': {
    date: '16 Jul 2026',
    features: [],
    fixes: [
      'Fixed “Only a custom channel can be renamed” error when renaming a custom channel — renaming now works.',
    ],
  },
  'v1.108.0': {
    date: '16 Jul 2026',
    features: [
      'Store logo on shipping labels can now be resized — open “Logo & size” on a channel to upload a logo and drag a slider (with a live label preview) to make it bigger or smaller. Re-adjust anytime.',
      'Custom channels can be renamed to your own name (editable anytime), shown with a small “(Custom)” tag for reference.',
      'Stock gating and Auto PO are now on/off toggle switches instead of buttons.',
    ],
    fixes: [],
  },
  'v1.107.1': {
    date: '16 Jul 2026',
    features: [
      'Shipping labels (single & bulk) now open with a Print and Download PDF button at the top of the tab, so you can re-print or save the label without relying on the auto print dialog. The buttons don’t appear on the printed/saved label itself.',
    ],
    fixes: [],
  },
  'v1.107.0': {
    date: '15 Jul 2026',
    features: [
      'RTO scan: after submitting, you now get a summary screen — location, scan ID, who scanned, order count — with each order’s products and quantity grouped by RTO batch number.',
      'Download the scan summary as a simple PDF for your records with one click.',
    ],
    fixes: [],
  },
  'v1.106.1': {
    date: '15 Jul 2026',
    features: [
      'RTO Reconcile: for a parcel received ahead of the courier, if the courier never confirms the return you can now manually “✓ Mark returned” on that order from the batch. It marks the order returned, clears the awaiting hold, and lets the batch be Confirmed.',
    ],
    fixes: [],
  },
  'v1.106.0': {
    date: '15 Jul 2026',
    features: [
      'RTO Reconcile: you can now scan or Mark Received a return the moment it physically arrives — no need to wait for the courier to mark it returned. Both “returning” and “returned” parcels are accepted.',
      'A parcel received ahead of the courier is grouped into its batch as received but tagged “⏳ Awaiting courier”. The batch’s Confirm stays locked until the courier confirms the return on every such parcel, then unlocks automatically.',
    ],
    fixes: [],
  },
  'v1.105.1': {
    date: '15 Jul 2026',
    features: [],
    fixes: [
      'Fixed split COD partial payment not recording in Shopify. Delivered parts of a split COD order now correctly post their amount (order shows “Partially paid”). Uses a method that works on standard (non-Plus) Shopify stores.',
    ],
  },
  'v1.105.0': {
    date: '15 Jul 2026',
    features: [
      'Split COD orders now capture payment per delivered part. As each part of a split order is delivered, its amount is recorded in Shopify (order shows “Partially paid”); once all parts are delivered the order is marked “Paid”.',
      'If one part is delivered and another is returned, Shopify now reflects exactly the cash collected on the delivered part instead of the order being stuck unpaid.',
    ],
    fixes: [],
  },
  'v1.104.0': {
    date: '14 Jul 2026',
    features: [
      'Purchase Orders: when confirming a PO (Auto or Manual) you can now add extra charges like Packing or Shipping. Click “Add extra charge”, pick a type, enter the amount and an optional remark. Charges are added to the vendor’s due for that PO and appear in the vendor ledger with your remark.',
      'Extra charges are optional (can be none), support multiple lines, and are shown on the confirmed PO’s details.',
    ],
    fixes: [],
  },
  'v1.103.1': {
    date: '14 Jul 2026',
    features: [],
    fixes: [
      'Fixed the new Closed Returns tab showing in-transit (not-yet-received) returns. It now correctly lists only scanned/received returns.',
    ],
  },
  'v1.103.0': {
    date: '14 Jul 2026',
    features: [
      'RTO Reconcile now opens on the Pending Returns tab by default.',
      'New “Closed Returns” tab (between Pending Returns and Batches) lists every scanned/received return — with a filter for Received-not-reconciled vs Reconciled, and the date & person who closed it.',
      'Pending Returns: tick multiple orders and “Mark Received” in bulk. By default each order is received at its own pickup location, with an option to send the whole selection to one chosen warehouse.',
      'Both tabs get a date filter (Last 7 / 15 / 30 days, All time, or a custom range) based on the courier return date, alongside the existing location + search filters.',
    ],
    fixes: [],
  },
  'v1.102.6': {
    date: '14 Jul 2026',
    features: [
      'RTO batch “Closed (Scanned)” column now also shows who scanned/closed the return and a short Scan # (the scan session it was closed in).',
    ],
    fixes: [
      'RTO “Return Date” now fills in properly — the courier’s return date is captured the moment the parcel is marked returned. Existing returns were backfilled from their tracking history.',
    ],
  },
  'v1.102.5': {
    date: '14 Jul 2026',
    features: [
      'RTO batch view now shows a “Closed (Scanned)” column: the date & time the return was scanned/received and the name of the person who closed it — separate from the courier’s return date.',
    ],
    fixes: [
      'Fixed the “Return Date” on RTO batches showing “Invalid Date”. It now correctly shows the date the courier marked the parcel returned (or “—” when the courier hasn’t reported one).',
    ],
  },
  'v1.102.4': {
    date: '14 Jul 2026',
    features: [],
    fixes: [
      'Fixed a case where scanning an RTO return closed the order but it never appeared in a return batch. Scanned returns that weren’t already grouped into a batch now get one automatically, so they always show up on the RTO Reconcile page. The one order already affected has been added to its batch.',
    ],
  },
  'v1.102.3': {
    date: '11 Jul 2026',
    features: [],
    fixes: [
      'Bulk “Retry Courier” now opens a popup to choose the pickup location and courier and confirm — the same as retrying a single order or bulk shipping. Previously it retried immediately with no choice or confirmation.',
    ],
  },
  'v1.102.2': {
    date: '11 Jul 2026',
    features: [],
    fixes: [
      'Printing labels now always creates the pickup batch. Previously, on a bulk print, orders could move to Ready-to-Pickup without appearing in a pickup batch if the batch step quietly failed — so the courier pickup wasn’t requested either. Batch creation now happens on the server as part of printing, reliably. Orders already stuck in this state have been added to their pickup batches.',
    ],
  },
  'v1.102.1': {
    date: '11 Jul 2026',
    features: [],
    fixes: [
      'A courier that’s disabled on a location no longer appears as an option when you ship — it’s hidden from the courier picker in every ship popup, and the server refuses it too.',
      'Courier priority on a location is now a proper ranking — two couriers can’t share the same priority any more. Setting one courier’s priority shifts the others to keep a clean 1, 2, 3… order, and it automatically fixes any location that already had a duplicate.',
    ],
  },
  'v1.102.0': {
    date: '11 Jul 2026',
    features: [
      'New “Test Connection” button on each connected courier. It checks the connection using your saved credentials — no re-entering anything — and tells you clearly whether it’s healthy, your login is wrong, or the courier itself is rejecting API calls (so you know when to contact the courier vs fix your password). It never touches any shipment.',
      'Disconnecting a courier now disables it instead of deleting it — orders already shipped through it keep tracking, and you can Restore it in one click. A separate “Remove” permanently deletes it, with a warning.',
    ],
    fixes: [
      'You can now update a courier’s credentials in place without disconnecting, so locations stay linked and existing shipments keep tracking through the same account.',
    ],
  },
  'v1.101.3': {
    date: '11 Jul 2026',
    features: [],
    fixes: [
      'Fixed the Products table columns being shifted by one — the product cost was showing under the GST column. Removed the redundant “Last Cost” column (Cost Used already shows the latest purchase price), so every value now lines up under its heading.',
    ],
  },
  'v1.101.2': {
    date: '10 Jul 2026',
    features: [],
    fixes: [
      'The Out-for-Delivery attempt breakdown showed every order as “Unknown”. The attempts were being counted correctly all along — the dashboard simply was not reading the field. It now shows the real split.',
      'The attempt breakdown moved out of the Out-for-Delivery card into its own card below, so it has room to grow and no longer stretches the cards beside it.',
      'Average shipping cost per order now sits on the same line as the order count, keeping all the Key Metrics cards the same height.',
    ],
  },
  'v1.101.1': {
    date: '10 Jul 2026',
    features: [],
    fixes: [
      'Fixed the dashboard showing a blank white screen. The new Out-for-Delivery attempt breakdown crashed the page as soon as its data loaded.',
    ],
  },
  'v1.101.0': {
    date: '10 Jul 2026',
    features: [
      'The Out-for-Delivery card on your dashboard now breaks the pipeline down by delivery attempt — 1st, 2nd, 3rd and beyond — showing both how many orders and how much money sits at each stage. Click any of them to open those exact orders. The parcels on a third attempt are the ones closest to coming back to you.',
      'The Total Shipping Cost card now shows your average shipping cost per order, separately for COD and prepaid. The difference between the two is essentially what COD collection is costing you.',
    ],
    fixes: [
      'The Total Shipping Cost card said “shipped” but was counting every parcel it had charged for, including those still awaiting pickup. The label now matches the number.',
    ],
  },
  'v1.100.1': {
    date: '10 Jul 2026',
    features: [
      'The Delivered tab now shows which attempt each parcel was delivered on — 1st, 2nd, 3rd and so on — in a new Attempt column, and you can filter the tab by it. Green for a first-time delivery, amber and red as it takes more tries. Across your account, 92% of orders are delivered first time.',
    ],
    fixes: [
      'Delivery attempts were not being counted for Delhivery orders. Delhivery has no live webhook, so its orders are only updated by our background tracking job — and that job was running an older version of the code. It now counts attempts like every other courier, and past orders have been filled in.',
    ],
  },
  'v1.100.0': {
    date: '10 Jul 2026',
    features: [
      'Product cost. You can now enter a cost for products you have never raised a purchase order for. A confirmed purchase order still wins — your manual cost only fills the gap. Enter it excluding GST; the field shows you the GST-inclusive figure so you can check it against your supplier’s invoice.',
      'Products page has a new “Cost Used” column showing the cost your profit and returns are actually valued at, and where it came from, plus a “Cost missing” filter to work through the gaps.',
      'When reconciling an RTO batch, any product without a cost now lets you type it in on the spot — it saves to the product, so every future batch and your profit figures pick it up.',
    ],
    fixes: [
      'Returned goods are no longer valued at ₹0. Products with no cost used to show ₹0 in RTO Reconcile as though that were a real number; they now say “cost missing” and the batch total is marked partial. You can still confirm the batch.',
      'Purchase Manager no longer tells you to order 1 unit of products you already have in stock, and it now subtracts stock that is already on an open purchase order — including the drafts Auto PO raises. Across your catalogue this was suggesting 423 units you did not need.',
      'Orders that failed to ship can no longer appear in Ready to Print with a Ship button. A courier update can no longer mark an order as shipped when it has no AWB.',
      'Velocity ship failures now show the real reason instead of “[object Object]”.',
    ],
  },
  'v1.99.3': {
    date: '10 Jul 2026',
    features: [],
    fixes: [
      'The Pending tab is much faster. It used to re-read every open order in your account each time you opened it; that work is now reused for a few seconds and thrown away the instant anything changes — so a shipped or cancelled order still leaves the list immediately.',
      'Being signed out no longer loses your place. You return to the page you were on after signing back in.',
      'The 4-hour inactivity sign-out now warns you first and gives you a “Stay signed in” button. Working in one browser tab also keeps your other tabs signed in.',
    ],
  },
  'v1.99.2': {
    date: '10 Jul 2026',
    features: [],
    fixes: [
      'Fixed random logouts. A database timeout or a moment of bad network was being reported as “invalid token”, so the app signed you out. Now only a genuinely expired or invalid session logs you out — a failed request is simply retried.',
      'Order tabs load far faster. Opening Booked, Printed, In Transit, Out for Delivery, NDR or RTO used to read every open order in your account; now it reads only the page you are looking at (about 11–16× quicker).',
      'Switching tabs no longer shows the previous tab’s orders while the new one loads — that could make you act on the wrong list. The new tab shows a placeholder instead, and only if loading takes longer than a moment.',
      'An order that was cancelled (or already closed) can no longer be shipped by mistake from a list that hasn’t refreshed yet. The server now refuses it.',
    ],
  },
  'v1.99.1': {
    date: '10 Jul 2026',
    features: [],
    fixes: [
      'A delivered order can never show “0 attempts” any more. If it was delivered, it went out at least once — so it now reads 1st attempt, marked with a small * meaning the courier never sent us an out-for-delivery scan.',
      'An NDR now counts as a delivery attempt on its own. Some couriers report the failed delivery but never the out-for-delivery scan that came before it, so those attempts were previously invisible.',
      'Delhivery attempt counts are now read from Delhivery’s own scan history instead of from our polling. Because Delhivery has no live webhook, a parcel that went out and was delivered between two checks used to lose its attempt entirely.',
      'Orders that never went out — cancelled, or not yet dispatched — correctly show a dash instead of an attempt count.',
    ],
  },
  'v1.99.0': {
    date: '10 Jul 2026',
    features: [
      'Delivery attempt tracking. Every time a parcel goes out for delivery, we count the attempt — 1st attempt, then 2nd after an NDR and a re-attempt the next day, and so on. The count appears as a colour-coded tag on the Out for Delivery tab (blue 1st, amber 2nd, red 3rd+ as an RTO warning), between the From and Courier / AWB columns.',
      'Filter the Out for Delivery tab by attempt — 1st, 2nd, or 3rd-and-above — to spot the parcels most at risk of coming back.',
    ],
    fixes: [
      'Attempts count once per calendar day, so a courier re-scanning “Out for Delivery” twice in one day (or a webhook and our poll both reporting it) can never inflate the number.',
      'We now record which attempt each order was finally delivered on. Your past orders were backfilled from their tracking history, so this history is already there.',
    ],
  },
  'v1.98.0': {
    date: '10 Jul 2026',
    features: [
      'Smoother loading everywhere. Pages now show a shaped placeholder while they load for the first time, instead of a blank “Loading…” — so the layout no longer jumps when your data appears. Applied to Dashboard, Orders and Products first.',
      'Changing a filter, switching a tab or refreshing no longer blanks the page: your current data stays on screen while the new data loads, with a thin progress bar at the top of the window showing that something is on the way.',
    ],
    fixes: [
      'Location courier priority is no longer capped at 3 — the number of priority slots now matches how many couriers that location has, and newly-added couriers (like Shiprocket) show their priority buttons right away instead of only after their first shipment.',
      'Connect Courier form makes it clear which email is your own reference label and which is the login we actually use. Shiprocket now shows its webhook URL and setup steps on the courier card.',
    ],
  },
  'v1.97.0': {
    date: '10 Jul 2026',
    features: [
      'Shiprocket is now a fully supported courier. Connect it under Integrations → Couriers (your Shiprocket API-user email + password), add it to a location, and ship. Shiprocket auto-picks the best courier per order; the sub-courier’s name shows on the order and label. Pickup address is auto-created from your location on the first shipment — no manual setup.',
    ],
    fixes: [
      'Shiprocket live tracking: status updates flow in via webhook (paste your webhook URL once in Shiprocket → Settings → API → Webhooks) and are also polled automatically as a fallback, so orders move through In-Transit → Delivered / RTO on their own.',
    ],
  },
  'v1.96.3': {
    date: '9 Jul 2026',
    features: [],
    fixes: [
      'When you have multiple accounts for a courier, the ship popup and bulk-ship courier list now show the account name a location ships through (e.g. “Delhivery — North”), and shipped-order rows show which account handled the parcel. Name only (no email) to keep it compact.',
    ],
  },
  'v1.96.2': {
    date: '9 Jul 2026',
    features: [],
    fixes: [
      'Add Courier on a Location now lists each connected account by your reference name (e.g. “Delhivery — North · email”), so you always pick the exact account — not just the courier type.',
    ],
  },
  'v1.96.1': {
    date: '9 Jul 2026',
    features: [
      'Multiple accounts per courier: you can now connect the same courier more than once with different API credentials (e.g. two Delhivery accounts). Each connection is its own named account. On a Location, when a courier has 2+ accounts you pick which account that warehouse ships through — shipping uses that account’s credentials and warehouse.',
    ],
    fixes: [],
  },
  'v1.96.0': {
    date: '9 Jul 2026',
    features: [
      'Courier connections now carry an Account Name + Account Email, so you can tell which courier account is connected at a glance. New connections ask for both; already-connected couriers show a prompt to name them (no need to re-enter credentials).',
    ],
    fixes: [],
  },
  'v1.95.6': {
    date: '9 Jul 2026',
    features: [],
    fixes: [
      'Retrying a courier-error order now always shows the Pickup Location and Courier choices in the Create Shipment popup (it defaults to the smart stock location but you can pick another location/courier) — previously the selectors could come up empty when stock couldn’t be auto-resolved.',
    ],
  },
  'v1.95.5': {
    date: '8 Jul 2026',
    features: [],
    fixes: [
      'Orders table polish: removed the big empty gap between “SKU / Weight / Dimensions” and “Total Wt.”, and the Pending row action buttons (Fix · Confirm · ⋮) now sit on a single line.',
    ],
  },
  'v1.95.4': {
    date: '8 Jul 2026',
    features: [],
    fixes: [
      'Pending Orders: the issue reason now has its own dedicated “Issue” column (shown only on the Pending tab, right after Fulfillment). The Action column is just the buttons. Also tightened the gap between “SKU / Weight / Dimensions” and “Total Wt.” to free up space.',
    ],
  },
  'v1.95.2': {
    date: '8 Jul 2026',
    features: [
      'RTO Reconcile → Returns tab is now a single unified table (like the Orders page): filter by state (pending / received / reconciled / all), location and search, with pickup & received location, return/received dates, products, value and state — paginated 50 per page. No more scattered “Unassigned” groups.',
      'RTO Reconcile → Batches are now grouped Location ▸ Date (like Pickups): multiple batches on the same day at a location sit together, each still openable and confirmable.',
    ],
    fixes: [],
  },
  'v1.95.1': {
    date: '8 Jul 2026',
    features: [
      'RTO Reconcile now works without the add-on: a free “Mark Received” button on the Pending Returns tab lets you receive a return one at a time — pick the warehouse (defaults to the pickup location), and it lands in a per-location Manual batch you can Restock or Buyback anytime, free of charge.',
      'Batches now show whether each was received by Scan or Manual. Scanning + the account PIN + Inbound scanner still need the add-on; receiving and reconciling are open to everyone (the add-on’s 2-credit fee applies only when it’s active).',
    ],
    fixes: [],
  },
  'v1.95.0': {
    date: '8 Jul 2026',
    features: [
      'RTO Reconcile → new “Pending Returns” tab: see every parcel still owed back, grouped by location, with “To receive” (arrived, ready to scan) vs “In transit back”, and the total value owed back. A clear to-receive list before you scan.',
    ],
    fixes: [],
  },
  'v1.94.3': {
    date: '8 Jul 2026',
    features: [],
    fixes: [
      'Customer tags now recognise repeat buyers correctly. “Repeat” is based on how many times a customer has ordered (2+), not on how many parcels were delivered — so a customer who ordered again today shows as Repeat right away instead of New. VIP still needs proven delivery/spend.',
    ],
  },
  'v1.94.2': {
    date: '8 Jul 2026',
    features: [],
    fixes: [
      'Location “Packer (all)” now prints one section per courier instead of merging them. This fixes non-Velocity orders wrongly showing under “Unassigned” in the carrier sort, and matches how couriers are actually handed over. Includes a “Copy all” for the whole location.',
    ],
  },
  'v1.94.1': {
    date: '8 Jul 2026',
    features: [
      'Pickups redesigned into a clear hierarchy: Location ▸ Courier ▸ Pickup batch. Each location shows an overview (couriers, batches, orders, prepaid/COD, picked) and a “Reprint all / Packer (all)” for the whole location; couriers keep their own group actions. Batch rows now show both the Batch Date and the Pickup (slot) Date. Locations auto-collapse to the overview when you have more than one.',
    ],
    fixes: [],
  },
  'v1.94.0': {
    date: '8 Jul 2026',
    features: [
      'Key Metrics: RTO Lost Revenue and Total Shipping Cost cards now show a COD vs Prepaid breakdown (value + order count), like the In-Transit and Out-for-Delivery cards.',
    ],
    fixes: [
      'Dashboard “Ready to Pickup” count was showing 0 — it now correctly includes label-printed orders waiting for pickup, matching the Orders page.',
    ],
  },
  'v1.93.2': {
    date: '8 Jul 2026',
    features: [],
    fixes: [
      'Auto PO quantity accuracy: fixed a race during bulk shipping where parallel shipments of the same product could under-count the shipped quantity, leaving a stock surplus after the Auto PO was confirmed and restocked. Stock deduction is now atomic.',
      'Auto PO confirm now requires a buying price for every item, so the purchase always posts to the vendor payment ledger (a ₹0 price used to post nothing).',
    ],
  },
  'v1.93.1': {
    date: '5 Jul 2026',
    features: [],
    fixes: [
      'Key Metrics: merged COD and Prepaid in-transit into a single “In-Transit Value” card with a COD vs Prepaid breakdown at the bottom (like the Out-for-Delivery card).',
    ],
  },
  'v1.93.0': {
    date: '5 Jul 2026',
    features: [
      'Redesigned Live Shipment Status: pipeline tiles now have coloured icon badges + accent, a live “last updated” time, and the money figures moved into a cleaner “Key Metrics” card row (icon, big value, trend sparkline).',
      'Out-for-Delivery Value now shows a COD vs Prepaid split (orders + value) right on the card.',
    ],
    fixes: [],
  },
  'v1.92.0': {
    date: '5 Jul 2026',
    features: [
      'Live Shipment Status now shows three new money blocks: Out-for-Delivery order value (with order count), Total Shipping Cost across all shipped orders (our calculation), and Total COGS (product cost × units shipped). COGS shows a “% costed” note and auto-corrects as you record buying prices via confirmed POs.',
    ],
    fixes: [],
  },
  'v1.91.0': {
    date: '5 Jul 2026',
    features: [
      'Richer sign-up: we now collect Brand/Company name, a website or Instagram, and your monthly order volume — so we can approve genuine sellers faster. Phone shows a fixed +91 and accepts a 10-digit mobile.',
      'You now get an email the moment your account is approved.',
    ],
    fixes: [
      'Admins can change their own password from the admin panel, and set a list of emails to be notified on every new sign-up and demo lead.',
    ],
  },
  'v1.90.5': {
    features: [],
    date: '5 Jul 2026',
    fixes: [
      'RTO Reconcile: fixed uneven gaps between rows in the inbound scan list — rows are now a consistent height and long tracking numbers no longer wrap.',
      'Phone scanner: when a session ends (you tap End, the desktop ends it, or you submit), it now shows a clear message and returns to the PIN screen instead of just closing.',
    ],
  },
  'v1.90.4': {
    features: [],
    date: '5 Jul 2026',
    fixes: [
      'Add Vendor: Name, Phone and PIN Code are now required. Enter the PIN and State + City auto-fill (you can still edit them manually), just like the Locations page.',
      'Mod Add-ons stay locked until you finish the 3 setup steps (sales channel + location + courier). You can still recharge your wallet anytime from Billing.',
    ],
  },
  'v1.90.3': {
    features: [
      'Redesigned setup guide: the old inline card is gone. Now a friendly floating “Finish setup” button (bottom-right) opens an animated step-by-step wizard with a progress ring, clear descriptions, and a one-click jump to each step. It opens automatically the first time and tucks away once all 3 steps are done.',
    ],
    date: '5 Jul 2026',
    fixes: [],
  },
  'v1.90.2': {
    date: '5 Jul 2026',
    features: [],
    fixes: [
      'Fixed the cramped “Verify & Create Account” button on the email verification step — it now spans the full width and sits cleanly with the consent line.',
      'The “What’s New” and “update available” popups no longer appear for accounts that are still pending approval — they now show only to approved users inside the dashboard.',
    ],
  },
  'v1.90.1': {
    date: '5 Jul 2026',
    features: [],
    fixes: [
      'The “Recharge ₹1000, get 500 free” offer no longer keeps reappearing on every refresh. Close it and it stays hidden for the rest of the day, then shows again the next day. After you close it 5 times, it stops showing altogether.',
      'The offer now sits as a slim announcement strip across the very top of the app, so it no longer pushes the dashboard content down.',
    ],
  },
  'v1.90.0': {
    date: '5 Jul 2026',
    features: [
      'Expanded legal pages for full compliance: a rewritten Privacy Policy and Terms & Conditions aligned with India’s DPDP Act 2023 and IT Act, plus a new Data Processing Addendum and a named Grievance Officer contact.',
    ],
    fixes: [
      'Sign-up now clearly states that by creating an account you agree to our Terms, Privacy Policy, Data Processing Addendum and all other policies (links open in a new tab).',
    ],
  },
  'v1.89.0': {
    date: '5 Jul 2026',
    features: [
      'Smoother onboarding: after verifying your email you go straight into setup (with a progress bar). Once approved, a welcome message and a 3-step Setup Checklist — add a sales channel, a location, and a courier — guide you to your first shipment (shipping unlocks once all 3 are done).',
      'New-user offer: recharge ₹1000 and get 500 credits free. A reminder shows until your first recharge.',
    ],
    fixes: [
      'Faster loading — pages now load on demand, so the app starts quicker.',
      'For security, you are automatically signed out after 4 hours of inactivity.',
      'New accounts no longer get automatic free credits on approval — the 500 bonus now comes with your first ₹1000 recharge instead.',
    ],
  },
  'v1.88.0': {
    date: '5 Jul 2026',
    features: [],
    fixes: [
      'Auto PO: confirming a draft now restocks and moves straight to Confirmed PO in a single step — no separate Confirm Inbound. You still set the vendor and buying price in the confirm popup.',
      'Purchase Manager: the “Purchase Orders” tab is now “Manual PO” (Auto POs go straight to Confirmed PO).',
      'The “new update available” banner now shows only inside the app, not on the public website.',
      'Adding Velocity to a location no longer pops the webhook setup — that’s configured once under Integrations → Couriers.',
    ],
  },
  'v1.83.0': {
    date: '5 Jul 2026',
    features: [
      'RTO Reconcile now handles returns that arrive at a DIFFERENT warehouse than they shipped from. When you start an Inbound scan you pick the receiving warehouse; any parcel picked elsewhere is moved into that warehouse’s separate “Migrated” batch (restock-only, locked to the receiving location) so stock counts stay correct.',
      'Each location now has a 4-digit RTO Warehouse PIN (on the Locations page). A remote scanner enters the account Scan PIN + this Warehouse PIN on modcommerce.in/scan so restocks land at the right place. The desktop popup’s session PIN already carries the location.',
    ],
    fixes: [
      'Product stock statement now tags RTO restocks — a plain “RTO” chip, or “RTO ⚠ cross-location” when a unit came back to a different warehouse (with the source order in the reference), so the movement is always explainable.',
    ],
  },
  'v1.82.0': {
    date: '5 Jul 2026',
    features: [
      'RTO Reconcile scanning is now conflict-free: the Inbound popup shows a temporary Session PIN + QR — scan it (or open the link) on a phone to join that exact session, so the desktop and phone share one live list. No more overlapping sessions.',
      'Two ways to scan: the temporary Session PIN (joins a specific popup session, no name asked) or your account Scan PIN on modcommerce.in/scan (independent remote scanning, asks the scanner’s name).',
    ],
    fixes: [
      'Return batch numbers are now unique codes (e.g. RLV4UG) instead of plain 1, 2, 3.',
      'Scan sessions left open (app/tab closed without ending) now auto-expire, so the Scan Log no longer shows them stuck as “active”. Submitting a session closes it and the phone returns to the PIN screen.',
    ],
  },
  'v1.81.0': {
    date: '5 Jul 2026',
    features: [
      'New add-on — RTO Reconcile: returned parcels are auto-grouped into per-location daily batches. Scan them back in (mobile camera app at modcommerce.in/scan, or type the AWB/Order #), then Confirm each batch to Restock (to any location) or Buyback (draft credit note). 2 credits per reconciled order; enabling is free.',
      'Mobile RTO scanner: open modcommerce.in/scan on a phone, enter your 8-digit account Scan PIN (shown on the RTO Reconcile page) + your name, and scan returned parcels. The Inbound popup on desktop shows the PIN, link, QR and a live view of phone scans.',
      'Shipping Cost: search across ALL shipments by Order # or AWB, with 100-per-page paging.',
      'Credit Notes now have a unique number (CN-0001…) and a date, and moved to their own left-menu item under Vendor Payments.',
    ],
    fixes: [
      'Customer profile tags now appear right away for newly-synced orders (previously they only updated on the nightly rebuild).',
      'RTO COD orders: the refunded COD fee is now clearly shown (Refunded tag + a COD Refunded total) so the shipping cost reads correctly.',
      'Fixed a case where manually splitting an order made the split part invisible in every tab — split parts now show correctly, and any existing one was recovered.',
      'Old shipments that had no shipping-cost record now show up (with their real billed cost) and are searchable.',
    ],
  },
  'v1.77.2': {
    date: '4 Jul 2026',
    features: [],
    fixes: [
      'Auto PO orders can now be shipped from any location — the ship popup no longer greys out “No stock” locations for them (they’re meant to sell at zero stock). The smart location stays preselected but you can change it, in both single and bulk ship. Mixed orders (Auto-PO + regular items) show a note that you can change location.',
    ],
  },
  'v1.77.0': {
    date: '4 Jul 2026',
    features: [
      'New add-on — Customer Intelligence (7-day free trial, then 399 credits/month): a Customers page with every customer, their value tag (New / Repeat / VIP), RTO-risk ⚠ flag, total spend, and order history. Orders merge across channels by phone.',
      'Customer profile tags now show next to the customer name on Orders, and you can filter Orders by profile (VIP / Repeat / New / Risky / Dormant).',
    ],
    fixes: [],
  },
  'v1.76.0': {
    date: '4 Jul 2026',
    features: [
      'Dashboard insights for Indian ecommerce: RTO by State, Top Selling States, Channel Performance, plus Avg Delivery TAT, Shipping Cost as % of revenue, and Repeat Customer %.',
      'Courier Performance and Top Products now reflect ALL shipments (all-time), not just the selected date range — so they match the live pipeline. Cleaner Prepaid vs COD layout.',
    ],
    fixes: [
      'Trend % on the KPI cards no longer shows huge numbers when the previous period had no orders — it shows “New” instead.',
    ],
  },
  'v1.75.1': {
    date: '4 Jul 2026',
    features: [],
    fixes: [
      'Fixed the “new update available” refresh banner not appearing after recent deploys — it now detects new builds reliably (from the app bundle) instead of a version file that could be missing.',
    ],
  },
  'v1.75.0': {
    date: '4 Jul 2026',
    features: [
      'Redesigned Dashboard: modern gradient KPI cards with mini trend sparklines and up/down % vs the previous period, plus a Dark / Light theme toggle at the top (your choice is remembered).',
    ],
    fixes: [],
  },
  'v1.74.1': {
    date: '4 Jul 2026',
    features: [],
    fixes: [
      'The three new Live Shipment Status value cards (COD / Prepaid in-transit, RTO lost) now also show the order count.',
    ],
  },
  'v1.74.0': {
    date: '4 Jul 2026',
    features: [
      'Dashboard → Live Shipment Status now shows three value cards: COD In-Transit Value, Prepaid In-Transit Value, and RTO Lost Revenue (Returning + Returned) — live across all orders.',
    ],
    fixes: [],
  },
  'v1.73.4': {
    date: '4 Jul 2026',
    features: [],
    fixes: [
      'The ⋮ row menu now closes the moment you pick an action (it used to stay open behind the confirm dialog). Cancel messages no longer say “Delhivery” — they name the order’s actual courier.',
    ],
  },
  'v1.73.3': {
    date: '4 Jul 2026',
    features: [],
    fixes: [
      'Remittance: added Today / Yesterday / Last 7 Days quick filters, defaulting to Last 7 Days (was the whole current month).',
    ],
  },
  'v1.73.2': {
    date: '4 Jul 2026',
    features: [],
    fixes: [
      'Confirmation popups are now in-app dialogs (matching the app’s look) instead of the browser’s grey box — they only close when you click Cancel or Confirm.',
      'Pending tab: added a bulk “Cancel Orders” button when you select multiple orders.',
      'Fixed a manual-courier order staying in the “Manually Fulfilled” tab after it was cancelled (cleared the stale flag).',
    ],
  },
  'v1.73.1': {
    date: '4 Jul 2026',
    features: [],
    fixes: [
      'Smart Split now also auto-splits mixed orders (an Auto-PO product + a normal product) even when the store isn’t stock-gated — the Auto-PO item peels off into its own sub-order automatically instead of waiting for a manual split.',
    ],
  },
  'v1.73.0': {
    date: '3 Jul 2026',
    features: [
      'Stock adjustments now have reasons. Add stock: Opening / RTO restoring / Purchase (pick a vendor + price → creates a confirmed PO and adds stock, feeding vendor dues) / Other. Remove stock: Damaged / Lost / Buyback / Other — with an optional “Create credit note”.',
      'New Credit Note tab (Purchase Manager): removing stock as damaged/lost/buyback can draft a vendor credit note (qty locked, price editable). Confirm it to reduce that vendor’s due — it shows on the vendor ledger.',
      'Auto PO smart location: an Auto-PO order with no stock now lands in Ready to Ship with a suggested location (the one that product usually ships from), marked ⚡ — you can still change it or pick a no-stock location at ship.',
    ],
    fixes: [],
  },
  'v1.72.3': {
    date: '3 Jul 2026',
    features: [],
    fixes: [
      'Products: the stock breakdown now shows locations that have gone negative (from Auto PO selling at zero stock), in red — so you can see exactly which location is short.',
      'Auto PO in the Purchase Orders tab is now edit-price-only: quantity (fixed to the shipped stock) and GST are locked, and an Auto PO can’t be cancelled — Confirm Inbound to restock.',
    ],
  },
  'v1.72.2': {
    date: '3 Jul 2026',
    features: [],
    fixes: [
      'Manual Ship polish: cancelling a manual order before dispatch now removes it from the Manually Fulfilled tab; the tracking page shows your store logo (ModCommerce credit moved to the bottom); the Courier/AWB column no longer shows “Manual” twice; and the manual label now has Product / Qty / Amount column headers.',
    ],
  },
  'v1.72.1': {
    date: '3 Jul 2026',
    features: [],
    fixes: [
      'Vendor Payments: the “Make New Payment” popup now has a Payment Date field (defaults to today, change it with the date picker). The page also has Today / Yesterday / Last 7 Days quick filters, defaulting to Last 7 Days.',
    ],
  },
  'v1.72.0': {
    date: '3 Jul 2026',
    features: [
      'New add-on — Manual Ship: ship an order via any courier that isn’t integrated in the panel. Add a Manual Courier under Integrations → Couriers, assign it to a location, then pick it at ship time (1 credit per manual ship).',
      'Manual orders print a clean label (no barcode — big customer address + phone, return address at the bottom) and get a branded public tracking page at modcommerce.in/tracking/… that’s pushed to your store as the tracking link.',
      'New “Manually Fulfilled” tab mirrors every manual order alongside its normal status. Use “Update Tracking” (courier name, tracking ID, cost) on Ready to Pickup, then Push status → Delivered or Returned from the ⋮ menu.',
    ],
    fixes: [],
  },
  'v1.71.0': {
    date: '3 Jul 2026',
    features: [
      'Minimum recharge amount is now controlled from the Admin console (Settings → Minimum recharge amount), just like the per-shipment rate. Default stays ₹1,000; can be lowered to as little as 5 credits.',
    ],
    fixes: [],
  },
  'v1.70.3': {
    date: '3 Jul 2026',
    features: [],
    fixes: [
      'The “What’s New” popup now appears only once per update and won’t keep re-appearing on every login. The app also loads the latest version immediately (no more needing a manual reload to see new changes).',
    ],
  },
  'v1.70.2': {
    date: '3 Jul 2026',
    features: [],
    fixes: [
      'Recharge amount now accepts whole credits only (1 credit = ₹1), so the amount you type always matches the GST and total shown. Decimals are no longer accepted.',
    ],
  },
  'v1.70.0': {
    date: '3 Jul 2026',
    features: [
      'Billing → new “Recharge History” tab: every wallet recharge attempt logged with date, transaction ID, gateway, amount (₹ + credits) and status (Success / Processing / Pending / Failed).',
    ],
    fixes: [],
  },
  'v1.69.5': {
    date: '3 Jul 2026',
    features: [],
    fixes: [
      'Wallet recharge now prefills your own registered mobile number at the Cashfree payment page (was showing a placeholder number).',
    ],
  },
  'v1.69.4': {
    date: '3 Jul 2026',
    features: [],
    fixes: [
      'Recharge popup now opens centered on top of the screen (it was appearing clipped near the sidebar).',
    ],
  },
  'v1.69.3': {
    date: '3 Jul 2026',
    features: [],
    fixes: [
      'Auto PO card now shows the correct “units” and “Qty to buy” (was showing 0), and the Confirm popup pre-fills the right quantity and total.',
      'Auto PO is a hands-off flow now: the manual “Discard” button is gone. A closed PO with nothing left to buy (all its orders were cancelled or returned) auto-moves to Cancelled; otherwise just Confirm it to restock.',
    ],
  },
  'v1.69.2': {
    date: '3 Jul 2026',
    features: [],
    fixes: [
      'Auto PO no longer gets stuck on “Open” when an order’s status is changed manually (Update Status) or via Mark RTO. An order that entered the courier network now correctly closes its Auto PO (ready to confirm); a pre-pickup cancel drops it. (Real courier tracking already handled this — only the manual paths were missed.)',
    ],
  },
  'v1.69.1': {
    date: '3 Jul 2026',
    features: [],
    fixes: [
      'Printing a shipping label now always moves the order to “Ready to Pickup”. Earlier, if the print tab was blocked or the label was printed from Pickups / bulk, some orders stayed stuck in “Ready to Print” — the status is now updated on the server for every print path.',
    ],
  },
  'v1.69.0': {
    date: '3 Jul 2026',
    features: [
      'Remittance: tap any COD amount (day, channel or total) to see the courier-wise split (how much COD via Delhivery / Velocity / Ekart etc.) in a popup.',
    ],
    fixes: [],
  },
  'v1.68.0': {
    date: '3 Jul 2026',
    features: [
      'Auto PO pricing changed: turning the add-on on is now free — you’re charged a flat 5 credits only when you confirm an Auto PO (no more daily fee).',
    ],
    fixes: [],
  },
  'v1.67.0': {
    date: '3 Jul 2026',
    features: [
      'Product “History” is now a full Stock Statement (Products → ⋮ → Statement). See every stock movement for a product — opening balance, purchases (PO #), manual adjustments, transfers, shipments, Auto-PO negatives and cancellations — with a running balance. Print it as a branded PDF.',
    ],
    fixes: [],
  },
  'v1.66.0': {
    date: '3 Jul 2026',
    features: [
      'Wallet recharge now supports secure online payment (cards / UPI / netbanking) via Cashfree — credits are added the moment payment succeeds, with the same GST invoice.',
    ],
    fixes: [],
  },
  'v1.65.0': {
    date: '3 Jul 2026',
    features: [
      'Velocity shipping labels now show the real carrier — e.g. “Delhivery Express” in big text with “Velocity” underneath — instead of just “Velocity”. New carriers are recognised automatically.',
      'Amazon Shipping labels: since Amazon requires its own label, we now save it at ship time so you can reprint anytime (its original link expires in 24h). Bulk/pickup prints include Amazon labels in the same PDF.',
    ],
    fixes: [
      'Tracking no longer overwrites the carrier name with an internal code.',
    ],
  },
  'v1.64.1': {
    date: '3 Jul 2026',
    features: [
      'Copy buttons on the Orders list — one-tap copy for the Order ID and each SKU.',
    ],
    fixes: [
      'Purchase Orders tab no longer shows each purchase order twice.',
      'Orders list SKU is now larger and easier to read.',
      'Products master now sorts by Stock by default.',
    ],
  },
  'v1.64.0': {
    date: '2 Jul 2026',
    features: [
      'Auto PO now has an Open / Closed lifecycle. A PO opens the moment an Auto-PO product ships (View only) and stays Open while any of its orders are still awaiting pickup. Once every order in it is picked up it turns Closed and the Confirm button unlocks.',
      'Day-end settlement: if only some orders in an Open PO were picked that day, it auto-splits — a Closed PO is created for the picked ones (ready to confirm) and a new Open PO carries the un-picked orders to the next day. Orders cancelled before pickup drop out of the PO automatically.',
    ],
    fixes: [],
  },
  'v1.63.0': {
    date: '2 Jul 2026',
    features: [
      'New add-on — Auto PO (Smart PO): let chosen products sell even at zero stock. Turn it on in Mod Add-ons (5 credits/day), then enable it per channel (Sales Channels) or per product (Products → ⋮ menu). Enabled products skip the stock hold — their orders go straight to Ready to Confirm and can ship (stock is allowed to go negative).',
      'Auto-created draft Purchase Orders: once an Auto-PO parcel is picked up, a draft PO is built automatically under Purchase Manager → ⚡ Auto PO, grouped by location. Just pick a vendor (last buying price pre-filled), confirm, then Confirm Inbound to restock — negative stock corrects itself.',
    ],
    fixes: [],
  },
  'v1.62.0': {
    date: '2 Jul 2026',
    features: [
      'Pickups now close themselves the moment every parcel in a request is picked up — no waiting for the day to end. A fully-picked pickup moves straight to Closed.',
    ],
    fixes: [],
  },
  'v1.61.0': {
    date: '2 Jul 2026',
    features: [
      'Courier-cancelled shipments auto-recover: if a courier cancels a parcel that was never picked up (failed pickups / not handed over), the order returns to Pending with “Shipment cancelled by courier” and a Retry — stock is restored, wallet charge refunded, and Shopify un-fulfilled so you can simply re-ship it.',
    ],
    fixes: [],
  },
  'v1.60.0': {
    date: '2 Jul 2026',
    features: [
      'Pickups now have Open / Closed status. A pickup is Open on its scheduled day; once the day passes it Closes.',
      'Auto carry-forward: un-picked (or partially-picked) shipments from a closed pickup roll into a fresh next-day pickup with a new courier request — automatically.',
      'Pickups page filters: Open / Closed (default Open) + Today / Yesterday / Last 7 Days.',
    ],
    fixes: [],
  },
  'v1.59.0': {
    date: '2 Jul 2026',
    features: [
      'Filters + search now work on ALL order tabs — including Delivered, Cancelled and Returned (earlier only the active tabs).',
      "What's New popup — after each update you'll see exactly what changed.",
    ],
    fixes: [
      '“Sync New Orders” no longer times out on larger stores.',
    ],
  },
};
